/**
 * App.jsx — Single-file FG-OS
 *
 * Contient TOUS les composants UI :
 *   - ThemeProvider + useTheme
 *   - ToastProvider + useToast + ToastRegion + ToastItem
 *   - Modal (focus trap, ESC, portal, placement center/bottom-sheet)
 *   - Button (variants : primary / secondary / ghost / danger)
 *   - Header (logo + theme toggle + statut sync)
 *   - TopNav (desktop 7 onglets), BottomNav (mobile 5 + Plus)
 *   - 7 modules placeholders (Module1Dashboard inclut demo Modal + Toast)
 *   - AppShell + App root
 *
 * Hooks utilitaires : useMediaQuery, useReducedMotion.
 *
 * Convention :
 *   - Tous les styles inline avec var(--xxx) (CSS vars injectees par applyTheme)
 *   - Aucun import de fichier CSS hors index.html (reset minimal + fonts)
 *   - Aucune alert/confirm/prompt native, jamais
 */

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useContext,
  createContext,
  Fragment,
} from 'react';
import { createPortal } from 'react-dom';
import {
  LayoutDashboard,
  Printer,
  Calculator,
  Workflow,
  PenLine,
  RefreshCw,
  Settings,
  MoreHorizontal,
  X,
  Sun,
  Moon,
  AlertTriangle,
  CheckCircle2,
  Info,
  Trash2,
  Plus,
  Minus,
  ArrowRight,
  ArrowLeft,
  FileText,
  Building2,
  Mail,
  Eye,
  Download,
  Send,
  Search,
  Sparkles,
  MapPin,
  Phone,
  AtSign,
  User,
  Copy,
  ExternalLink,
  Loader,
  Bell,
  Check,
  Upload,
  Filter,
  LayoutGrid,
  List,
  Target,
  Zap,
  EyeOff,
  MessageSquare,
  CalendarClock,
} from 'lucide-react';

import {
  applyTheme,
  typoStyle,
  cssTransition,
  space,
  radius,
  getMotionDuration,
} from './lib/tokens.js';
import {
  addProspect,
  getProspects,
  getCANonFG,
  setCANonFG,
  getLastSync,
  getActiveNav,
  setActiveNav,
  setTheme as persistTheme,
  listAudits,
  getAudit,
  saveAudit,
  removeAudit,
  setActiveAuditId,
  listDevis,
  getDevis,
  saveDevis,
  removeDevis,
  listDevisByProspect,
  getActiveAuditId,
  clearActiveAuditId,
  getActiveDevisId,
  setActiveDevisId,
  clearActiveDevisId,
  getActiveProspectForAudit,
  setActiveProspectForAudit,
  clearActiveProspectForAudit,
  clearAll,
  approxSize,
  updateProspect,
  deriveTon,
  deriveSegment,
  setProspectTon,
  clearProspectTonForce,
  addCommande,
  updateCommande,
  listCommandesByProspect,
  getDerniereCommande,
  removeProspect,
  addDraft,
  updateDraft,
  getImportedSirets,
  addImportedSiret,
  isAlreadyImported,
  removeImportedSiret,
  cleanupOrphanImportedSirets,
  getSireneImportHistory,
  addSireneImportEntry,
  getEnrichissement,
  saveEnrichissement,
  mergeEnrichissement,
  isEnrichmentFresh,
  getNotifications,
  getAllNotificationsRaw,
  markNotificationRead,
  markAllNotificationsRead,
  dismissNotification,
  getUnreadCount,
  cleanExpiredNotifications,
  getMailsGeneres,
  addMailGenere,
  exportAllData,
  importAllData,
  getInkyaCatalogue,
  setInkyaCatalogue,
  getInkyaMeta,
  clearInkyaCatalogue,
  listInteractionsByProspect,
  addInteraction,
  listProspectsSansProchaineAction,
} from './lib/storage.js';
import { buildInkyaIndex, findInkyaMatch, normalizeRef } from './lib/inkya.js';
import { scoreProspect } from './lib/scoring.js';
import { getReassortsAVenir, computeReassortsCommande } from './lib/reassort.js';
import { hasFutureProchaineAction } from './lib/interactions.js';

/**
 * Enrichit les lignes d'une commande avec le rendement reel (capacite_pages)
 * depuis D1 via /api/inkya-lookup. Fire-and-forget : echec silencieux -> les
 * moyennes par famille prennent le relais (badge "estimation"). Le rendement
 * est fige sur la ligne a l'achat, zero fetch recurrent ensuite.
 */
async function enrichCommandeRendements(commande) {
  try {
    const lignes = await Promise.all(commande.lignes.map(async (l) => {
      try {
        const resp = await fetch(`/api/inkya-lookup?ref=${encodeURIComponent(l.ref_interne || '')}`);
        if (!resp.ok) return l;
        const data = await resp.json();
        const cap = data?.result?.match?.capacite_pages;
        return (cap != null && Number(cap) > 0) ? { ...l, rendement_pages: Number(cap) } : l;
      } catch {
        return l;
      }
    }));
    updateCommande(commande.id, { lignes });
  } catch {
    /* offline / functions absentes : moyennes famille en fallback */
  }
}
import {
  checkAndGenerateNotifications,
  triggerOnStatutChange,
} from './lib/notifications.js';
import {
  matchPrinterModel,
  extractKnownModels,
  suggestModels,
} from './lib/matching.js';
import compatLaserData from './data/fg_compatibilite_laser.json';
import catalogueComplet from './data/catalogue-fallback.json';
import luxembourgB2B from './data/luxembourg-b2b.json';
// pdf.js (jspdf + autotable, ~200KB gzip) charge dynamiquement dans Module 4 handlers
// pour ne pas alourdir le bundle critique. Warm-up declenche dans main.jsx au idle.
import {
  calcPaliersFranchis,
  calcPrimesOuvertures,
  calcProjectionCA,
  calcTotal,
  calcEconomieClient,
  formatEUR,
  formatPct,
  PALIERS_CA,
  PRIME_PAR_PALIER,
  PRIME_OUVERTURE_COMPTE,
  CGV,
} from './lib/pricing.js';

// ============================================================
// Constants
// ============================================================

const BREAKPOINT_MOBILE = '(max-width: 767px)';
const BREAKPOINT_REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

/**
 * 7 modules FG-OS, ordre logique du pipeline metier.
 *   primary=true  -> visible en bottom-nav mobile (5 items)
 *   primary=false -> regroupes dans le sheet "Plus"
 */
const MODULES = [
  { id: 'dashboard',   label: 'Dashboard',     short: 'CA',       icon: LayoutDashboard, primary: true  },
  { id: 'audit',       label: 'Audit parc',    short: 'Audit',    icon: Printer,         primary: true  },
  { id: 'chiffrage',   label: 'Chiffrage',     short: 'Devis',    icon: Calculator,      primary: true  },
  { id: 'pipeline',    label: 'Pipeline',      short: 'Pipeline', icon: Workflow,        primary: true  },
  { id: 'prospection', label: 'Prospection',   short: 'Pros.',    icon: Sparkles,        primary: true  },
  { id: 'redaction',   label: 'Redaction',     short: 'Mail',     icon: PenLine,         primary: false },
  { id: 'sync',        label: 'Sync Tarif',    short: 'Sync',     icon: RefreshCw,       primary: false },
  { id: 'reglages',    label: 'Reglages',      short: 'Regl.',    icon: Settings,        primary: false },
];

const PRIMARY_MODULES = MODULES.filter((m) => m.primary);
const NON_PRIMARY_MODULES = MODULES.filter((m) => !m.primary);

// ============================================================
// Hooks utilitaires
// ============================================================

/**
 * useMediaQuery — re-render quand le media query change d'etat.
 * SSR-safe, fallback false sans matchMedia.
 * Compat Safari < 14 via addListener legacy.
 */
function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(query);
    // Re-sync au mount : l'initial useState lazy peut etre stale dans
    // certains contextes (browser headless, navigation prerender, etc.).
    setMatches(mq.matches);
    const handler = (e) => setMatches(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler); // Safari < 14
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, [query]);

  return matches;
}

function useReducedMotion() {
  return useMediaQuery(BREAKPOINT_REDUCED_MOTION);
}

// ============================================================
// ThemeContext
// ============================================================

const ThemeContext = createContext({ theme: 'light', toggleTheme: () => {} });
function useTheme() {
  return useContext(ThemeContext);
}

function ThemeProvider({ initial = 'light', children }) {
  const [theme, setTheme] = useState(initial);

  useEffect(() => {
    applyTheme(theme);
    persistTheme(theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((t) => (t === 'light' ? 'dark' : 'light'));
  }, []);

  const value = useMemo(() => ({ theme, toggleTheme }), [theme, toggleTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// ============================================================
// NavContext (active module + persist localStorage)
// ============================================================

const NavContext = createContext({ activeId: 'dashboard', setActiveId: () => {} });
function useNav() {
  return useContext(NavContext);
}

const VALID_NAV_IDS = MODULES.map((m) => m.id);

function NavProvider({ children }) {
  const [activeId, setActiveIdState] = useState(() => {
    const stored = getActiveNav();
    return stored && VALID_NAV_IDS.includes(stored) ? stored : 'dashboard';
  });

  const setActiveId = useCallback((id) => {
    if (!VALID_NAV_IDS.includes(id)) {
      console.warn(`[nav] setActiveId: id "${id}" inconnu (modules valides : ${VALID_NAV_IDS.join(', ')})`);
      return;
    }
    setActiveIdState(id);
    setActiveNav(id);
  }, []);

  const value = useMemo(() => ({ activeId, setActiveId }), [activeId, setActiveId]);
  return <NavContext.Provider value={value}>{children}</NavContext.Provider>;
}

// ============================================================
// NotificationContext (Bloc 1 — bell + dropdown)
// ============================================================

const NotificationContext = createContext({
  notifications: [],
  unread: 0,
  refresh: () => {},
  markRead: () => {},
  dismiss: () => {},
  markAllRead: () => {},
});
function useNotifications() {
  return useContext(NotificationContext);
}

function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState(() => {
    cleanExpiredNotifications();
    return getNotifications();
  });

  const refresh = useCallback(() => {
    checkAndGenerateNotifications();
    setNotifications(getNotifications());
  }, []);

  useEffect(() => {
    refresh();
    const t = window.setInterval(refresh, 5 * 60 * 1000); // toutes les 5 min
    return () => window.clearInterval(t);
  }, [refresh]);

  const markRead = useCallback((id) => {
    markNotificationRead(id);
    setNotifications(getNotifications());
  }, []);

  const dismiss = useCallback((id) => {
    dismissNotification(id);
    setNotifications(getNotifications());
  }, []);

  const markAllRead = useCallback(() => {
    markAllNotificationsRead();
    setNotifications(getNotifications());
  }, []);

  const unread = notifications.filter((n) => !n.read).length;

  const value = useMemo(
    () => ({ notifications, unread, refresh, markRead, dismiss, markAllRead }),
    [notifications, unread, refresh, markRead, dismiss, markAllRead],
  );
  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

// ============================================================
// HealthContext (Bloc 4 — 4 dots statut secrets)
// ============================================================

const HealthContext = createContext({ status: null, loading: false, refresh: () => {} });
function useHealth() {
  return useContext(HealthContext);
}

function HealthProvider({ children }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch('/api/health');
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setStatus(data);
    } catch (err) {
      console.warn('[health] fetch error:', err.message);
      setStatus({ anthropic: false, gmail: false, notion: false, sirene: false, error: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(() => ({ status, loading, refresh }), [status, loading, refresh]);
  return <HealthContext.Provider value={value}>{children}</HealthContext.Provider>;
}

// ============================================================
// ToastContext + ToastRegion + ToastItem
// ============================================================

const ToastContext = createContext({ showToast: () => {}, dismissToast: () => {} });
function useToast() {
  return useContext(ToastContext);
}

function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismissToast = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (message, options = {}) => {
      const id =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `t-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const kind = options.kind || 'info';
      const duration = options.duration ?? 4000;
      setToasts((list) => [...list, { id, message, kind }]);
      if (duration > 0) {
        window.setTimeout(() => dismissToast(id), duration);
      }
      return id;
    },
    [dismissToast],
  );

  const value = useMemo(() => ({ showToast, dismissToast }), [showToast, dismissToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastRegion toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
}

const TOAST_ICONS = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: AlertTriangle,
};

const TOAST_COLORS = {
  info:    { fg: 'var(--info)',    bg: 'var(--info-subtle)'    },
  success: { fg: 'var(--accent)',  bg: 'var(--accent-subtle)'  },
  warning: { fg: 'var(--warning)', bg: 'var(--warning-subtle)' },
  danger:  { fg: 'var(--danger)',  bg: 'var(--danger-subtle)'  },
};

function ToastRegion({ toasts, onDismiss }) {
  return (
    <div
      role="region"
      aria-label="Notifications"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: space.lg,
        right: space.lg,
        display: 'flex',
        flexDirection: 'column',
        gap: space.sm,
        zIndex: 10000,
        pointerEvents: 'none',
        maxWidth: 'min(420px, calc(100vw - 32px))',
      }}
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} {...t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastItem({ message, kind, onDismiss }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const Icon = TOAST_ICONS[kind] || Info;
  const colors = TOAST_COLORS[kind] || TOAST_COLORS.info;

  return (
    <div
      role="status"
      style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: radius.md,
        boxShadow: 'var(--shadow-md)',
        padding: `${space.md} ${space.lg}`,
        display: 'flex',
        alignItems: 'flex-start',
        gap: space.md,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateX(0)' : 'translateX(16px)',
        transition: cssTransition('standard', ['opacity', 'transform']),
        pointerEvents: 'auto',
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: radius.sm,
          background: colors.bg,
          color: colors.fg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon size={16} strokeWidth={2} />
      </div>

      <div style={{ flex: 1, ...typoStyle('body-md'), color: 'var(--text)' }}>
        {message}
      </div>

      <button
        type="button"
        onClick={onDismiss}
        aria-label="Fermer la notification"
        style={{
          background: 'transparent',
          border: 'none',
          color: 'var(--text-subtle)',
          cursor: 'pointer',
          padding: 4,
          margin: -4,
          borderRadius: radius.sm,
          display: 'flex',
          alignItems: 'center',
          transition: cssTransition('instant', ['color', 'background']),
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = 'var(--text)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = 'var(--text-subtle)';
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
}

// ============================================================
// Modal (custom, focus trap, ESC, portal, placement center/bottom-sheet)
// ============================================================

const MODAL_SIZES = { sm: 400, md: 560, lg: 760, xl: 960 };

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function Modal({
  open,
  onClose,
  title,
  size = 'md',
  placement = 'center', // 'center' | 'bottom-sheet'
  variant = 'default',  // 'default' | 'danger'
  closeOnOverlay = true,
  children,
  footer,
}) {
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(false);
  const panelRef = useRef(null);
  const reducedMotion = useReducedMotion();

  // Mount + animation cycle (entree/sortie)
  useEffect(() => {
    if (open) {
      setMounted(true);
      const id = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(id);
    }
    if (mounted) {
      setVisible(false);
      // Exit : standard 200ms (overlay) + epsilon pour transform du panel
      const exitMs = reducedMotion ? 0 : 220;
      const t = window.setTimeout(() => setMounted(false), exitMs);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [open, mounted, reducedMotion]);

  // Focus trap + ESC + body scroll lock
  useEffect(() => {
    if (!mounted) return undefined;

    const prevFocus = document.activeElement;
    const panel = panelRef.current;
    if (!panel) return undefined;

    // Auto-focus 1er champ saisissable, sinon 1er bouton
    const firstField = panel.querySelector('input, textarea, select');
    const target = firstField || panel.querySelector(FOCUSABLE_SELECTOR);
    if (target && typeof target.focus === 'function') {
      target.focus();
    }

    const handler = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const list = Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.disabled,
      );
      if (list.length === 0) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handler);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = prevOverflow;
      if (prevFocus && typeof prevFocus.focus === 'function') {
        try {
          prevFocus.focus();
        } catch {
          /* ignore */
        }
      }
    };
  }, [mounted, onClose]);

  if (!mounted) return null;

  const isBottomSheet = placement === 'bottom-sheet';
  const accentColor = variant === 'danger' ? 'var(--danger)' : 'var(--accent)';

  const overlayStyle = {
    position: 'fixed',
    inset: 0,
    background: 'var(--overlay)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: isBottomSheet ? 'flex-end' : 'center',
    justifyContent: 'center',
    padding: isBottomSheet ? 0 : space.lg,
    zIndex: 9999,
    opacity: visible ? 1 : 0,
    transition: cssTransition('standard', ['opacity']),
  };

  const panelStyleCenter = {
    width: `min(${MODAL_SIZES[size] || 560}px, calc(100vw - 32px))`,
    maxHeight: 'min(calc(100vh - 32px), calc(100dvh - 32px))',
    borderRadius: radius.lg,
    transform: visible ? 'translateY(0) scale(1)' : 'translateY(16px) scale(0.96)',
    transition: cssTransition('expressive', ['opacity', 'transform']),
  };

  const panelStyleBottomSheet = {
    width: '100%',
    maxHeight: 'min(85vh, 85dvh)',
    borderRadius: `${radius.lg} ${radius.lg} 0 0`,
    transform: visible ? 'translateY(0)' : 'translateY(100%)',
    transition: cssTransition('expressive', ['transform', 'opacity']),
  };

  const panelStyle = {
    background: 'var(--bg-elevated)',
    boxShadow: 'var(--shadow-modal)',
    display: 'flex',
    flexDirection: 'column',
    opacity: visible ? 1 : 0,
    overflow: 'hidden',
    ...(isBottomSheet ? panelStyleBottomSheet : panelStyleCenter),
  };

  return createPortal(
    <div
      style={overlayStyle}
      onMouseDown={(e) => {
        if (!closeOnOverlay) return;
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'fg-modal-title' : undefined}
        style={panelStyle}
      >
        {isBottomSheet && (
          <div
            aria-hidden="true"
            style={{
              width: 36,
              height: 4,
              background: 'var(--border-strong)',
              borderRadius: radius.full,
              margin: `${space.sm} auto 0`,
            }}
          />
        )}

        {title && (
          <header
            style={{
              padding: `${space.lg} ${space.xl}`,
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              gap: space.md,
            }}
          >
            {variant === 'danger' && (
              <AlertTriangle size={20} color={accentColor} aria-hidden="true" />
            )}
            <h2
              id="fg-modal-title"
              style={{
                ...typoStyle('display-md'),
                color: 'var(--text)',
                margin: 0,
                flex: 1,
              }}
            >
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Fermer"
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                padding: space.sm,
                borderRadius: radius.sm,
                display: 'flex',
                alignItems: 'center',
                transition: cssTransition('instant', ['color', 'background']),
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = 'var(--text)';
                e.currentTarget.style.background = 'var(--bg-subtle)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'var(--text-muted)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <X size={18} />
            </button>
          </header>
        )}

        <div
          style={{
            padding: `${space.lg} ${space.xl}`,
            overflowY: 'auto',
            flex: 1,
            ...typoStyle('body-md'),
            color: 'var(--text)',
          }}
        >
          {children}
        </div>

        {footer && (
          <footer
            style={{
              padding: `${space.md} ${space.xl}`,
              borderTop: '1px solid var(--border)',
              background: 'var(--bg-subtle)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: space.sm,
            }}
          >
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}

// ============================================================
// Button (variants : primary / secondary / ghost / danger)
// ============================================================

const BTN_HEIGHTS = { sm: 28, md: 36, lg: 44 };
const BTN_PADDINGS = { sm: `0 ${space.md}`, md: `0 ${space.lg}`, lg: `0 ${space.xl}` };
const BTN_FONTSIZE = { sm: '12px', md: '14px', lg: '15px' };
const BTN_ICONSIZE = { sm: 14, md: 16, lg: 18 };

const BTN_VARIANTS = {
  primary: {
    bg: 'var(--accent)',
    color: 'var(--text-on-accent)',
    border: '1px solid var(--accent)',
    hoverBg: 'var(--accent-hover)',
  },
  secondary: {
    bg: 'transparent',
    color: 'var(--text)',
    border: '1px solid var(--border-strong)',
    hoverBg: 'var(--bg-hover)',
  },
  ghost: {
    bg: 'transparent',
    color: 'var(--text-muted)',
    border: '1px solid transparent',
    hoverBg: 'var(--bg-subtle)',
  },
  danger: {
    bg: 'var(--danger)',
    color: '#ffffff',
    border: '1px solid var(--danger)',
    hoverBg: 'var(--danger)',
  },
};

function Button({
  variant = 'secondary',
  size = 'md',
  type = 'button',
  iconLeft: IconLeft,
  iconRight: IconRight,
  disabled = false,
  loading = false,
  fullWidth = false,
  children,
  ...rest
}) {
  const vs = BTN_VARIANTS[variant] || BTN_VARIANTS.secondary;
  const iconSize = BTN_ICONSIZE[size];

  return (
    <button
      type={type}
      disabled={disabled || loading}
      style={{
        height: BTN_HEIGHTS[size],
        padding: BTN_PADDINGS[size],
        background: vs.bg,
        color: vs.color,
        border: vs.border,
        borderRadius: radius.md,
        fontFamily: 'inherit',
        fontSize: BTN_FONTSIZE[size],
        fontWeight: 600,
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: space.sm,
        width: fullWidth ? '100%' : undefined,
        transition: cssTransition('instant', ['background', 'color', 'border-color']),
        whiteSpace: 'nowrap',
      }}
      onMouseEnter={(e) => {
        if (disabled || loading) return;
        e.currentTarget.style.background = vs.hoverBg;
      }}
      onMouseLeave={(e) => {
        if (disabled || loading) return;
        e.currentTarget.style.background = vs.bg;
      }}
      {...rest}
    >
      {loading ? (
        <RefreshCw size={iconSize} style={{ animation: 'fg-spin 1s linear infinite' }} />
      ) : IconLeft ? (
        <IconLeft size={iconSize} />
      ) : null}
      {children}
      {IconRight && !loading && <IconRight size={iconSize} />}
    </button>
  );
}

// ============================================================
// Navigation — TopNav (desktop)
// ============================================================

function TopNav() {
  const { activeId, setActiveId } = useNav();
  return (
    <nav
      role="tablist"
      aria-label="Modules FG-OS"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: space.xs,
        padding: `0 ${space.xl}`,
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg-elevated)',
        overflowX: 'auto',
      }}
    >
      {MODULES.map((m) => {
        const isActive = m.id === activeId;
        const Icon = m.icon;
        return (
          <button
            key={m.id}
            id={`tab-${m.id}`}
            role="tab"
            aria-selected={isActive}
            aria-controls={`panel-${m.id}`}
            onClick={() => setActiveId(m.id)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: space.sm,
              height: 48,
              padding: `0 ${space.md}`,
              border: 'none',
              background: 'transparent',
              borderBottom: `2px solid ${isActive ? 'var(--accent)' : 'transparent'}`,
              color: isActive ? 'var(--text)' : 'var(--text-muted)',
              fontFamily: 'inherit',
              fontSize: '14px',
              fontWeight: isActive ? 600 : 500,
              cursor: 'pointer',
              transition: cssTransition('instant', ['color', 'border-color']),
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => {
              if (!isActive) e.currentTarget.style.color = 'var(--text)';
            }}
            onMouseLeave={(e) => {
              if (!isActive) e.currentTarget.style.color = 'var(--text-muted)';
            }}
          >
            <Icon size={16} strokeWidth={1.75} />
            <span>{m.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

// ============================================================
// Navigation — BottomNav (mobile) + MoreSheet
// ============================================================

function BottomNav() {
  const { activeId, setActiveId } = useNav();
  const [moreOpen, setMoreOpen] = useState(false);

  return (
    <>
      <nav
        role="tablist"
        aria-label="Navigation principale"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          paddingBottom: 'env(safe-area-inset-bottom, 0)',
          display: 'grid',
          gridTemplateColumns: `repeat(${PRIMARY_MODULES.length + 1}, 1fr)`,
          background: 'var(--bg-elevated)',
          borderTop: '1px solid var(--border)',
          zIndex: 100,
        }}
      >
        {PRIMARY_MODULES.map((m) => {
          const isActive = m.id === activeId;
          const Icon = m.icon;
          return (
            <button
              key={m.id}
              id={`tab-${m.id}`}
              role="tab"
              aria-selected={isActive}
              aria-controls={`panel-${m.id}`}
              onClick={() => setActiveId(m.id)}
              style={{
                background: 'transparent',
                border: 'none',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                height: 56,
                color: isActive ? 'var(--accent)' : 'var(--text-muted)',
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: '10px',
                fontWeight: 600,
                position: 'relative',
                transition: cssTransition('instant', ['color']),
              }}
            >
              <Icon size={20} strokeWidth={isActive ? 2 : 1.75} />
              <span>{m.short}</span>
              {isActive && (
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: '24%',
                    right: '24%',
                    height: 2,
                    background: 'var(--accent)',
                    borderRadius: radius.full,
                  }}
                />
              )}
            </button>
          );
        })}

        <button
          type="button"
          aria-label="Plus de modules"
          aria-expanded={moreOpen}
          onClick={() => setMoreOpen(true)}
          style={{
            background: 'transparent',
            border: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            height: 56,
            color: NON_PRIMARY_MODULES.some((m) => m.id === activeId)
              ? 'var(--accent)'
              : 'var(--text-muted)',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: '10px',
            fontWeight: 600,
            position: 'relative',
          }}
        >
          <MoreHorizontal size={20} strokeWidth={1.75} />
          <span>Plus</span>
          {NON_PRIMARY_MODULES.some((m) => m.id === activeId) && (
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 0,
                left: '24%',
                right: '24%',
                height: 2,
                background: 'var(--accent)',
                borderRadius: radius.full,
              }}
            />
          )}
        </button>
      </nav>

      <Modal
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        placement="bottom-sheet"
        title="Modules"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
          {NON_PRIMARY_MODULES.map((m) => {
            const isActive = m.id === activeId;
            const Icon = m.icon;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => {
                  setActiveId(m.id);
                  setMoreOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: space.md,
                  padding: space.lg,
                  background: isActive ? 'var(--accent-subtle)' : 'transparent',
                  border: '1px solid var(--border)',
                  borderRadius: radius.md,
                  color: isActive ? 'var(--accent-strong)' : 'var(--text)',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: '15px',
                  fontWeight: 500,
                  textAlign: 'left',
                  transition: cssTransition('instant', ['background', 'color']),
                }}
              >
                <Icon size={20} strokeWidth={1.75} />
                <span style={{ flex: 1 }}>{m.label}</span>
                {isActive && (
                  <span style={{ ...typoStyle('body-sm'), color: 'var(--accent)' }}>
                    actif
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </Modal>
    </>
  );
}

// ============================================================
// Header (logo + theme toggle + statut sync)
// ============================================================

/**
 * Etat d'une integration a partir de /api/health (audit prod 18/08).
 * L'endpoint fait desormais un VRAI appel par integration et renvoie
 * `details.<cle>.state` : 'ok' | 'invalid' | 'absent' (+ 'present' pour cron,
 * secret partage sans service tiers a interroger). Le booleen de premier
 * niveau est conserve en retro-compat mais ne distingue pas absent/invalide —
 * on lit `details` en priorite, avec repli sur l'ancien format.
 */
function integrationState(status, key) {
  if (!status) return 'unknown';
  const s = status.details?.[key]?.state;
  if (s) return s;
  return status[key] ? 'ok' : 'absent';
}

const INTEGRATION_STATE_LABELS = {
  ok: '✓',
  invalid: 'configuré mais INVALIDE',
  absent: 'non configuré',
  present: 'configuré (non vérifiable)',
  unknown: '…',
};

function integrationStateColor(state) {
  if (state === 'unknown') return 'var(--text-subtle)';
  if (state === 'ok') return 'var(--accent)';
  if (state === 'invalid') return 'var(--danger)';
  if (state === 'present') return 'var(--info)';
  return 'var(--warning)';
}

function HealthDots() {
  const { status } = useHealth();
  const [hoverIdx, setHoverIdx] = useState(-1);
  const items = [
    { key: 'anthropic', label: 'Anthropic' },
    { key: 'gmail',     label: 'Gmail' },
    { key: 'notion',    label: 'Notion' },
    { key: 'sirene',    label: 'SIRENE' },
  ];
  const dotColor = (state) => integrationStateColor(state);
  // Tooltip global au survol container (title html), tooltip detaille par dot via tooltip custom positionne.
  const tooltipLines = items.map((it) => {
    const state = integrationState(status, it.key);
    return `${it.label} ${INTEGRATION_STATE_LABELS[state]}`;
  });
  return (
    <div
      title={tooltipLines.join('  ·  ')}
      aria-label="Statut des intégrations externes"
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: `${space.xs} ${space.sm}`,
        background: 'transparent',
        border: '1px solid var(--border)',
        borderRadius: radius.sm,
        cursor: 'help',
      }}
      onMouseLeave={() => setHoverIdx(-1)}
    >
      {items.map((it, idx) => {
        const state = integrationState(status, it.key);
        return (
          <span
            key={it.key}
            onMouseEnter={() => setHoverIdx(idx)}
            onFocus={() => setHoverIdx(idx)}
            onBlur={() => setHoverIdx(-1)}
            tabIndex={0}
            aria-label={`${it.label} ${INTEGRATION_STATE_LABELS[state]}`}
            style={{
              display: 'inline-block',
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: dotColor(state),
              outline: 'none',
              cursor: 'help',
            }}
          />
        );
      })}
      {hoverIdx >= 0 && (
        <span
          role="tooltip"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border-strong)',
            borderRadius: radius.sm,
            boxShadow: 'var(--shadow-md)',
            padding: `${space.xs} ${space.sm}`,
            ...typoStyle('body-sm'),
            color: 'var(--text)',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 999,
          }}
        >
          {items[hoverIdx].label} {INTEGRATION_STATE_LABELS[integrationState(status, items[hoverIdx].key)]}
          {status?.details?.[items[hoverIdx].key]?.error
            ? ` — ${status.details[items[hoverIdx].key].error}`
            : ''}
        </span>
      )}
    </div>
  );
}

function NotificationBell() {
  const { notifications, unread, markRead, dismiss, markAllRead } = useNotifications();
  const { setActiveId } = useNav();
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handleClickOut(e) {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    function handleKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOut);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOut);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  function handleNotifClick(n) {
    markRead(n.id);
    if (n.navigate?.module) {
      // Pre-fill cross-module via window-scoped param consume-once
      if (n.navigate.module === 'redaction' && n.navigate.context) {
        window.__FG_REDACTION_PREFILL__ = n.navigate.context;
      }
      setActiveId(n.navigate.module);
    }
    setOpen(false);
  }

  const top10 = notifications.slice(0, 10);
  const sevColor = {
    info: 'var(--info)',
    warning: 'var(--warning)',
    success: 'var(--accent)',
    danger: 'var(--danger)',
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={`Notifications (${unread} non lues)`}
        style={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 36,
          height: 36,
          background: 'transparent',
          border: '1px solid var(--border)',
          borderRadius: radius.sm,
          color: 'var(--text-muted)',
          cursor: 'pointer',
          transition: cssTransition('instant', ['color', 'background']),
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-subtle)'; e.currentTarget.style.color = 'var(--text)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; }}
      >
        <Bell size={16} strokeWidth={1.75} />
        {unread > 0 && (
          <span
            style={{
              position: 'absolute',
              top: -4,
              right: -4,
              minWidth: 16,
              height: 16,
              padding: '0 4px',
              background: 'var(--danger)',
              color: 'white',
              borderRadius: radius.full,
              fontSize: 10,
              fontWeight: 700,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid var(--bg-elevated)',
            }}
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Notifications"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            width: 360,
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: '70vh',
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border-strong)',
            borderRadius: radius.md,
            boxShadow: 'var(--shadow-modal)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <header
            style={{
              padding: `${space.md} ${space.lg}`,
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <strong style={{ ...typoStyle('body-md'), color: 'var(--text)' }}>
              Notifications {unread > 0 && <span style={{ color: 'var(--accent)' }}>({unread})</span>}
            </strong>
            {notifications.length > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  fontSize: 12,
                  textDecoration: 'underline',
                  fontFamily: 'inherit',
                }}
              >
                Tout marquer lu
              </button>
            )}
          </header>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {top10.length === 0 ? (
              <div
                style={{
                  padding: space.xl,
                  textAlign: 'center',
                  color: 'var(--text-subtle)',
                  ...typoStyle('body-sm'),
                }}
              >
                Aucune notification active.
                <br />
                Tout est sous controle.
              </div>
            ) : (
              top10.map((n) => (
                <div
                  key={n.id}
                  style={{
                    padding: `${space.md} ${space.lg}`,
                    borderBottom: '1px solid var(--border)',
                    background: n.read ? 'transparent' : 'var(--bg-subtle)',
                    display: 'flex',
                    gap: space.sm,
                    cursor: 'pointer',
                  }}
                  onClick={() => handleNotifClick(n)}
                >
                  <span
                    style={{
                      width: 4,
                      borderRadius: 2,
                      background: sevColor[n.severity] || 'var(--text-subtle)',
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ ...typoStyle('body-md'), color: 'var(--text)', fontWeight: 600 }}>
                      {n.title}
                    </div>
                    <div style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', marginTop: 2 }}>
                      {n.message}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); dismiss(n.id); }}
                    aria-label="Ignorer cette notification"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--text-subtle)',
                      cursor: 'pointer',
                      padding: 4,
                      borderRadius: radius.sm,
                      alignSelf: 'flex-start',
                    }}
                  >
                    <X size={14} strokeWidth={1.75} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Header({ isMobile }) {
  const { theme, toggleTheme } = useTheme();
  const { setActiveId } = useNav();
  const lastSync = getLastSync();

  return (
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: space.sm,
        padding: `${space.md} ${space.lg}`,
        borderBottom: isMobile ? '1px solid var(--border)' : 'none',
        background: 'var(--bg-elevated)',
        minHeight: 56,
      }}
    >
      {/* Logo Espace Conso (bascule clair/sombre) — clic = Dashboard (AMÉLIO 2) */}
      <button
        type="button"
        onClick={() => setActiveId('dashboard')}
        aria-label="Retour au tableau de bord"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: space.sm,
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          opacity: 1,
          transition: cssTransition('instant', ['opacity']),
        }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.8'; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
      >
        <img
          src={theme === 'dark' ? '/logo/espaceconso-light-text.png' : '/logo/espaceconso-dark-text.png'}
          alt="Espace Conso"
          height={30}
          style={{ height: 30, width: 'auto', objectFit: 'contain', display: 'block' }}
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      </button>

      <div style={{ flex: 1 }} />

      {/* Health dots (Bloc 4) */}
      {!isMobile && <HealthDots />}

      {/* Statut sync — clic = Réglages (AMÉLIO 3) */}
      <button
        type="button"
        onClick={() => setActiveId('reglages')}
        title={
          lastSync
            ? `Dernière sync : ${new Date(lastSync).toLocaleString('fr-FR')} — clic pour ouvrir Réglages`
            : 'Aucune sync effectuée — clic pour ouvrir Réglages'
        }
        aria-label="Statut synchronisation tarif (ouvrir Réglages)"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: space.xs,
          padding: `${space.xs} ${space.sm}`,
          background: 'transparent',
          border: '1px solid var(--border)',
          borderRadius: radius.sm,
          color: lastSync ? 'var(--accent)' : 'var(--warning)',
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontSize: '12px',
          fontWeight: 500,
          textDecoration: 'none',
          transition: cssTransition('instant', ['background', 'text-decoration-color']),
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'var(--bg-subtle)';
          e.currentTarget.style.textDecoration = 'underline';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.textDecoration = 'none';
        }}
      >
        <RefreshCw size={14} strokeWidth={1.75} />
        <span>{lastSync ? 'OK' : 'jamais'}</span>
      </button>

      {/* Bell notifications (Bloc 1) */}
      <NotificationBell />

      {/* Bascule theme */}
      <button
        type="button"
        onClick={toggleTheme}
        aria-label={`Activer le theme ${theme === 'light' ? 'sombre' : 'clair'}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 36,
          height: 36,
          background: 'transparent',
          border: '1px solid var(--border)',
          borderRadius: radius.sm,
          color: 'var(--text-muted)',
          cursor: 'pointer',
          transition: cssTransition('instant', ['color', 'background']),
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'var(--bg-subtle)';
          e.currentTarget.style.color = 'var(--text)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = 'var(--text-muted)';
        }}
      >
        {theme === 'light' ? (
          <Moon size={16} strokeWidth={1.75} />
        ) : (
          <Sun size={16} strokeWidth={1.75} />
        )}
      </button>
    </header>
  );
}

// ============================================================
// Modules placeholders
// ============================================================

function ModulePlaceholder({ id, title, icon: Icon, description, children }) {
  return (
    <section
      id={`panel-${id}`}
      role="tabpanel"
      aria-labelledby={`tab-${id}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: `${space['4xl']} ${space.lg}`,
        minHeight: '50vh',
        gap: space.lg,
      }}
    >
      <Icon size={40} strokeWidth={1.25} color="var(--text-subtle)" aria-hidden="true" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm, maxWidth: 480 }}>
        <h1 style={{ ...typoStyle('display-lg'), color: 'var(--text)', margin: 0 }}>
          {title}
        </h1>
        <p style={{ ...typoStyle('body-md'), color: 'var(--text-muted)', margin: 0 }}>
          {description}
        </p>
      </div>
      <span
        style={{
          display: 'inline-block',
          padding: `${space.xs} ${space.md}`,
          background: 'var(--accent-subtle)',
          color: 'var(--accent-strong)',
          ...typoStyle('body-sm'),
          borderRadius: radius.full,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        A venir
      </span>
      {children}
    </section>
  );
}

// ============================================================
// MISSION 1 — Module 1 Dashboard (logique metier)
// ============================================================
//
// Affiche :
//   - Compteur CA mensuel global (count-up 600ms easeOutExpo au 1er mount)
//   - Barre verticale 4 paliers 20k/33k/60k/80k (column-reverse :
//     20k visuellement en BAS, 80k en HAUT, sens de progression "jauge")
//     Flash + scale-bump uniquement si nouveau palier franchi (useRef compare)
//   - Bloc primes cumulees (paliers x 300 + ouvertures eligibles x 50)
//   - Projection fin de mois via calcProjectionCA
//   - Badge "dont X EUR recurrent parc securise" sous le compteur
//   - Liste 5 derniers prospects avec chip statut
//   - Statut sync tarif (bouton "Sync maintenant" desactive — Mission 2)
//
// Seed v1 applique si storage vide. Effacable via Reset Reglages (futur).

// --- Seed ----------------------------------------------------

function applySeedV1() {
  setCANonFG(14500);

  const now = new Date();
  const baseISO = (offsetDays) => {
    const d = new Date(now);
    d.setDate(d.getDate() + offsetDays);
    return d.toISOString();
  };

  const seedProspects = [
    {
      nom: 'Mairie Lyon 1er',
      type: 'externe',
      statut: 'repere',
      contact: 'Service Achats — m.dupont@mairie-lyon1.fr',
      notes: 'Reperage via WhatsApp groupe collegues. RDV pas pris.',
      caEstimeParcMensuel: 400,
      dateContact: baseISO(-12),
    },
    {
      nom: 'Cabinet Comptable Vauban',
      type: 'externe',
      statut: 'audite',
      contact: '04 78 XX XX XX — Mme Riviere',
      notes: 'Audit parc 22 mai : 3 HP M402 + 1 Brother HL-L3270. Devis a sortir.',
      caEstimeParcMensuel: 220,
      dateContact: baseISO(-5),
    },
    {
      nom: 'Etude Notariale Bellecour',
      type: 'externe',
      statut: 'devis_envoye',
      contact: 'me.bellecour@notaires-lyon.fr',
      notes: 'Devis envoye il y a 3 jours. Relance prevue J+5.',
      caEstimeParcMensuel: 350,
      dateContact: baseISO(-3),
      devis: {
        numero: 'DEV-2026-0008',
        montantHT: 1200,
        dateEnvoi: baseISO(-3),
      },
    },
    {
      nom: 'Leclerc Givors bureau',
      type: 'interne',
      statut: 'compte_ouvert',
      contact: 'Bureau accueil — Mme Lefevre',
      notes: 'Compte ouvert ce mois. 1ere commande comptant 1800 EUR.',
      caEstimeParcMensuel: 250,
      dateContact: baseISO(-8),
      devis: {
        numero: 'DEV-2026-0007',
        montantHT: 1800,
        dateEnvoi: baseISO(-8),
      },
    },
    {
      nom: 'Garage Renault Villeurbanne',
      type: 'externe',
      statut: 'recurrent',
      contact: 'compta@garage-renault-vlb.fr',
      notes: 'Client récurrent depuis 3 mois. Commande chaque trimestre.',
      caEstimeParcMensuel: 600,
      dateContact: baseISO(-2),
    },
  ];

  for (const p of seedProspects) addProspect(p);
}

// --- Data loader ---------------------------------------------

function loadDashboardData() {
  const prospects = getProspects();
  const caNonFG = getCANonFG().montant || 0;

  const now = new Date();
  const isCurrentMonth = (iso) => {
    if (!iso) return false;
    const d = new Date(iso);
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  };

  const STATUTS_FACTURES = ['devis_envoye', 'compte_ouvert', 'recurrent'];
  const caFG = prospects
    .filter((p) => STATUTS_FACTURES.includes(p.statut))
    .filter((p) => p.devis && isCurrentMonth(p.devis.dateEnvoi))
    .reduce((sum, p) => sum + (Number(p.devis.montantHT) || 0), 0);

  const caRecurrentParc = prospects
    .filter((p) => p.statut === 'recurrent')
    .reduce((sum, p) => sum + (Number(p.caEstimeParcMensuel) || 0), 0);

  const caTotal = caNonFG + caFG;

  const ouvertures = prospects
    .filter((p) => p.statut === 'compte_ouvert')
    .filter((p) => p.devis && isCurrentMonth(p.devis.dateEnvoi))
    .map((p) => ({ montant: p.devis.montantHT, prospectId: p.id }));

  const paliers = calcPaliersFranchis(caTotal);
  const primesOuv = calcPrimesOuvertures(ouvertures);
  const remuTotal = primesOuv.primeTotale + paliers.primesPaliers;

  const dayOfMonth = now.getDate();
  const totalDays = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const projection = calcProjectionCA(caTotal, dayOfMonth, totalDays);

  const recentProspects = [...prospects]
    .sort((a, b) => (b.dateDernierStatut || '').localeCompare(a.dateDernierStatut || ''))
    .slice(0, 5);

  const monthLabel = now.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const monthLabelCap = monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1);

  return {
    caNonFG,
    caFG,
    caTotal,
    caRecurrentParc,
    paliers,
    ouvertures,
    primesOuv,
    remuTotal,
    projection,
    recentProspects,
    monthLabel: monthLabelCap,
    dayOfMonth,
    totalDays,
  };
}

// --- Hooks animation -----------------------------------------

function useCountUp(target, duration = 600) {
  const [current, setCurrent] = useState(target);
  const startVal = useRef(target);
  const startTime = useRef(0);
  const isFirstMount = useRef(true);

  useEffect(() => {
    if (isFirstMount.current) {
      // AMÉLIO 6 : pour cibles > 5000, animer depuis max(0, target - 2000)
      // au lieu de 0 — perception moins incohérente sur gros chiffres CA.
      const initial = target > 5000 ? Math.max(0, target - 2000) : 0;
      startVal.current = initial;
      setCurrent(initial);
      isFirstMount.current = false;
    } else {
      startVal.current = current;
    }
    startTime.current = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - startTime.current) / duration);
      const eased = 1 - Math.pow(2, -10 * t);
      setCurrent(startVal.current + (target - startVal.current) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  return current;
}

function usePalierFlash(nbFranchis) {
  const prev = useRef(null);
  const [flashOn, setFlashOn] = useState(false);

  useEffect(() => {
    if (prev.current === null) {
      prev.current = nbFranchis;
      return undefined;
    }
    if (nbFranchis > prev.current) {
      setFlashOn(true);
      const t = window.setTimeout(() => setFlashOn(false), 600);
      prev.current = nbFranchis;
      return () => window.clearTimeout(t);
    }
    prev.current = nbFranchis;
    return undefined;
  }, [nbFranchis]);

  return flashOn;
}

// --- Sous-composants Module 1 --------------------------------

function DashboardHeader({ monthLabel, dayOfMonth, totalDays }) {
  const pct = Math.round((dayOfMonth / totalDays) * 100);
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: space.md,
        flexWrap: 'wrap',
      }}
    >
      <h1 style={{ ...typoStyle('display-lg'), color: 'var(--text)', margin: 0 }}>
        {monthLabel}
      </h1>
      <span style={{ ...typoStyle('mono-md'), color: 'var(--text-muted)' }}>
        Jour {dayOfMonth}/{totalDays} · {pct}% du mois
      </span>
    </div>
  );
}

function ProjectionVerdict({ projection }) {
  if (projection.prochainPalier === null) {
    return (
      <span
        style={{ ...typoStyle('body-sm'), color: 'var(--accent-strong)', fontWeight: 600 }}
      >
        ✓ Plafond 80k+ deja atteint — maximum primes
      </span>
    );
  }
  if (projection.atteindraProchain) {
    return (
      <span
        style={{ ...typoStyle('body-sm'), color: 'var(--accent-strong)', fontWeight: 600 }}
      >
        ✓ Atteindra {formatEUR(projection.prochainPalier, { withHT: false })} au rythme actuel
      </span>
    );
  }
  return (
    <span style={{ ...typoStyle('body-sm'), color: 'var(--warning)', fontWeight: 600 }}>
      Manque {formatEUR(projection.manqueProjecte, { withHT: false })} projete pour{' '}
      {formatEUR(projection.prochainPalier, { withHT: false })}
    </span>
  );
}

function CACompteur({ caTotal, caRecurrentParc, projection, caAnimated, flashOn }) {
  const reducedMotion = useReducedMotion();
  return (
    <div
      style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: radius.lg,
        padding: space.xl,
        display: 'flex',
        flexDirection: 'column',
        gap: space.lg,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
        <span
          style={{
            ...typoStyle('body-sm'),
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            fontWeight: 600,
          }}
        >
          CA mensuel global
        </span>
        <span
          style={{
            ...typoStyle('mono-xl'),
            color: flashOn ? 'var(--accent)' : 'var(--text)',
            transform: flashOn && !reducedMotion ? 'scale(1.04)' : 'scale(1)',
            transformOrigin: 'left center',
            transition: cssTransition('expressive', ['transform', 'color']),
            display: 'inline-block',
          }}
        >
          {formatEUR(Math.round(caAnimated), { withHT: false })}
        </span>
        {caRecurrentParc > 0 && (
          <span style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
            dont{' '}
            <strong style={{ color: 'var(--accent-strong)', fontWeight: 600 }}>
              {formatEUR(caRecurrentParc, { withHT: false })}
            </strong>{' '}
            recurrent parc securise
          </span>
        )}
      </div>

      <div
        style={{
          borderTop: '1px solid var(--border)',
          paddingTop: space.lg,
          display: 'flex',
          flexDirection: 'column',
          gap: space.xs,
        }}
      >
        <span
          style={{
            ...typoStyle('body-sm'),
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            fontWeight: 600,
          }}
        >
          Projete fin de mois
        </span>
        <div
          style={{ display: 'flex', alignItems: 'baseline', gap: space.sm, flexWrap: 'wrap' }}
        >
          <span style={{ ...typoStyle('mono-lg'), color: 'var(--text)' }}>
            {formatEUR(projection.caProjeteFin, { withHT: false })}
          </span>
          <span style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
            cadence {formatEUR(projection.cadenceJournaliere, { withHT: false })}/j
          </span>
        </div>
        <ProjectionVerdict projection={projection} />
      </div>
    </div>
  );
}

function PaliersBar({ paliers, caTotal, flashOn }) {
  const reducedMotion = useReducedMotion();
  // Ordre natif croissant [20000, 33000, 60000, 80000].
  // Rendering en column-reverse : 20k visuellement en BAS, 80k en HAUT
  // (sens "jauge" — progression vers le haut)
  const orderedPaliers = [...PALIERS_CA];

  return (
    <div
      style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: radius.lg,
        padding: space.xl,
        display: 'flex',
        flexDirection: 'column',
        gap: space.md,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span
          style={{
            ...typoStyle('body-sm'),
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            fontWeight: 600,
          }}
        >
          Paliers prime
        </span>
        <span style={{ ...typoStyle('mono-md'), color: 'var(--text-muted)' }}>
          {paliers.nbFranchis}/{PALIERS_CA.length}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column-reverse', gap: space.xs }}>
        {orderedPaliers.map((palier) => {
          const franchi = caTotal >= palier;
          const isProchain = paliers.prochainPalier === palier;

          let bg, fg, border;
          if (franchi) {
            bg = 'var(--accent)';
            fg = 'var(--text-on-accent)';
            border = 'var(--accent)';
          } else if (isProchain) {
            bg = 'var(--accent-subtle)';
            fg = 'var(--accent-strong)';
            border = 'var(--accent)';
          } else {
            bg = 'var(--bg-subtle)';
            fg = 'var(--text-subtle)';
            border = 'var(--border)';
          }

          return (
            <div
              key={palier}
              style={{
                height: 52,
                background: bg,
                color: fg,
                border: `1px solid ${border}`,
                borderRadius: radius.md,
                padding: `0 ${space.md}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: cssTransition('expressive', [
                  'background',
                  'color',
                  'border-color',
                  'transform',
                ]),
                transform:
                  flashOn && franchi && !reducedMotion ? 'scale(1.02)' : 'scale(1)',
              }}
            >
              <span style={{ ...typoStyle('mono-md'), fontWeight: 600 }}>
                {formatEUR(palier, { withHT: false })}
              </span>
              <span style={{ ...typoStyle('body-sm'), opacity: 0.85 }}>
                {franchi ? `+${PRIME_PAR_PALIER}€` : isProchain ? 'prochain' : ''}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PrimesBlock({ paliers, primesOuv, remuTotal }) {
  return (
    <div
      style={{
        background: 'var(--accent-subtle)',
        border: '1px solid var(--accent)',
        borderRadius: radius.lg,
        padding: space.lg,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: space.md,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span
          style={{
            ...typoStyle('body-sm'),
            color: 'var(--accent-strong)',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            fontWeight: 600,
          }}
        >
          Primes du mois
        </span>
        <span
          style={{
            ...typoStyle('body-sm'),
            color: 'var(--text-muted)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {paliers.nbFranchis} × {formatEUR(PRIME_PAR_PALIER, { withHT: false })} (paliers)
          {' + '}
          {primesOuv.eligibles} × {formatEUR(PRIME_OUVERTURE_COMPTE, { withHT: false })}{' '}
          (ouvertures ≥ {formatEUR(1500, { withHT: false })})
        </span>
      </div>
      <span style={{ ...typoStyle('mono-lg'), color: 'var(--accent)', fontWeight: 700 }}>
        = {formatEUR(remuTotal, { withHT: false })}
      </span>
    </div>
  );
}

function SyncStatusBlock() {
  const lastSync = getLastSync();
  const lastSyncFR = lastSync
    ? new Date(lastSync).toLocaleString('fr-FR', {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    : null;

  return (
    <div
      style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: radius.md,
        padding: space.lg,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: space.md,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: space.md }}>
        <RefreshCw
          size={18}
          strokeWidth={1.75}
          color={lastSync ? 'var(--accent)' : 'var(--warning)'}
        />
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ ...typoStyle('body-md'), color: 'var(--text)', fontWeight: 500 }}>
            Sync tarif Notion
          </span>
          <span style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
            {lastSyncFR
              ? `Dernière sync : ${lastSyncFR}`
              : 'Aucune sync effectuée — Mission 2 differee'}
          </span>
        </div>
      </div>
      <Button
        variant="secondary"
        size="sm"
        disabled
        title="Mission 2 differee — sync manuelle a venir"
        iconLeft={RefreshCw}
      >
        Sync maintenant
      </Button>
    </div>
  );
}

const STATUT_LABELS = {
  repere: 'Repéré',
  audite: 'Audité',
  devis_envoye: 'Devis envoyé',
  compte_ouvert: 'Compte ouvert',
  recurrent: 'Récurrent',
};

// Recurrent : SAME bg que compte_ouvert (accent-subtle + accent-strong)
// mais border 1px solid accent pour le distinguer visuellement.
// Pas de fill solide vert (trop lourd sur liste 5 items).
const STATUT_STYLES = {
  repere: {
    bg: 'var(--bg-subtle)',
    fg: 'var(--text-muted)',
    border: '1px solid var(--border)',
  },
  audite: {
    bg: 'var(--info-subtle)',
    fg: 'var(--info)',
    border: '1px solid transparent',
  },
  devis_envoye: {
    bg: 'var(--warning-subtle)',
    fg: 'var(--warning)',
    border: '1px solid transparent',
  },
  compte_ouvert: {
    bg: 'var(--accent-subtle)',
    fg: 'var(--accent-strong)',
    border: '1px solid transparent',
  },
  recurrent: {
    bg: 'var(--accent-subtle)',
    fg: 'var(--accent-strong)',
    border: '1px solid var(--accent)',
  },
};

function StatutChip({ statut }) {
  const styles = STATUT_STYLES[statut] || STATUT_STYLES.repere;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: `2px ${space.sm}`,
        background: styles.bg,
        color: styles.fg,
        border: styles.border,
        borderRadius: radius.full,
        fontSize: '11px',
        fontWeight: 600,
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
      }}
    >
      {STATUT_LABELS[statut] || statut}
    </span>
  );
}

function RecentProspects({ prospects }) {
  if (!prospects.length) {
    return (
      <div
        style={{
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          borderRadius: radius.md,
          padding: space.xl,
          textAlign: 'center',
          color: 'var(--text-muted)',
        }}
      >
        Aucun prospect saisi. Repere ton premier compte via Pipeline.
      </div>
    );
  }

  return (
    <div
      style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: radius.lg,
        overflow: 'hidden',
      }}
    >
      <header
        style={{
          padding: `${space.md} ${space.lg}`,
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}
      >
        <span
          style={{
            ...typoStyle('body-sm'),
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            fontWeight: 600,
          }}
        >
          Derniers prospects
        </span>
        <span style={{ ...typoStyle('body-sm'), color: 'var(--text-subtle)' }}>
          {prospects.length} affiches
        </span>
      </header>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {prospects.map((p, i) => (
          <li
            key={p.id}
            style={{
              padding: `${space.md} ${space.lg}`,
              borderTop: i === 0 ? 'none' : '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              gap: space.md,
              justifyContent: 'space-between',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  ...typoStyle('body-md'),
                  color: 'var(--text)',
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {p.nom}
              </div>
              <div style={{ ...typoStyle('body-sm'), color: 'var(--text-subtle)' }}>
                {p.type === 'interne' ? 'GMS bureau' : 'B2B externe'}
                {p.caEstimeParcMensuel
                  ? ` · ~${formatEUR(p.caEstimeParcMensuel, { withHT: false })}/mois parc`
                  : ''}
              </div>
            </div>
            <StatutChip statut={p.statut} />
          </li>
        ))}
      </ul>
    </div>
  );
}

// --- Composant principal Module 1 ----------------------------

function ActionsDuJour() {
  const { notifications, markRead } = useNotifications();
  const { setActiveId } = useNav();
  if (!notifications.length) return null;

  // Priorite : warning > danger > success > info
  const order = { danger: 0, warning: 1, success: 2, info: 3 };
  const top3 = [...notifications]
    .sort((a, b) => (order[a.severity] ?? 9) - (order[b.severity] ?? 9))
    .slice(0, 3);

  function handleAction(n) {
    markRead(n.id);
    if (n.navigate?.module === 'redaction' && n.navigate.context) {
      window.__FG_REDACTION_PREFILL__ = n.navigate.context;
    }
    if (n.navigate?.module) setActiveId(n.navigate.module);
  }

  const sevColor = {
    info: 'var(--info)',
    warning: 'var(--warning)',
    success: 'var(--accent)',
    danger: 'var(--danger)',
  };
  const sevIcon = {
    info: Info,
    warning: AlertTriangle,
    success: CheckCircle2,
    danger: AlertTriangle,
  };

  return (
    <section
      style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: radius.lg,
        overflow: 'hidden',
      }}
    >
      <header
        style={{
          padding: `${space.md} ${space.lg}`,
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: space.sm,
        }}
      >
        <Zap size={16} strokeWidth={1.75} color="var(--accent)" />
        <span
          style={{
            ...typoStyle('body-sm'),
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            fontWeight: 600,
          }}
        >
          Actions du jour
        </span>
      </header>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {top3.map((n) => {
          const Icon = sevIcon[n.severity] || Info;
          return (
            <li
              key={n.id}
              style={{
                padding: `${space.md} ${space.lg}`,
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                gap: space.md,
                alignItems: 'center',
              }}
            >
              <Icon size={18} strokeWidth={1.75} color={sevColor[n.severity]} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ ...typoStyle('body-md'), color: 'var(--text)', fontWeight: 600 }}>
                  {n.title}
                </div>
                <div style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
                  {n.message}
                </div>
              </div>
              <Button variant="primary" size="sm" iconRight={ArrowRight} onClick={() => handleAction(n)}>
                Action
              </Button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * Modale "Commande confirmee" (SPEC-04) : 2e point d'entree de l'evenement
 * commande, a faible friction — ref + quantite pre-remplies depuis la
 * prediction, editables. Ferme la boucle : sans elle, seule la 1ere vente
 * (devis accepte) alimenterait le socle et la prediction deriverait des la
 * 2e commande.
 */
function ConfirmCommandeModal({ open, onClose, prospect, prefill, onConfirmed }) {
  const [ref, setRef] = useState('');
  const [designation, setDesignation] = useState('');
  const [qte, setQte] = useState(1);

  useEffect(() => {
    if (open) {
      setRef(prefill?.ref_interne || '');
      setDesignation(prefill?.designation || '');
      setQte(prefill?.quantite || 1);
    }
  }, [open, prefill]);

  const isValid = ref.trim().length > 0 && Number(qte) >= 1;

  function handleConfirm() {
    if (!isValid || !prospect) return;
    const commande = addCommande({
      prospectId: prospect.id,
      date: new Date().toISOString(),
      lignes: [{
        ref_interne: ref.trim(),
        designation: designation.trim() || null,
        quantite: Number(qte) || 1,
        pa_ht: prefill?.pa_ht ?? null,
      }],
      source: 'reassort_confirme',
    });
    if (commande) enrichCommandeRendements(commande);
    onConfirmed?.(commande);
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Commande confirmee — ${prospect?.nom || ''}`}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button variant="primary" onClick={handleConfirm} disabled={!isValid}>Enregistrer</Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}>
        <p style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', margin: 0 }}>
          Enregistre l'achat reel : le prochain reassort sera calcule depuis cette commande.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
          <label htmlFor="cc-ref" style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', fontWeight: 500 }}>Reference *</label>
          <input id="cc-ref" type="text" value={ref} onChange={(e) => setRef(e.target.value)} style={inputBaseStyle} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
          <label htmlFor="cc-des" style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', fontWeight: 500 }}>Designation</label>
          <input id="cc-des" type="text" value={designation} onChange={(e) => setDesignation(e.target.value)} style={inputBaseStyle} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
          <label htmlFor="cc-qte" style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', fontWeight: 500 }}>Quantite *</label>
          <input id="cc-qte" type="number" min={1} step="1" value={qte} onChange={(e) => setQte(e.target.value)} style={{ ...inputBaseStyle, maxWidth: 120 }} />
        </div>
      </div>
    </Modal>
  );
}

/** Badge "estimation approximative" (SPEC-04) : rendement = moyenne de famille. */
function EstimationBadge() {
  return (
    <span style={{
      ...typoStyle('body-sm'),
      padding: `1px ${space.sm}`,
      background: 'var(--warning-subtle)',
      color: 'var(--warning)',
      borderRadius: radius.full,
      whiteSpace: 'nowrap',
    }}>
      estimation approximative
    </span>
  );
}

/**
 * Carte Dashboard "Reassorts a venir" (14 prochains jours, echus inclus),
 * triee par date. Alimentee par getReassortsAVenir() — la meme fonction
 * servira le Brief matinal (backlog n°3).
 */
function ReassortsAVenir() {
  const { setActiveId } = useNav();
  const { showToast } = useToast();
  const [items, setItems] = useState(() => getReassortsAVenir({ horizonJours: 14 }));
  const [confirmFor, setConfirmFor] = useState(null); // { prospect, prediction }

  function refresh() {
    setItems(getReassortsAVenir({ horizonJours: 14 }));
  }

  function handleMail(item) {
    window.__FG_REDACTION_PREFILL__ = { prospectId: item.prospect.id, type: 'relance_reassort' };
    setActiveId('redaction');
  }

  if (!items.length) return null;

  return (
    <section style={{
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border)',
      borderRadius: radius.lg,
      overflow: 'hidden',
    }}>
      <header style={{
        padding: `${space.md} ${space.lg}`,
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        gap: space.sm,
      }}>
        <RefreshCw size={16} strokeWidth={1.75} color="var(--accent)" />
        <span style={{
          ...typoStyle('body-sm'),
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          fontWeight: 600,
        }}>
          Reassorts a venir (14 j)
        </span>
      </header>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {items.slice(0, 6).map((item) => {
          const { prospect, prediction } = item;
          const dateStr = prediction.dateReassort.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
          const echu = prediction.dateReassort.getTime() < Date.now();
          return (
            <li key={`${prospect.id}:${prediction.ref_interne}`} style={{
              padding: `${space.md} ${space.lg}`,
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              gap: space.md,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}>
              <span style={{ ...typoStyle('mono-md'), color: echu ? 'var(--danger)' : 'var(--text)', fontWeight: 700, minWidth: 44 }}>
                {dateStr}
              </span>
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ ...typoStyle('body-md'), color: 'var(--text)', fontWeight: 600 }}>{prospect.nom}</div>
                <div style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', display: 'flex', gap: space.sm, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span>{prediction.ref_interne} ×{prediction.quantite}</span>
                  {prediction.estimation && <EstimationBadge />}
                </div>
              </div>
              <div style={{ display: 'flex', gap: space.sm }}>
                <Button variant="secondary" size="sm" onClick={() => setConfirmFor(item)}>
                  Commande confirmee
                </Button>
                <Button variant="primary" size="sm" iconRight={ArrowRight} onClick={() => handleMail(item)}>
                  Mail
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
      <ConfirmCommandeModal
        open={!!confirmFor}
        onClose={() => setConfirmFor(null)}
        prospect={confirmFor?.prospect}
        prefill={confirmFor ? {
          ref_interne: confirmFor.prediction.ref_interne,
          designation: confirmFor.prediction.designation,
          quantite: confirmFor.prediction.quantite,
          pa_ht: confirmFor.prediction.pa_ht,
        } : null}
        onConfirmed={() => {
          showToast('Commande enregistree — prochain reassort recalcule', { kind: 'success' });
          refresh();
        }}
      />
    </section>
  );
}

/**
 * Carte Dashboard "Prospects sans prochaine action" (SPEC-05, Partie 2).
 * Pas un blocage retroactif : liste les cas historiques a corriger au
 * rythme de Wissam, en avant tout nouveau flux est deja garde par
 * addInteraction (storage.js).
 */
function ProspectsSansAction() {
  const { setActiveId } = useNav();
  const [items, setItems] = useState(() => listProspectsSansProchaineAction());

  if (!items.length) return null;

  function ouvrir(prospectId) {
    window.__FG_PIPELINE_OPEN_ID__ = prospectId;
    setActiveId('pipeline');
  }

  return (
    <section style={{
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border)',
      borderRadius: radius.lg,
      overflow: 'hidden',
    }}>
      <header style={{
        padding: `${space.md} ${space.lg}`,
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        gap: space.sm,
      }}>
        <AlertTriangle size={16} strokeWidth={1.75} color="var(--warning)" />
        <span style={{
          ...typoStyle('body-sm'),
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          fontWeight: 600,
        }}>
          Prospects sans prochaine action ({items.length})
        </span>
      </header>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {items.slice(0, 6).map((p) => (
          <li key={p.id} style={{
            padding: `${space.md} ${space.lg}`,
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: space.md,
            flexWrap: 'wrap',
          }}>
            <div>
              <div style={{ ...typoStyle('body-md'), color: 'var(--text)', fontWeight: 600 }}>{p.nom}</div>
              <StatutChip statut={p.statut} />
            </div>
            <Button variant="secondary" size="sm" onClick={() => ouvrir(p.id)}>
              Ouvrir
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Module1Dashboard() {
  const isMobile = useMediaQuery(BREAKPOINT_MOBILE);
  const { showToast } = useToast();
  const [data, setData] = useState(() => loadDashboardData());

  // Seed v1 si storage vide
  useEffect(() => {
    if (getProspects().length === 0 && (getCANonFG().montant || 0) === 0) {
      applySeedV1();
      showToast(
        'Seed demo charge : 5 prospects + CA 14 500 EUR. Reset via Reglages.',
        { kind: 'info', duration: 5000 },
      );
      setData(loadDashboardData());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-load au focus window, debounced 300ms pour eviter re-renders rapides
  useEffect(() => {
    let timeoutId;
    const handler = () => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        setData(loadDashboardData());
      }, 300);
    };
    window.addEventListener('focus', handler);
    return () => {
      window.removeEventListener('focus', handler);
      window.clearTimeout(timeoutId);
    };
  }, []);

  const caAnimated = useCountUp(data.caTotal, 600);
  const flashOn = usePalierFlash(data.paliers.nbFranchis);

  return (
    <section
      id="panel-dashboard"
      role="tabpanel"
      aria-labelledby="tab-dashboard"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: space.lg,
        maxWidth: 1100,
        margin: '0 auto',
        width: '100%',
      }}
    >
      <DashboardHeader
        monthLabel={data.monthLabel}
        dayOfMonth={data.dayOfMonth}
        totalDays={data.totalDays}
      />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '3fr 2fr',
          gap: space.lg,
          alignItems: 'stretch',
        }}
      >
        <CACompteur
          caTotal={data.caTotal}
          caRecurrentParc={data.caRecurrentParc}
          projection={data.projection}
          caAnimated={caAnimated}
          flashOn={flashOn}
        />
        <PaliersBar paliers={data.paliers} caTotal={data.caTotal} flashOn={flashOn} />
      </div>

      <PrimesBlock
        paliers={data.paliers}
        primesOuv={data.primesOuv}
        remuTotal={data.remuTotal}
      />

      <ActionsDuJour />

      <ReassortsAVenir />

      <ProspectsSansAction />

      <SyncStatusBlock />

      <RecentProspects prospects={data.recentProspects} />
    </section>
  );
}

function Module2Sync() {
  return (
    <ModulePlaceholder
      id="sync"
      title="Sync Tarif"
      icon={RefreshCw}
      description="Cron Cloudflare 7h00 quotidien : pull du fichier XLSM attache a la page Notion, parse, ecriture KV namespace FG_CATALOGUE. Mission 2, differee."
    />
  );
}

// ============================================================
// MISSION 3 — Module 3 Audit de parc
// ============================================================
//
// Vue accueil = "Nouvel audit" + liste 5 audits recents (reprendre / supprimer).
// Vue editor = client + N imprimantes saisies + cards refs compat + actions.
//
// Flux :
//   1. Wissam tape modele imprimante (autocomplete debounce 150ms, top 8)
//   2. Match via matchPrinterModel : card refs compat (toner/tambour/collecteur)
//      OU alerte hard rouge si Xerox/Ricoh/OKI
//      OU alerte warning si modele inconnu (autres marques)
//   3. Refs toutes cochees par defaut, decoche celles en stock client
//   4. Notes imprimante 1 ligne (localisation, pages/mois, etat cartouche)
//   5. Save audit OU "Generer devis" -> Module 4 via setActiveAuditId
//
// Pas d'auto-save. Save explicite. Modal danger sortie si isDirty.
//
// Note type "Autre" : computeRowFromQuery inclut TOUTES les refs matchees
// dans refsSelectionnees (ref_interne[]) sans filtrer par type. Si le catalogue
// futur ajoute un type ("Bouteille d'encre", "Kit maintenance"), les refs sont
// preservees pour le passage Module 4. Seul TYPE_ORDER limite l'affichage.

// --- Helpers Module 3 ----------------------------------------

function genAuditId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `a-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function makeEmptyImprimanteRow() {
  return {
    id: genAuditId(),
    query: '',
    matchedModel: null,
    marque: null,
    uncoveredBrand: null,
    matches: [],
    refsSelectionnees: [],
    notes: '',
  };
}

function makeEmptyAuditDraft() {
  return {
    auditId: null,
    clientName: '',
    prospectId: null,
    imprimantes: [makeEmptyImprimanteRow()],
    createdAt: null,
  };
}

/**
 * Lance matchPrinterModel et retourne un patch pour la row imprimante.
 * Toutes les refs matchees (TOUS types : Toner/Tambour/Collecteur/Autre/futur)
 * sont incluses dans refsSelectionnees. TYPE_ORDER limite uniquement l'affichage,
 * pas la collecte pour le passage Module 4.
 */
function computeRowFromQuery(query) {
  const trimmed = (query || '').trim();
  if (!trimmed) {
    return {
      matches: [],
      matchedModel: null,
      marque: null,
      uncoveredBrand: null,
      refsSelectionnees: [],
    };
  }
  const result = matchPrinterModel(trimmed, compatLaserData);
  if (result.uncoveredBrand) {
    return {
      matches: [],
      matchedModel: null,
      marque: null,
      uncoveredBrand: result.uncoveredBrand,
      refsSelectionnees: [],
    };
  }
  // IMPORTANT : map sur TOUTES les matches sans filtre type
  // -> refs "Autre" ou type futur incluses pour Module 4
  const allRefIds = result.matches.map((m) => m.ref.ref_interne);
  const top = result.matches[0];
  return {
    matches: result.matches,
    matchedModel: top?.matchedModel || trimmed,
    marque: top?.ref?.marque_imprimante || null,
    uncoveredBrand: null,
    refsSelectionnees: allRefIds,
  };
}

function useDebouncedValue(value, delay = 150) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function formatRelativeDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "aujourd'hui";
  if (diffDays === 1) return 'hier';
  if (diffDays < 7) return `il y a ${diffDays} jours`;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

const inputBaseStyle = {
  width: '100%',
  height: 40,
  padding: `0 ${space.md}`,
  fontFamily: 'inherit',
  // 16px : empeche le zoom auto iOS Safari au focus (seuil < 16px declenche le zoom).
  fontSize: '16px',
  color: 'var(--text)',
  background: 'var(--bg)',
  border: '1px solid var(--border-strong)',
  borderRadius: radius.md,
  outline: 'none',
  transition: cssTransition('instant', ['border-color', 'background']),
};

// --- AutocompleteInput (generique : printers OU prospects) ---

function AutocompleteInput({
  value,
  onChange,
  onSelect,
  options,
  placeholder,
  autoFocus = false,
  ariaLabel,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const inputRef = useRef(null);
  // Suppress reopen for 1 effect cycle after selection (sinon useEffect rouvre car suggestions stables)
  const suppressReopenRef = useRef(false);
  const listboxId = useRef(`fg-listbox-${genAuditId()}`).current;
  const debouncedValue = useDebouncedValue(value, 150);

  const suggestions = useMemo(() => {
    if (!debouncedValue || debouncedValue.length < 2) return [];
    return suggestModels(debouncedValue, options, 8);
  }, [debouncedValue, options]);

  useEffect(() => {
    if (suppressReopenRef.current) {
      suppressReopenRef.current = false;
      setIsOpen(false);
      return;
    }
    if (suggestions.length > 0 && document.activeElement === inputRef.current) {
      setIsOpen(true);
      setHighlightIdx(-1);
    } else if (suggestions.length === 0) {
      setIsOpen(false);
    }
  }, [suggestions]);

  function commitSelection(picked) {
    suppressReopenRef.current = true;
    onChange(picked);
    onSelect(picked);
    setIsOpen(false);
    setHighlightIdx(-1);
    // Blur explicite : empeche le useEffect de rouvrir au prochain cycle
    if (inputRef.current && typeof inputRef.current.blur === 'function') {
      inputRef.current.blur();
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen && suggestions.length > 0) {
        setIsOpen(true);
        setHighlightIdx(0);
      } else {
        setHighlightIdx((i) => (i + 1) % suggestions.length);
      }
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!isOpen) return;
      setHighlightIdx((i) => (i - 1 + suggestions.length) % suggestions.length);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isOpen && highlightIdx >= 0) {
        commitSelection(suggestions[highlightIdx]);
      } else if (value.trim()) {
        commitSelection(value.trim());
      }
      return;
    }
    if (e.key === 'Escape') {
      setIsOpen(false);
      return;
    }
    if (e.key === 'Tab') {
      setIsOpen(false);
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (suggestions.length > 0) setIsOpen(true);
        }}
        onBlur={() => {
          window.setTimeout(() => setIsOpen(false), 150);
        }}
        placeholder={placeholder}
        autoFocus={autoFocus}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-autocomplete="list"
        aria-controls={listboxId}
        aria-activedescendant={
          highlightIdx >= 0 ? `${listboxId}-opt-${highlightIdx}` : undefined
        }
        aria-label={ariaLabel}
        style={{
          ...inputBaseStyle,
          borderColor: isOpen ? 'var(--border-focus)' : 'var(--border-strong)',
        }}
      />
      {isOpen && suggestions.length > 0 && (
        <ul
          id={listboxId}
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            margin: 0,
            padding: space.xs,
            listStyle: 'none',
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
            borderRadius: radius.md,
            boxShadow: 'var(--shadow-md)',
            maxHeight: 280,
            overflowY: 'auto',
            zIndex: 50,
          }}
        >
          {suggestions.map((sug, idx) => {
            const isHighlight = idx === highlightIdx;
            return (
              <li
                key={sug}
                id={`${listboxId}-opt-${idx}`}
                role="option"
                aria-selected={isHighlight}
                onMouseDown={(e) => {
                  e.preventDefault();
                  commitSelection(sug);
                }}
                onMouseEnter={() => setHighlightIdx(idx)}
                style={{
                  padding: `${space.sm} ${space.md}`,
                  borderRadius: radius.sm,
                  cursor: 'pointer',
                  background: isHighlight ? 'var(--bg-hover)' : 'transparent',
                  color: isHighlight ? 'var(--text)' : 'var(--text-muted)',
                  fontSize: '14px',
                  transition: cssTransition('instant', ['background', 'color']),
                }}
              >
                {sug}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// --- AuditClientBlock ----------------------------------------

function AuditClientBlock({ clientName, prospectId, prospects, onChange }) {
  const prospectNames = useMemo(() => prospects.map((p) => p.nom), [prospects]);

  function handleSelect(picked) {
    const matched = prospects.find((p) => p.nom === picked);
    onChange({ clientName: picked, prospectId: matched?.id || null });
  }

  return (
    <div
      style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: radius.lg,
        padding: space.lg,
        display: 'flex',
        flexDirection: 'column',
        gap: space.sm,
      }}
    >
      <label
        style={{
          ...typoStyle('body-sm'),
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: space.xs,
        }}
      >
        <Building2 size={14} strokeWidth={1.75} />
        Client
      </label>
      <AutocompleteInput
        value={clientName}
        onChange={(v) => onChange({ clientName: v, prospectId: null })}
        onSelect={handleSelect}
        options={prospectNames}
        placeholder="Nom du client (autocomplete sur prospects existants)"
        ariaLabel="Nom du client"
      />
      {prospectId ? (
        <span style={{ ...typoStyle('body-sm'), color: 'var(--accent-strong)' }}>
          ✓ Prospect existant lie a cet audit
        </span>
      ) : clientName ? (
        <span style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
          Client standalone (non lie a un prospect). Convertir en prospect : a venir.
        </span>
      ) : null}
    </div>
  );
}

// --- UncoveredAlert / UnknownAlert ---------------------------

function UncoveredAlert({ brand }) {
  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        background: 'var(--danger-subtle)',
        border: '1px solid var(--danger)',
        borderRadius: radius.md,
        padding: space.lg,
        display: 'flex',
        gap: space.md,
        alignItems: 'flex-start',
      }}
    >
      <AlertTriangle size={20} color="var(--danger)" strokeWidth={2} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
        <strong style={{ ...typoStyle('body-md'), color: 'var(--danger)' }}>
          Parc non adressable — marque {brand.toUpperCase()}
        </strong>
        <span style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
          Xerox, Ricoh et OKI ne sont pas couverts par le catalogue G&G.
          Ne pas promettre la livraison de consommables pour cette imprimante.
        </span>
        <span style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
          Laisse le client sur Amazon Business pour ce modele, ou prospecte
          les autres imprimantes du parc.
        </span>
      </div>
    </div>
  );
}

function UnknownAlert({ query }) {
  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        background: 'var(--warning-subtle)',
        border: '1px solid var(--warning)',
        borderRadius: radius.md,
        padding: space.lg,
        display: 'flex',
        gap: space.md,
        alignItems: 'flex-start',
      }}
    >
      <AlertTriangle size={20} color="var(--warning)" strokeWidth={2} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
        <strong style={{ ...typoStyle('body-md'), color: 'var(--warning)' }}>
          Modele inconnu : "{query}"
        </strong>
        <span style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
          Pas de correspondance dans le catalogue. Verifie l'orthographe ou regarde
          la plaque arriere de l'imprimante.
        </span>
        <span style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
          Marques couvertes : HP, Lexmark, Brother, Epson, Canon, Kyocera, Samsung.
        </span>
      </div>
    </div>
  );
}

// --- ImprimanteResults (card refs groupees par type) ---------

// Limite affichage. Types hors liste (Autre, futurs) restent dans
// refsSelectionnees pour passage Module 4, juste non visibles ici.
const TYPE_ORDER = ['Toner', 'Tambour', 'Collecteur'];

function ImprimanteResults({ row, onToggleRef }) {
  const groups = useMemo(() => {
    const out = { Toner: [], Tambour: [], Collecteur: [] };
    for (const m of row.matches) {
      const t = m.ref.type;
      if (out[t]) out[t].push(m);
    }
    return out;
  }, [row.matches]);

  const avgPrice = useMemo(() => {
    if (row.matches.length === 0) return 0;
    const sum = row.matches.reduce((s, m) => s + (Number(m.ref.pa_ht) || 0), 0);
    return Math.round(sum / row.matches.length);
  }, [row.matches]);

  return (
    <div
      style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: radius.md,
        padding: space.lg,
        display: 'flex',
        flexDirection: 'column',
        gap: space.md,
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: space.md,
          paddingBottom: space.sm,
          borderBottom: '1px solid var(--border)',
        }}
      >
        <Printer size={18} strokeWidth={1.75} color="var(--accent)" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              ...typoStyle('body-md'),
              color: 'var(--text)',
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.matchedModel}
          </div>
          <div style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
            {row.marque ? `Marque ${row.marque} · ` : ''}
            {row.matches.length} ref{row.matches.length > 1 ? 's' : ''} adressable
            {row.matches.length > 1 ? 's' : ''}
            {avgPrice > 0 ? ` · ~${formatEUR(avgPrice, { withHT: false })} moy.` : ''}
          </div>
        </div>
      </header>

      {TYPE_ORDER.map((type) => {
        const items = groups[type];
        return (
          <section
            key={type}
            style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}
          >
            <h4
              style={{
                ...typoStyle('body-sm'),
                color: 'var(--text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontWeight: 600,
                margin: 0,
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <span>{type}s ({items.length})</span>
              {items.length === 0 && (
                <span
                  style={{
                    ...typoStyle('body-sm'),
                    color: 'var(--text-subtle)',
                    textTransform: 'none',
                    letterSpacing: 0,
                    fontWeight: 400,
                  }}
                >
                  aucun compatible
                </span>
              )}
            </h4>
            {items.length > 0 && (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {items.map((m) => {
                  const refId = m.ref.ref_interne;
                  const checked = row.refsSelectionnees.includes(refId);
                  const label =
                    m.ref.couleur && m.ref.couleur !== 'N/A'
                      ? `${m.ref.type} ${m.ref.couleur}`
                      : m.ref.type;
                  return (
                    <li key={refId}>
                      <label
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'auto 110px 1fr auto',
                          alignItems: 'center',
                          gap: space.sm,
                          padding: `${space.xs} ${space.sm}`,
                          borderRadius: radius.sm,
                          cursor: 'pointer',
                          background: checked ? 'transparent' : 'var(--bg-subtle)',
                          opacity: checked ? 1 : 0.65,
                          transition: cssTransition('instant', ['background', 'opacity']),
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => onToggleRef(refId)}
                          aria-label={`Selectionner ${refId} ${label}`}
                          style={{ accentColor: 'var(--accent)', cursor: 'pointer' }}
                        />
                        <span
                          style={{
                            ...typoStyle('mono-md'),
                            color: 'var(--text)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={refId}
                        >
                          {refId}
                        </span>
                        <span
                          style={{
                            ...typoStyle('body-sm'),
                            color: 'var(--text-muted)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={m.ref.designation}
                        >
                          {label}
                        </span>
                        <span style={{ ...typoStyle('mono-md'), color: 'var(--text)', textAlign: 'right' }}>
                          {formatEUR(m.ref.pa_ht, { withHT: false })}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        );
      })}

      <footer style={{ ...typoStyle('body-sm'), color: 'var(--text-subtle)' }}>
        Toutes les refs selectionnees par defaut. Decoche les refs deja en stock client.
      </footer>
    </div>
  );
}

// --- AuditImprimanteRow --------------------------------------

function AuditImprimanteRow({
  row,
  index,
  knownModels,
  onUpdate,
  onCommitQuery,
  onToggleRef,
  onRemove,
}) {
  // Auto-commit debounced : declenche matching (donc detection Xerox/Ricoh/OKI)
  // pendant la saisie, sans attendre Enter ou click suggestion.
  const lastCommittedRef = useRef(row.matchedModel || row.query || '');
  const debouncedQuery = useDebouncedValue(row.query, 350);
  useEffect(() => {
    const trimmed = (debouncedQuery || '').trim();
    if (trimmed && trimmed !== lastCommittedRef.current) {
      lastCommittedRef.current = trimmed;
      onCommitQuery(row.id, trimmed);
    } else if (!trimmed && lastCommittedRef.current) {
      lastCommittedRef.current = '';
    }
  }, [debouncedQuery, row.id, onCommitQuery]);

  const showCard = row.matches.length > 0 && !row.uncoveredBrand;
  const showUncovered = !!row.uncoveredBrand;
  const showUnknown = !showUncovered && !showCard && row.query.trim().length > 0;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: space.md,
        padding: space.lg,
        background: 'var(--bg-subtle)',
        border: '1px solid var(--border)',
        borderRadius: radius.lg,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: space.sm }}>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: space.xs,
          }}
        >
          <label
            style={{
              ...typoStyle('body-sm'),
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              fontWeight: 600,
            }}
          >
            Imprimante #{index + 1}
          </label>
          <AutocompleteInput
            value={row.query}
            onChange={(v) => onUpdate(row.id, { query: v })}
            onSelect={(picked) => onCommitQuery(row.id, picked)}
            options={knownModels}
            placeholder="Modele imprimante (ex: HP M402, Brother HL-L3270, Kyocera FS-1320)"
            ariaLabel={`Modele imprimante #${index + 1}`}
          />
        </div>
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Retirer imprimante #${index + 1}`}
          title="Retirer cette imprimante"
          style={{
            height: 36,
            width: 36,
            background: 'transparent',
            border: '1px solid var(--border)',
            borderRadius: radius.md,
            color: 'var(--text-muted)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: cssTransition('instant', ['background', 'color', 'border-color']),
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--danger-subtle)';
            e.currentTarget.style.color = 'var(--danger)';
            e.currentTarget.style.borderColor = 'var(--danger)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = 'var(--text-muted)';
            e.currentTarget.style.borderColor = 'var(--border)';
          }}
        >
          <X size={16} />
        </button>
      </div>

      {showCard && <ImprimanteResults row={row} onToggleRef={(refId) => onToggleRef(row.id, refId)} />}
      {showUncovered && <UncoveredAlert brand={row.uncoveredBrand} />}
      {showUnknown && <UnknownAlert query={row.query} />}

      <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
        <label
          htmlFor={`notes-${row.id}`}
          style={{
            ...typoStyle('body-sm'),
            color: 'var(--text-muted)',
            fontWeight: 500,
          }}
        >
          Notes terrain
        </label>
        <input
          id={`notes-${row.id}`}
          type="text"
          value={row.notes}
          onChange={(e) => onUpdate(row.id, { notes: e.target.value })}
          placeholder="Localisation, nb pages/mois, etat cartouche actuelle..."
          style={inputBaseStyle}
        />
      </div>
    </div>
  );
}

// --- AuditSynthese -------------------------------------------

function AuditSynthese({ imprimantes }) {
  const nbImprimantes = imprimantes.filter((r) => r.query.trim()).length;
  const nbRefsTotal = imprimantes.reduce((sum, r) => sum + r.refsSelectionnees.length, 0);
  const nbUncovered = imprimantes.filter((r) => r.uncoveredBrand).length;

  if (nbImprimantes === 0) return null;

  return (
    <div
      style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: radius.md,
        padding: space.md,
        display: 'flex',
        gap: space.lg,
        alignItems: 'center',
        flexWrap: 'wrap',
        ...typoStyle('body-sm'),
      }}
    >
      <span>
        <strong style={{ color: 'var(--text)' }}>{nbImprimantes}</strong>
        <span style={{ color: 'var(--text-muted)' }}>
          {' '}imprimante{nbImprimantes > 1 ? 's' : ''} auditee{nbImprimantes > 1 ? 's' : ''}
        </span>
      </span>
      <span>
        <strong style={{ color: 'var(--accent)' }}>{nbRefsTotal}</strong>
        <span style={{ color: 'var(--text-muted)' }}>
          {' '}ref{nbRefsTotal > 1 ? 's' : ''} adressable{nbRefsTotal > 1 ? 's' : ''}
        </span>
      </span>
      {nbUncovered > 0 && (
        <span>
          <strong style={{ color: 'var(--danger)' }}>{nbUncovered}</strong>
          <span style={{ color: 'var(--text-muted)' }}>
            {' '}non couverte{nbUncovered > 1 ? 's' : ''}
          </span>
        </span>
      )}
    </div>
  );
}

// --- AuditActions (sticky bottom) ----------------------------

function AuditActions({ canSave, canGenerate, onCancel, onSave, onGenerate, isMobile }) {
  return (
    <div
      style={{
        position: 'sticky',
        bottom: isMobile ? `calc(56px + env(safe-area-inset-bottom, 0px))` : 0,
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: radius.md,
        padding: space.md,
        display: 'flex',
        gap: space.sm,
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
        boxShadow: 'var(--shadow-md)',
        zIndex: 10,
      }}
    >
      <Button variant="ghost" onClick={onCancel}>
        Annuler
      </Button>
      <Button variant="secondary" onClick={onSave} disabled={!canSave}>
        Sauvegarder l'audit
      </Button>
      <Button
        variant="primary"
        onClick={onGenerate}
        disabled={!canGenerate}
        iconRight={ArrowRight}
      >
        Generer devis
      </Button>
    </div>
  );
}

// --- AuditAccueil (vue initiale + delete) ---------------------

function AuditAccueil({ onStart, onResume }) {
  const { showToast } = useToast();
  const [recentAudits, setRecentAudits] = useState(() => loadRecent());
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  function loadRecent() {
    return [...listAudits()]
      .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))
      .slice(0, 5);
  }

  function handleDelete(id) {
    removeAudit(id);
    setRecentAudits(loadRecent());
    setDeleteConfirmId(null);
    showToast('Audit supprime', { kind: 'danger' });
  }

  const deletingAudit = deleteConfirmId
    ? recentAudits.find((a) => a.id === deleteConfirmId)
    : null;

  return (
    <section
      id="panel-audit"
      role="tabpanel"
      aria-labelledby="tab-audit"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: space.xl,
        maxWidth: 720,
        margin: '0 auto',
        width: '100%',
      }}
    >
      <header style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}>
        <h1 style={{ ...typoStyle('display-lg'), color: 'var(--text)', margin: 0 }}>
          Audit de parc
        </h1>
        <p style={{ ...typoStyle('body-md'), color: 'var(--text-muted)', margin: 0 }}>
          Identifie les imprimantes du client, matche les consommables compatibles G&G,
          prepare le chiffrage. Alerte hard si marque non couverte (Xerox/Ricoh/OKI).
        </p>
      </header>

      <div>
        <Button variant="primary" size="lg" iconLeft={Plus} onClick={onStart}>
          Nouvel audit
        </Button>
      </div>

      {recentAudits.length > 0 && (
        <section style={{ display: 'flex', flexDirection: 'column', gap: space.md }}>
          <h2
            style={{
              ...typoStyle('body-sm'),
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              fontWeight: 600,
              margin: 0,
            }}
          >
            Reprendre un audit recent
          </h2>
          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: space.xs,
            }}
          >
            {recentAudits.map((a) => (
              <li
                key={a.id}
                style={{
                  display: 'flex',
                  alignItems: 'stretch',
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border)',
                  borderRadius: radius.md,
                  overflow: 'hidden',
                  transition: cssTransition('instant', ['border-color']),
                }}
              >
                <button
                  type="button"
                  onClick={() => onResume(a.id)}
                  style={{
                    flex: 1,
                    background: 'transparent',
                    border: 'none',
                    padding: space.md,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: space.md,
                    textAlign: 'left',
                    transition: cssTransition('instant', ['background']),
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--bg-hover)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <FileText size={18} strokeWidth={1.75} color="var(--text-muted)" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        ...typoStyle('body-md'),
                        color: 'var(--text)',
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {a.clientName || '(sans nom)'}
                    </div>
                    <div style={{ ...typoStyle('body-sm'), color: 'var(--text-subtle)' }}>
                      Cree {formatRelativeDate(a.createdAt)}
                    </div>
                  </div>
                  <ArrowRight size={16} color="var(--text-subtle)" />
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteConfirmId(a.id)}
                  aria-label={`Supprimer l'audit ${a.clientName || '(sans nom)'}`}
                  title="Supprimer cet audit"
                  style={{
                    width: 48,
                    background: 'transparent',
                    border: 'none',
                    borderLeft: '1px solid var(--border)',
                    color: 'var(--text-subtle)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: cssTransition('instant', ['background', 'color']),
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--danger-subtle)';
                    e.currentTarget.style.color = 'var(--danger)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = 'var(--text-subtle)';
                  }}
                >
                  <Trash2 size={16} strokeWidth={1.75} />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Modal
        open={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
        title="Supprimer cet audit ?"
        size="sm"
        variant="danger"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteConfirmId(null)}>
              Annuler
            </Button>
            <Button variant="danger" onClick={() => handleDelete(deleteConfirmId)}>
              Supprimer definitivement
            </Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          Suppression definitive de l'audit
          {deletingAudit ? ` de ${deletingAudit.clientName || '(sans nom)'}` : ''}.
          Pas d'annulation possible. Le devis genere depuis cet audit, s'il existe,
          reste intact.
        </p>
      </Modal>
    </section>
  );
}

// --- AuditEditor (vue edition) -------------------------------

function AuditEditor({ initialAudit, onCancel, onSavedAndGenerate }) {
  const isMobile = useMediaQuery(BREAKPOINT_MOBILE);
  const { showToast } = useToast();
  const { setActiveId } = useNav();

  const [audit, setAudit] = useState(initialAudit);
  const [removeRowConfirmId, setRemoveRowConfirmId] = useState(null);
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);
  const initialSnapshot = useRef(JSON.stringify(initialAudit));

  const prospects = useMemo(() => getProspects(), []);
  const knownModels = useMemo(() => extractKnownModels(compatLaserData), []);

  const isDirty = useMemo(
    () => JSON.stringify(audit) !== initialSnapshot.current,
    [audit],
  );

  const canSave =
    audit.clientName.trim().length > 0 &&
    audit.imprimantes.some((r) => r.query.trim().length > 0);

  const totalRefs = audit.imprimantes.reduce((s, r) => s + r.refsSelectionnees.length, 0);
  const canGenerate = canSave && totalRefs > 0;

  function updateRow(rowId, patch) {
    setAudit((a) => ({
      ...a,
      imprimantes: a.imprimantes.map((r) => (r.id === rowId ? { ...r, ...patch } : r)),
    }));
  }

  function commitQuery(rowId, query) {
    const patch = computeRowFromQuery(query);
    updateRow(rowId, { query, ...patch });
  }

  function toggleRef(rowId, refId) {
    setAudit((a) => ({
      ...a,
      imprimantes: a.imprimantes.map((r) => {
        if (r.id !== rowId) return r;
        const set = new Set(r.refsSelectionnees);
        if (set.has(refId)) set.delete(refId);
        else set.add(refId);
        return { ...r, refsSelectionnees: Array.from(set) };
      }),
    }));
  }

  function addRow() {
    setAudit((a) => ({
      ...a,
      imprimantes: [...a.imprimantes, makeEmptyImprimanteRow()],
    }));
  }

  function requestRemoveRow(rowId) {
    const row = audit.imprimantes.find((r) => r.id === rowId);
    if (!row) return;
    if (row.refsSelectionnees.length > 0 || row.notes.trim().length > 0) {
      setRemoveRowConfirmId(rowId);
    } else {
      performRemoveRow(rowId);
    }
  }

  function performRemoveRow(rowId) {
    setAudit((a) => {
      const filtered = a.imprimantes.filter((r) => r.id !== rowId);
      return {
        ...a,
        imprimantes: filtered.length > 0 ? filtered : [makeEmptyImprimanteRow()],
      };
    });
    setRemoveRowConfirmId(null);
  }

  function handleSave() {
    if (!canSave) return;
    const id = audit.auditId || genAuditId();
    const toSave = {
      ...audit,
      id,
      auditId: id,
      createdAt: audit.createdAt || new Date().toISOString(),
    };
    saveAudit(toSave);
    initialSnapshot.current = JSON.stringify(toSave);
    setAudit(toSave);
    showToast('Audit sauvegardé', { kind: 'success' });
  }

  function handleGenerate() {
    if (!canGenerate) return;
    const id = audit.auditId || genAuditId();
    const toSave = {
      ...audit,
      id,
      auditId: id,
      createdAt: audit.createdAt || new Date().toISOString(),
    };
    saveAudit(toSave);
    setActiveAuditId(id);
    showToast('Audit sauvegardé. Preparation du devis...', { kind: 'success' });
    onSavedAndGenerate();
    setActiveId('chiffrage');
  }

  function handleCancel() {
    if (isDirty) {
      setExitConfirmOpen(true);
    } else {
      onCancel();
    }
  }

  return (
    <section
      id="panel-audit"
      role="tabpanel"
      aria-labelledby="tab-audit"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: space.lg,
        maxWidth: 960,
        margin: '0 auto',
        width: '100%',
      }}
    >
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: space.md,
          flexWrap: 'wrap',
        }}
      >
        <h1 style={{ ...typoStyle('display-lg'), color: 'var(--text)', margin: 0 }}>
          {audit.auditId ? 'Audit en cours' : 'Nouvel audit'}
        </h1>
        {isDirty && (
          <span style={{ ...typoStyle('body-sm'), color: 'var(--warning)' }}>
            • Modifications non sauvegardees
          </span>
        )}
      </header>

      <AuditClientBlock
        clientName={audit.clientName}
        prospectId={audit.prospectId}
        prospects={prospects}
        onChange={(patch) => setAudit((a) => ({ ...a, ...patch }))}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: space.lg }}>
        {audit.imprimantes.map((row, idx) => (
          <AuditImprimanteRow
            key={row.id}
            row={row}
            index={idx}
            knownModels={knownModels}
            onUpdate={updateRow}
            onCommitQuery={commitQuery}
            onToggleRef={toggleRef}
            onRemove={() => requestRemoveRow(row.id)}
          />
        ))}
        <Button variant="secondary" iconLeft={Plus} onClick={addRow}>
          Ajouter une imprimante
        </Button>
      </div>

      <AuditSynthese imprimantes={audit.imprimantes} />

      <AuditActions
        canSave={canSave}
        canGenerate={canGenerate}
        onCancel={handleCancel}
        onSave={handleSave}
        onGenerate={handleGenerate}
        isMobile={isMobile}
      />

      <Modal
        open={!!removeRowConfirmId}
        onClose={() => setRemoveRowConfirmId(null)}
        title="Retirer cette imprimante ?"
        size="sm"
        variant="danger"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRemoveRowConfirmId(null)}>
              Annuler
            </Button>
            <Button variant="danger" onClick={() => performRemoveRow(removeRowConfirmId)}>
              Retirer
            </Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          Cette imprimante a des refs selectionnees ou des notes saisies.
          Retrait definitif (pas d'annulation).
        </p>
      </Modal>

      <Modal
        open={exitConfirmOpen}
        onClose={() => setExitConfirmOpen(false)}
        title="Quitter sans sauvegarder ?"
        size="sm"
        variant="danger"
        footer={
          <>
            <Button variant="ghost" onClick={() => setExitConfirmOpen(false)}>
              Annuler
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                setExitConfirmOpen(false);
                onCancel();
              }}
            >
              Quitter quand meme
            </Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          L'audit n'est pas sauvegarde. Toutes les saisies seront perdues.
        </p>
      </Modal>
    </section>
  );
}

// --- Module 3 main -------------------------------------------

function Module3Audit() {
  const [editorAudit, setEditorAudit] = useState(null);

  // Mount : check prefill prospect set par Pipeline (Module 5 "Creer audit")
  useEffect(() => {
    const prospectId = getActiveProspectForAudit();
    if (!prospectId) return;
    clearActiveProspectForAudit();
    const prospects = getProspects();
    const prospect = prospects.find((p) => p.id === prospectId);
    if (!prospect) return;
    const draft = makeEmptyAuditDraft();
    draft.clientName = prospect.nom;
    draft.prospectId = prospect.id;
    setEditorAudit(draft);
  }, []);

  function startNew() {
    setEditorAudit(makeEmptyAuditDraft());
  }

  function resumeAudit(auditId) {
    const loaded = getAudit(auditId);
    if (!loaded) return;
    setEditorAudit({
      auditId: loaded.id,
      clientName: loaded.clientName || '',
      prospectId: loaded.prospectId || null,
      imprimantes: loaded.imprimantes?.length
        ? loaded.imprimantes
        : [makeEmptyImprimanteRow()],
      createdAt: loaded.createdAt || null,
    });
  }

  if (!editorAudit) {
    return <AuditAccueil onStart={startNew} onResume={resumeAudit} />;
  }
  return (
    <AuditEditor
      initialAudit={editorAudit}
      onCancel={() => setEditorAudit(null)}
      onSavedAndGenerate={() => setEditorAudit(null)}
    />
  );
}

// ============================================================
// MISSION 4 — Module 4 Chiffrage (piece maitresse)
// ============================================================
//
// Flux :
//   - Mount avec activeAuditId -> charge audit + convertit en devis brouillon
//     (clearActiveAuditId immediat, evite re-load)
//   - Mount sans activeAuditId -> ChiffrageAccueil (liste 5 devis recents
//     + bouton "Nouveau devis vierge")
//
// Devis persistant : statuts brouillon/envoye/accepte/refuse
//   - brouillon : editable, sauvegardable, exportable PDF si numero assigne
//   - envoye/accepte/refuse : READ-ONLY, seul changement de statut possible
//
// Numero devis : assigne au 1er saveDevis explicite (DEV-2026-NNNN).
// Boutons PDF disabled tant que numero null.
//
// Seuils CGV :
//   - < 250 EUR : banner rouge, save OK, PDF/envoi DISABLED
//   - 250-299 : banner ambre, tout active, port 12 EUR
//   - >= 300 : banner vert FRANCO, tout active, port 0
//
// Comparateur Amazon : GLOBAL (1 prix saisi), auto-skip dans PDF si perte.
// Ajout ligne manuelle : modal autocomplete sur 588 refs catalogue complet.

// --- Helpers Module 4 ----------------------------------------

/**
 * Cherche une ref par ref_interne :
 *   1. dans compatLaserData.compatibilites (laser, 88 refs)
 *   2. fallback dans catalogueComplet.references (588 refs FG)
 * @returns {object | null}
 */
function findRefInCatalogue(refId) {
  if (!refId) return null;
  const laser = (compatLaserData.compatibilites || []).find((r) => r.ref_interne === refId);
  if (laser) return laser;
  const full = (catalogueComplet.references || []).find((r) => r.ref_interne === refId);
  if (full) return full;
  return null;
}

/**
 * Convertit un audit en lignes devis (auto-merge sur ref_interne).
 * Skip silencieusement les refs introuvables.
 */
function convertAuditToLignes(audit) {
  const refMap = new Map();
  for (const imp of (audit.imprimantes || [])) {
    for (const refId of (imp.refsSelectionnees || [])) {
      const refData = findRefInCatalogue(refId);
      if (!refData) continue;
      if (refMap.has(refId)) {
        refMap.get(refId).quantite += 1;
      } else {
        refMap.set(refId, {
          id: genAuditId(),
          ref_interne: refId,
          designation: refData.designation || '',
          type: refData.type || 'Autre',
          couleur: refData.couleur || 'N/A',
          pa_ht: Number(refData.pa_ht) || 0,
          quantite: 1,
        });
      }
    }
  }
  return Array.from(refMap.values());
}

function makeEmptyDevisDraft() {
  return {
    id: null,
    numero: null,
    statut: 'brouillon',
    clientName: '',
    clientAdresse: '',
    clientContact: '',
    prospectId: null,
    auditId: null,
    lignes: [],
    sousTotal: 0,
    port: 0,
    total: 0,
    prixAmazon: null,
    economie: 0,
    pourcentage: 0,
    perteVsAmazon: false,
    notes: '',
    validite: 30,
    createdAt: null,
    updatedAt: null,
    dateEnvoi: null,
    dateAcceptation: null,
    dateRefus: null,
  };
}

/**
 * Construit un devis brouillon depuis un audit (mount via setActiveAuditId).
 */
function buildDevisFromAudit(audit) {
  const lignes = convertAuditToLignes(audit);
  return {
    ...makeEmptyDevisDraft(),
    clientName: audit.clientName || '',
    prospectId: audit.prospectId || null,
    auditId: audit.id || audit.auditId || null,
    lignes,
  };
}

/**
 * Slugify pour filename PDF : "Mairie Lyon 1er" -> "mairie-lyon-1er"
 */
function slugify(s) {
  return (s || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

// --- DevisStatutChip -----------------------------------------

const DEVIS_STATUT_LABELS = {
  brouillon: 'Brouillon',
  envoye: 'Envoyé',
  accepte: 'Accepté',
  refuse: 'Refusé',
};

const DEVIS_STATUT_STYLES = {
  brouillon: { bg: 'var(--bg-subtle)', fg: 'var(--text-muted)', border: '1px solid var(--border)' },
  envoye:    { bg: 'var(--warning-subtle)', fg: 'var(--warning)', border: '1px solid transparent' },
  accepte:   { bg: 'var(--accent-subtle)', fg: 'var(--accent-strong)', border: '1px solid var(--accent)' },
  refuse:    { bg: 'var(--danger-subtle)', fg: 'var(--danger)', border: '1px solid transparent' },
};

function DevisStatutChip({ statut }) {
  const styles = DEVIS_STATUT_STYLES[statut] || DEVIS_STATUT_STYLES.brouillon;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: `2px ${space.sm}`,
        background: styles.bg,
        color: styles.fg,
        border: styles.border,
        borderRadius: radius.full,
        fontSize: '11px',
        fontWeight: 600,
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
      }}
    >
      {DEVIS_STATUT_LABELS[statut] || statut}
    </span>
  );
}

// --- QuantiteInput -------------------------------------------

function QuantiteInput({ value, onChange, onRequestDelete, disabled }) {
  function handleInputChange(e) {
    const n = parseInt(e.target.value, 10);
    if (isNaN(n) || n < 1) return; // pas en dessous de 1
    onChange(n);
  }
  function handleMinus() {
    if (value <= 1) {
      onRequestDelete();
      return;
    }
    onChange(value - 1);
  }
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: space.xs }}>
      <button
        type="button"
        onClick={handleMinus}
        disabled={disabled}
        aria-label="Diminuer quantite"
        style={{
          width: 28,
          height: 28,
          border: '1px solid var(--border-strong)',
          background: 'var(--bg)',
          color: disabled ? 'var(--text-subtle)' : 'var(--text-muted)',
          borderRadius: radius.sm,
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Minus size={14} />
      </button>
      <input
        type="number"
        min={1}
        value={value}
        onChange={handleInputChange}
        disabled={disabled}
        aria-label="Quantite"
        style={{
          width: 48,
          height: 28,
          padding: `0 ${space.xs}`,
          border: '1px solid var(--border-strong)',
          borderRadius: radius.sm,
          background: 'var(--bg)',
          color: 'var(--text)',
          fontFamily: typoStyle('mono-md').fontFamily,
          fontSize: '14px',
          fontVariantNumeric: 'tabular-nums',
          textAlign: 'center',
          MozAppearance: 'textfield',
        }}
      />
      <button
        type="button"
        onClick={() => onChange(value + 1)}
        disabled={disabled}
        aria-label="Augmenter quantite"
        style={{
          width: 28,
          height: 28,
          border: '1px solid var(--border-strong)',
          background: 'var(--bg)',
          color: disabled ? 'var(--text-subtle)' : 'var(--text-muted)',
          borderRadius: radius.sm,
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Plus size={14} />
      </button>
    </div>
  );
}

// --- Comparateur Inkya inline (badge prix client final concurrent) ----

// Index Inkya memoise module-level : reconstruit seulement si le catalogue change.
let _inkyaIdxCache = { count: -1, idx: null };
function getInkyaIndexCached() {
  const meta = getInkyaMeta();
  if (meta.count !== _inkyaIdxCache.count) {
    _inkyaIdxCache = { count: meta.count, idx: buildInkyaIndex(getInkyaCatalogue()) };
  }
  return _inkyaIdxCache.idx;
}

/**
 * Petit indice prix Inkya sous une ligne devis. Affiche le prix client final
 * TTC du concurrent si la ref est matchee. Argument de vente.
 */
function InkyaLineHint({ ref_interne, ean }) {
  const hit = useMemo(() => {
    const idx = getInkyaIndexCached();
    if (!idx) return null;
    return findInkyaMatch({ ref_interne, ean }, idx);
  }, [ref_interne, ean]);
  if (!hit || !hit.match || !hit.match.prix_ttc) return null;
  return (
    <div
      title={`Inkya ref ${hit.match.ref_inkya} (match ${hit.method})`}
      style={{ ...typoStyle('body-sm'), color: 'var(--text-subtle)', display: 'flex', alignItems: 'center', gap: 4 }}
    >
      <Search size={11} strokeWidth={1.75} />
      Inkya client final : <strong style={{ ...typoStyle('mono-md'), color: 'var(--text-muted)' }}>
        {formatEUR(hit.match.prix_ttc, { withHT: false })} TTC
      </strong>
    </div>
  );
}

// --- DevisLigneRow (desktop) / DevisLigneCard (mobile) -------

function DevisLigneRow({ ligne, onUpdateQte, onRequestRemove, disabled }) {
  const total = (Number(ligne.pa_ht) || 0) * (Number(ligne.quantite) || 0);
  const label = ligne.couleur && ligne.couleur !== 'N/A' ? `${ligne.type} ${ligne.couleur}` : (ligne.type || '');
  return (
    <tr style={{ borderBottom: '1px solid var(--border)' }}>
      <td style={{ padding: space.sm, ...typoStyle('mono-md'), color: 'var(--text)', whiteSpace: 'nowrap' }}>
        {ligne.ref_interne}
      </td>
      <td style={{ padding: space.sm }}>
        <div style={{ ...typoStyle('body-md'), color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320 }} title={ligne.designation}>
          {ligne.designation || label || ligne.ref_interne}
        </div>
        {label && (
          <div style={{ ...typoStyle('body-sm'), color: 'var(--text-subtle)' }}>{label}</div>
        )}
        <InkyaLineHint ref_interne={ligne.ref_interne} ean={ligne.ean} />
      </td>
      <td style={{ padding: space.sm }}>
        <QuantiteInput
          value={ligne.quantite}
          onChange={(n) => onUpdateQte(ligne.id, n)}
          onRequestDelete={() => onRequestRemove(ligne.id)}
          disabled={disabled}
        />
      </td>
      <td style={{ padding: space.sm, textAlign: 'right', ...typoStyle('mono-md'), color: 'var(--text)' }}>
        {formatEUR(ligne.pa_ht, { withHT: false })}
      </td>
      <td style={{ padding: space.sm, textAlign: 'right', ...typoStyle('mono-md'), color: 'var(--text)', fontWeight: 600 }}>
        {formatEUR(total, { withHT: false })}
      </td>
      <td style={{ padding: space.sm, width: 36 }}>
        <button
          type="button"
          onClick={() => onRequestRemove(ligne.id)}
          disabled={disabled}
          aria-label={`Retirer ligne ${ligne.ref_interne}`}
          title="Retirer cette ligne"
          style={{
            width: 28,
            height: 28,
            background: 'transparent',
            border: 'none',
            color: 'var(--text-subtle)',
            cursor: disabled ? 'not-allowed' : 'pointer',
            borderRadius: radius.sm,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: cssTransition('instant', ['color', 'background']),
          }}
          onMouseEnter={(e) => { if (!disabled) { e.currentTarget.style.color = 'var(--danger)'; e.currentTarget.style.background = 'var(--danger-subtle)'; } }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-subtle)'; e.currentTarget.style.background = 'transparent'; }}
        >
          <X size={14} />
        </button>
      </td>
    </tr>
  );
}

function DevisLigneCard({ ligne, onUpdateQte, onRequestRemove, disabled }) {
  const total = (Number(ligne.pa_ht) || 0) * (Number(ligne.quantite) || 0);
  const label = ligne.couleur && ligne.couleur !== 'N/A' ? `${ligne.type} ${ligne.couleur}` : (ligne.type || '');
  return (
    <div
      style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: radius.md,
        padding: space.md,
        display: 'flex',
        flexDirection: 'column',
        gap: space.sm,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: space.sm }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...typoStyle('mono-md'), color: 'var(--text)' }}>{ligne.ref_interne}</div>
          <div style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={ligne.designation}>
            {label}{label && ligne.designation ? ' · ' : ''}{ligne.designation}
          </div>
          <InkyaLineHint ref_interne={ligne.ref_interne} ean={ligne.ean} />
        </div>
        <button
          type="button"
          onClick={() => onRequestRemove(ligne.id)}
          disabled={disabled}
          aria-label={`Retirer ligne ${ligne.ref_interne}`}
          style={{
            width: 28,
            height: 28,
            background: 'transparent',
            border: 'none',
            color: 'var(--text-subtle)',
            cursor: disabled ? 'not-allowed' : 'pointer',
            borderRadius: radius.sm,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <X size={16} />
        </button>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: space.sm }}>
        <QuantiteInput
          value={ligne.quantite}
          onChange={(n) => onUpdateQte(ligne.id, n)}
          onRequestDelete={() => onRequestRemove(ligne.id)}
          disabled={disabled}
        />
        <div style={{ textAlign: 'right' }}>
          <div style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
            {formatEUR(ligne.pa_ht, { withHT: false })} / unite
          </div>
          <div style={{ ...typoStyle('mono-md'), color: 'var(--text)', fontWeight: 700 }}>
            {formatEUR(total, { withHT: false })}
          </div>
        </div>
      </div>
    </div>
  );
}

// --- CGVSeuilBanner ------------------------------------------

function CGVSeuilBanner({ sousTotal }) {
  if (sousTotal <= 0) return null;
  if (sousTotal < CGV.MIN_FACTURATION_HT) {
    const manque = CGV.MIN_FACTURATION_HT - sousTotal;
    return (
      <div
        role="alert"
        style={{
          background: 'var(--danger-subtle)',
          border: '1px solid var(--danger)',
          borderRadius: radius.md,
          padding: space.md,
          display: 'flex',
          alignItems: 'center',
          gap: space.md,
          color: 'var(--danger)',
          ...typoStyle('body-md'),
        }}
      >
        <AlertTriangle size={18} strokeWidth={2} />
        <strong>Sous le minimum de facturation.</strong>
        <span style={{ color: 'var(--text-muted)' }}>
          Manque {formatEUR(manque, { withHT: false })} pour atteindre {formatEUR(CGV.MIN_FACTURATION_HT, { withHT: false })} HT. PDF et envoi desactives.
        </span>
      </div>
    );
  }
  if (sousTotal < CGV.FRANCO_HT) {
    const manque = CGV.FRANCO_HT - sousTotal;
    return (
      <div
        style={{
          background: 'var(--warning-subtle)',
          border: '1px solid var(--warning)',
          borderRadius: radius.md,
          padding: space.md,
          display: 'flex',
          alignItems: 'center',
          gap: space.md,
          color: 'var(--warning)',
          ...typoStyle('body-md'),
        }}
      >
        <AlertTriangle size={18} strokeWidth={2} />
        <strong>Franco non atteint.</strong>
        <span style={{ color: 'var(--text-muted)' }}>
          Ajouter {formatEUR(manque, { withHT: false })} pour economiser {formatEUR(CGV.PORT_HT, { withHT: false })} de port.
        </span>
      </div>
    );
  }
  return (
    <div
      style={{
        background: 'var(--accent-subtle)',
        border: '1px solid var(--accent)',
        borderRadius: radius.md,
        padding: space.md,
        display: 'flex',
        alignItems: 'center',
        gap: space.md,
        color: 'var(--accent-strong)',
        ...typoStyle('body-md'),
      }}
    >
      <CheckCircle2 size={18} strokeWidth={2} />
      <strong>Franco de port atteint.</strong>
      <span style={{ color: 'var(--text-muted)' }}>Pas de frais de port.</span>
    </div>
  );
}

// --- ComparateurAmazonBlock ----------------------------------

function ComparateurAmazonBlock({ total, prixAmazon, onChange, disabled }) {
  const compare = useMemo(() => calcEconomieClient(total, prixAmazon || 0), [total, prixAmazon]);
  // Heuristique : si prixAmazon < 30 % du total panier, probablement saisie unitaire par erreur.
  const looksUnitary = prixAmazon && total > 0 && prixAmazon > 0 && prixAmazon < total * 0.3;
  return (
    <div
      style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: radius.md,
        padding: space.lg,
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: space.lg,
        alignItems: 'center',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
        <label
          htmlFor="prixAmazon"
          style={{
            ...typoStyle('body-sm'),
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            fontWeight: 600,
          }}
        >
          Prix Amazon Business HT (total panier estimé)
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: space.sm }}>
          <input
            id="prixAmazon"
            type="number"
            min={0}
            step="0.01"
            value={prixAmazon || ''}
            onChange={(e) => {
              const v = e.target.value === '' ? null : Number(e.target.value);
              onChange(v);
            }}
            disabled={disabled}
            placeholder="Total équivalent panier sur Amazon Business HT"
            style={{ ...inputBaseStyle, maxWidth: 280 }}
          />
          <span style={{ ...typoStyle('body-sm'), color: 'var(--text-subtle)' }}>EUR HT</span>
        </div>
        {looksUnitary && (
          <div style={{ ...typoStyle('body-sm'), color: 'var(--warning)', display: 'flex', alignItems: 'center', gap: space.xs }}>
            <AlertTriangle size={14} strokeWidth={2} /> Ce prix semble être unitaire. Entrez le total équivalent panier ({formatEUR(total, { withHT: false })} chez nous).
          </div>
        )}
      </div>
      <div style={{ textAlign: 'right' }}>
        {prixAmazon && prixAmazon > 0 ? (
          compare.perteVsAmazon ? (
            <div>
              <div style={{ ...typoStyle('body-sm'), color: 'var(--danger)', fontWeight: 600 }}>
                Nos prix dépassent Amazon sur ce panier
              </div>
              <div style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
                Ne pas afficher dans le devis.
              </div>
            </div>
          ) : (
            <div>
              <div style={{ ...typoStyle('mono-lg'), color: 'var(--accent)', fontWeight: 700 }}>
                {formatEUR(compare.economie, { withHT: false })}
              </div>
              <div style={{ ...typoStyle('body-sm'), color: 'var(--accent-strong)' }}>
                Économie {formatPct(compare.pourcentage)}
              </div>
            </div>
          )
        ) : (
          <span style={{ ...typoStyle('body-sm'), color: 'var(--text-subtle)' }}>
            Saisis le total panier équivalent pour comparer
          </span>
        )}
      </div>
    </div>
  );
}

// --- TotauxBlock ---------------------------------------------

function TotauxBlock({ sousTotal, port, total, atteintFranco }) {
  return (
    <div
      style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: radius.md,
        padding: space.lg,
        display: 'flex',
        flexDirection: 'column',
        gap: space.xs,
        alignSelf: 'flex-end',
        minWidth: 280,
        marginLeft: 'auto',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', ...typoStyle('body-md'), color: 'var(--text-muted)' }}>
        <span>Sous-total HT</span>
        <span style={{ ...typoStyle('mono-md'), color: 'var(--text)' }}>
          {formatEUR(sousTotal, { withHT: false })}
        </span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', ...typoStyle('body-md'), color: 'var(--text-muted)' }}>
        <span>Port HT {atteintFranco ? '(FRANCO)' : ''}</span>
        <span style={{ ...typoStyle('mono-md'), color: atteintFranco ? 'var(--accent)' : 'var(--text)' }}>
          {atteintFranco ? formatEUR(0, { withHT: false }) : formatEUR(port, { withHT: false })}
        </span>
      </div>
      <div style={{ height: 1, background: 'var(--border-strong)', margin: `${space.xs} 0` }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ ...typoStyle('body-md'), color: 'var(--text)', fontWeight: 700 }}>Total HT</span>
        <span style={{ ...typoStyle('mono-lg'), color: 'var(--accent)', fontWeight: 700 }}>
          {formatEUR(total, { withHT: false })}
        </span>
      </div>
    </div>
  );
}

// --- DevisSourceAuditBadge -----------------------------------

function DevisSourceAuditBadge({ auditId }) {
  const audit = useMemo(() => (auditId ? getAudit(auditId) : null), [auditId]);
  const { setActiveId } = useNav();
  if (!audit) return null;
  return (
    <div
      style={{
        background: 'var(--accent-subtle)',
        border: '1px solid var(--accent)',
        borderRadius: radius.md,
        padding: space.md,
        display: 'flex',
        alignItems: 'center',
        gap: space.md,
        flexWrap: 'wrap',
      }}
    >
      <FileText size={16} color="var(--accent-strong)" />
      <span style={{ ...typoStyle('body-sm'), color: 'var(--accent-strong)', fontWeight: 600 }}>
        Genere depuis audit
      </span>
      <span style={{ ...typoStyle('body-sm'), color: 'var(--text)' }}>
        {audit.clientName || '(sans nom)'} · {(audit.imprimantes || []).length} imprimantes
      </span>
      <div style={{ flex: 1 }} />
      <Button
        variant="ghost"
        size="sm"
        iconLeft={ArrowLeft}
        onClick={() => {
          setActiveAuditId(auditId);
          setActiveId('audit');
        }}
      >
        Retour audit
      </Button>
    </div>
  );
}

// --- DevisClientBlock ----------------------------------------

function DevisClientBlock({ devis, prospects, onChange, disabled }) {
  const prospectNames = useMemo(() => prospects.map((p) => p.nom), [prospects]);
  function handleSelect(picked) {
    const matched = prospects.find((p) => p.nom === picked);
    onChange({ clientName: picked, prospectId: matched?.id || null });
  }
  return (
    <div
      style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: radius.lg,
        padding: space.lg,
        display: 'flex',
        flexDirection: 'column',
        gap: space.md,
      }}
    >
      <label
        style={{
          ...typoStyle('body-sm'),
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: space.xs,
        }}
      >
        <Building2 size={14} strokeWidth={1.75} />
        Client
      </label>
      {disabled ? (
        <div style={{ ...typoStyle('body-md'), color: 'var(--text)' }}>{devis.clientName || '(sans nom)'}</div>
      ) : (
        <AutocompleteInput
          value={devis.clientName}
          onChange={(v) => onChange({ clientName: v, prospectId: null })}
          onSelect={handleSelect}
          options={prospectNames}
          placeholder="Nom du client"
          ariaLabel="Nom du client"
        />
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: space.md }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
          <label htmlFor="clientAdresse" style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', fontWeight: 500 }}>
            Adresse (optionnel)
          </label>
          <input
            id="clientAdresse"
            type="text"
            value={devis.clientAdresse}
            onChange={(e) => onChange({ clientAdresse: e.target.value })}
            disabled={disabled}
            placeholder="Adresse postale"
            style={inputBaseStyle}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
          <label htmlFor="clientContact" style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', fontWeight: 500 }}>
            Contact (optionnel)
          </label>
          <input
            id="clientContact"
            type="text"
            value={devis.clientContact}
            onChange={(e) => onChange({ clientContact: e.target.value })}
            disabled={disabled}
            placeholder="Tel, mail, nom personne"
            style={inputBaseStyle}
          />
        </div>
      </div>
    </div>
  );
}

// --- AjouterLigneModal (autocomplete 588 refs) ---------------

function AjouterLigneModal({ open, onClose, onAdd }) {
  const [query, setQuery] = useState('');
  const [highlightIdx, setHighlightIdx] = useState(0);
  const debouncedQuery = useDebouncedValue(query, 150);

  // Reset au open
  useEffect(() => {
    if (open) {
      setQuery('');
      setHighlightIdx(0);
    }
  }, [open]);

  const suggestions = useMemo(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) return [];
    const q = debouncedQuery.toLowerCase();
    const out = [];
    for (const ref of (catalogueComplet.references || [])) {
      const haystack = `${ref.ref_interne} ${ref.designation || ''}`.toLowerCase();
      if (haystack.includes(q)) {
        out.push(ref);
        if (out.length >= 20) break;
      }
    }
    return out;
  }, [debouncedQuery]);

  function handleSelect(ref) {
    const laser = (compatLaserData.compatibilites || []).find((r) => r.ref_interne === ref.ref_interne);
    const full = laser || ref;
    onAdd({
      id: genAuditId(),
      ref_interne: full.ref_interne,
      designation: full.designation || '',
      type: full.type || 'Autre',
      couleur: full.couleur || 'N/A',
      pa_ht: Number(full.pa_ht) || 0,
      quantite: 1,
    });
    onClose();
  }

  function handleKey(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && suggestions[highlightIdx]) {
      e.preventDefault();
      handleSelect(suggestions[highlightIdx]);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Ajouter une ref du catalogue"
      size="lg"
      footer={
        <Button variant="ghost" onClick={onClose}>Fermer</Button>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: space.md, minHeight: 360 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: space.sm }}>
          <Search size={16} color="var(--text-muted)" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Cherche ref interne, designation (ex: GG_HL, ramette, cartouche)..."
            autoFocus
            style={inputBaseStyle}
          />
        </div>
        {query.length < 2 ? (
          <p style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
            Tape au moins 2 caracteres. 588 references FG disponibles.
          </p>
        ) : suggestions.length === 0 ? (
          <p style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
            Aucune ref ne correspond a "{query}".
          </p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {suggestions.map((ref, idx) => {
              const isHighlight = idx === highlightIdx;
              return (
                <li key={ref.ref_interne}>
                  <button
                    type="button"
                    onClick={() => handleSelect(ref)}
                    onMouseEnter={() => setHighlightIdx(idx)}
                    style={{
                      width: '100%',
                      background: isHighlight ? 'var(--bg-hover)' : 'var(--bg)',
                      border: `1px solid ${isHighlight ? 'var(--border-strong)' : 'var(--border)'}`,
                      borderRadius: radius.sm,
                      padding: space.sm,
                      display: 'grid',
                      gridTemplateColumns: '110px 1fr auto',
                      alignItems: 'center',
                      gap: space.md,
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: cssTransition('instant', ['background', 'border-color']),
                    }}
                  >
                    <span style={{ ...typoStyle('mono-md'), color: 'var(--text)' }}>{ref.ref_interne}</span>
                    <span style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={ref.designation}>
                      {ref.designation}
                    </span>
                    <span style={{ ...typoStyle('mono-md'), color: 'var(--text)' }}>
                      {formatEUR(ref.pa_ht, { withHT: false })}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Modal>
  );
}

// --- DevisHeader ---------------------------------------------

function DevisHeader({ devis }) {
  return (
    <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: space.md, flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: space.md, flexWrap: 'wrap' }}>
        <h1 style={{ ...typoStyle('display-lg'), color: 'var(--text)', margin: 0 }}>
          {devis.numero ? `Devis ${devis.numero}` : 'Nouveau devis'}
        </h1>
        <DevisStatutChip statut={devis.statut} />
      </div>
      <div style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
        {devis.createdAt && <>Cree {formatRelativeDate(devis.createdAt)}</>}
        {devis.updatedAt && devis.updatedAt !== devis.createdAt && <> · Maj {formatRelativeDate(devis.updatedAt)}</>}
        {devis.validite && <> · Validite {devis.validite}j</>}
      </div>
    </header>
  );
}

// --- DevisActions (sticky) -----------------------------------

function DevisActions({
  devis,
  canSave,
  canExport,
  isMobile,
  isReadOnly,
  isLoadingPdf,
  onCancel,
  onSave,
  onPreview,
  onExport,
  onMarkSent,
  onMarkAccepted,
  onMarkRefused,
}) {
  return (
    <div
      style={{
        position: 'sticky',
        bottom: isMobile ? `calc(56px + env(safe-area-inset-bottom, 0px))` : 0,
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: radius.md,
        padding: space.md,
        display: 'flex',
        gap: space.sm,
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
        boxShadow: 'var(--shadow-md)',
        zIndex: 10,
      }}
    >
      <Button variant="ghost" onClick={onCancel}>Annuler</Button>
      {!isReadOnly && (
        <>
          <Button variant="secondary" onClick={onSave} disabled={!canSave}>Sauvegarder</Button>
          <Button
            variant="ghost"
            iconLeft={Eye}
            onClick={onPreview}
            disabled={!canExport || isLoadingPdf}
            title={!canExport ? 'Sauvegarder le brouillon avant export' : undefined}
          >
            Apercu
          </Button>
          <Button
            variant="secondary"
            iconLeft={Download}
            onClick={onExport}
            disabled={!canExport || isLoadingPdf}
            loading={isLoadingPdf}
            title={!canExport ? 'Sauvegarder le brouillon avant export' : undefined}
          >
            Exporter PDF
          </Button>
          <Button
            variant="primary"
            iconLeft={Send}
            onClick={onMarkSent}
            disabled={!canExport}
          >
            Marquer envoye
          </Button>
        </>
      )}
      {devis.statut === 'envoye' && (
        <>
          <Button variant="secondary" iconLeft={Eye} onClick={onPreview} disabled={isLoadingPdf}>
            Apercu
          </Button>
          <Button variant="secondary" iconLeft={Download} onClick={onExport} disabled={isLoadingPdf} loading={isLoadingPdf}>
            Re-exporter PDF
          </Button>
          <Button variant="primary" onClick={onMarkAccepted}>
            Marquer accepte
          </Button>
          <Button variant="danger" onClick={onMarkRefused}>
            Marquer refuse
          </Button>
        </>
      )}
      {(devis.statut === 'accepte' || devis.statut === 'refuse') && (
        <Button variant="secondary" iconLeft={Eye} onClick={onPreview} disabled={isLoadingPdf}>
          Apercu PDF
        </Button>
      )}
    </div>
  );
}

// --- ChiffrageAccueil ----------------------------------------

function ChiffrageAccueil({ onNew, onResume }) {
  const { showToast } = useToast();
  const [recentDevis, setRecentDevis] = useState(() => loadRecent());
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  function loadRecent() {
    return [...listDevis()]
      .sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''))
      .slice(0, 5);
  }

  function handleDelete(id) {
    removeDevis(id);
    setRecentDevis(loadRecent());
    setDeleteConfirmId(null);
    showToast('Devis supprime', { kind: 'danger' });
  }

  const deletingDevis = deleteConfirmId ? recentDevis.find((d) => d.id === deleteConfirmId) : null;

  return (
    <section
      id="panel-chiffrage"
      role="tabpanel"
      aria-labelledby="tab-chiffrage"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: space.xl,
        maxWidth: 720,
        margin: '0 auto',
        width: '100%',
      }}
    >
      <header style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}>
        <h1 style={{ ...typoStyle('display-lg'), color: 'var(--text)', margin: 0 }}>
          Chiffrage & Comparateur
        </h1>
        <p style={{ ...typoStyle('body-md'), color: 'var(--text-muted)', margin: 0 }}>
          Genere un devis depuis un audit (Module 3) ou cree un devis vierge.
          Verification seuils CGV automatique. Export PDF aux couleurs Espace Conso.
        </p>
      </header>

      <div>
        <Button variant="primary" size="lg" iconLeft={Plus} onClick={onNew}>
          Nouveau devis vierge
        </Button>
      </div>

      {recentDevis.length === 0 ? (
        <p style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
          Aucun devis. Pars d'un audit (Module 3) ou cree un devis vierge ci-dessus.
        </p>
      ) : (
        <section style={{ display: 'flex', flexDirection: 'column', gap: space.md }}>
          <h2 style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, margin: 0 }}>
            Devis recents
          </h2>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: space.xs }}>
            {recentDevis.map((d) => (
              <li
                key={d.id}
                style={{
                  display: 'flex',
                  alignItems: 'stretch',
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border)',
                  borderRadius: radius.md,
                  overflow: 'hidden',
                }}
              >
                <button
                  type="button"
                  onClick={() => onResume(d.id)}
                  style={{
                    flex: 1,
                    background: 'transparent',
                    border: 'none',
                    padding: space.md,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: space.md,
                    textAlign: 'left',
                    transition: cssTransition('instant', ['background']),
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <FileText size={18} strokeWidth={1.75} color="var(--text-muted)" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ ...typoStyle('body-md'), color: 'var(--text)', fontWeight: 500, display: 'flex', alignItems: 'center', gap: space.sm, flexWrap: 'wrap' }}>
                      <span>{d.numero || '(brouillon sans numero)'}</span>
                      <DevisStatutChip statut={d.statut} />
                    </div>
                    <div style={{ ...typoStyle('body-sm'), color: 'var(--text-subtle)' }}>
                      {d.clientName || '(sans nom)'} · {formatEUR(d.total || 0, { withHT: false })} · Maj {formatRelativeDate(d.updatedAt)}
                    </div>
                  </div>
                  <ArrowRight size={16} color="var(--text-subtle)" />
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteConfirmId(d.id)}
                  aria-label={`Supprimer devis ${d.numero || '(sans numero)'}`}
                  title="Supprimer ce devis"
                  style={{
                    width: 48,
                    background: 'transparent',
                    border: 'none',
                    borderLeft: '1px solid var(--border)',
                    color: 'var(--text-subtle)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: cssTransition('instant', ['background', 'color']),
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--danger-subtle)';
                    e.currentTarget.style.color = 'var(--danger)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = 'var(--text-subtle)';
                  }}
                >
                  <Trash2 size={16} strokeWidth={1.75} />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Modal
        open={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
        title="Supprimer ce devis ?"
        size="sm"
        variant="danger"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteConfirmId(null)}>Annuler</Button>
            <Button variant="danger" onClick={() => handleDelete(deleteConfirmId)}>Supprimer definitivement</Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          Suppression definitive du devis
          {deletingDevis ? ` ${deletingDevis.numero || ''} de ${deletingDevis.clientName || '(sans nom)'}` : ''}.
          Pas d'annulation possible.
        </p>
      </Modal>
    </section>
  );
}

// --- ChiffrageEditeur ----------------------------------------

function ChiffrageEditeur({ initialDevis, onCancel }) {
  const isMobile = useMediaQuery(BREAKPOINT_MOBILE);
  const { showToast } = useToast();

  const [devis, setDevis] = useState(initialDevis);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [removeRowConfirmId, setRemoveRowConfirmId] = useState(null);
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);
  const [markSentConfirmOpen, setMarkSentConfirmOpen] = useState(false);
  const [markAcceptedConfirmOpen, setMarkAcceptedConfirmOpen] = useState(false);
  const [markRefusedConfirmOpen, setMarkRefusedConfirmOpen] = useState(false);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const initialSnapshot = useRef(JSON.stringify(initialDevis));

  const prospects = useMemo(() => getProspects(), []);
  const isReadOnly = devis.statut !== 'brouillon';

  // Calcul totaux live (snapshot dans devis pour PDF + index)
  const totaux = useMemo(() => calcTotal(devis.lignes), [devis.lignes]);
  const compare = useMemo(
    () => calcEconomieClient(totaux.total, devis.prixAmazon || 0),
    [totaux.total, devis.prixAmazon],
  );

  // Sync totaux + compare dans devis state (pour save)
  useEffect(() => {
    setDevis((d) => ({
      ...d,
      sousTotal: totaux.sousTotal,
      port: totaux.port,
      total: totaux.total,
      economie: compare.economie,
      pourcentage: compare.pourcentage,
      perteVsAmazon: compare.perteVsAmazon,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totaux.sousTotal, totaux.port, totaux.total, compare.economie, compare.pourcentage, compare.perteVsAmazon]);

  const isDirty = useMemo(() => JSON.stringify(devis) !== initialSnapshot.current, [devis]);

  const canSave = devis.clientName.trim().length > 0 && devis.lignes.length > 0;
  const canExport = canSave && devis.numero !== null && totaux.atteintMin;

  function updateLigneQte(ligneId, n) {
    setDevis((d) => ({
      ...d,
      lignes: d.lignes.map((l) => (l.id === ligneId ? { ...l, quantite: n } : l)),
    }));
  }

  function requestRemoveLigne(ligneId) {
    setRemoveRowConfirmId(ligneId);
  }

  function performRemoveLigne(ligneId) {
    setDevis((d) => ({ ...d, lignes: d.lignes.filter((l) => l.id !== ligneId) }));
    setRemoveRowConfirmId(null);
  }

  function addLigne(ligne) {
    setDevis((d) => ({ ...d, lignes: [...d.lignes, ligne] }));
  }

  function handleSave() {
    if (!canSave) return;
    const saved = saveDevis(devis);
    if (!saved) {
      showToast('Erreur a la sauvegarde', { kind: 'danger' });
      return;
    }
    initialSnapshot.current = JSON.stringify(saved);
    setDevis(saved);
    showToast(`Devis ${saved.numero} sauvegarde`, { kind: 'success' });
  }

  async function handlePreview() {
    if (isLoadingPdf) return;
    setIsLoadingPdf(true);
    try {
      // Dynamic import pour code-split jspdf hors bundle critique
      const { generateDevisPDF, previewDevis } = await import('./lib/pdf.js');
      const blob = await generateDevisPDF(devis);
      previewDevis(blob);
    } catch (err) {
      console.error('[chiffrage] preview pdf:', err);
      showToast('Erreur a la generation du PDF', { kind: 'danger' });
    } finally {
      setIsLoadingPdf(false);
    }
  }

  async function handleExport() {
    if (isLoadingPdf || !canExport) return;
    setIsLoadingPdf(true);
    try {
      const { generateDevisPDF, downloadDevis } = await import('./lib/pdf.js');
      const blob = await generateDevisPDF(devis);
      const filename = `devis-${devis.numero || 'sans-numero'}-${slugify(devis.clientName)}.pdf`;
      downloadDevis(blob, filename);
      showToast('PDF telecharge', { kind: 'success' });
    } catch (err) {
      console.error('[chiffrage] export pdf:', err);
      showToast('Erreur a la generation du PDF', { kind: 'danger' });
    } finally {
      setIsLoadingPdf(false);
    }
  }

  function performMarkSent() {
    const toSave = { ...devis, statut: 'envoye', dateEnvoi: new Date().toISOString() };
    const saved = saveDevis(toSave);
    initialSnapshot.current = JSON.stringify(saved);
    setDevis(saved);
    setMarkSentConfirmOpen(false);
    showToast('Devis marque comme envoye', { kind: 'success' });
  }

  function performMarkAccepted() {
    const toSave = { ...devis, statut: 'accepte', dateAcceptation: new Date().toISOString() };
    const saved = saveDevis(toSave);
    initialSnapshot.current = JSON.stringify(saved);
    setDevis(saved);
    setMarkAcceptedConfirmOpen(false);
    // SPEC-04 Option B : devis accepte = commande reelle. Evenement d'achat
    // journalise automatiquement (idempotent par devisId), socle du reassort.
    if (saved.prospectId && Array.isArray(saved.lignes) && saved.lignes.length > 0) {
      const commande = addCommande({
        prospectId: saved.prospectId,
        date: saved.dateAcceptation,
        lignes: saved.lignes.map((l) => ({
          ref_interne: l.ref_interne,
          designation: l.designation,
          quantite: l.quantite,
          pa_ht: l.pa_ht,
        })),
        source: 'devis_accepte',
        devisId: saved.id,
        totalHT: saved.total ?? null,
      });
      if (commande) enrichCommandeRendements(commande);
    }
    showToast('Devis accepte. Bravo.', { kind: 'success' });
  }

  function performMarkRefused() {
    const toSave = { ...devis, statut: 'refuse', dateRefus: new Date().toISOString() };
    const saved = saveDevis(toSave);
    initialSnapshot.current = JSON.stringify(saved);
    setDevis(saved);
    setMarkRefusedConfirmOpen(false);
    showToast('Devis marque comme refuse', { kind: 'danger' });
  }

  function handleCancel() {
    if (isDirty) setExitConfirmOpen(true);
    else onCancel();
  }

  return (
    <section
      id="panel-chiffrage"
      role="tabpanel"
      aria-labelledby="tab-chiffrage"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: space.lg,
        maxWidth: 1100,
        margin: '0 auto',
        width: '100%',
      }}
    >
      <DevisHeader devis={devis} />

      {devis.auditId && <DevisSourceAuditBadge auditId={devis.auditId} />}

      <DevisClientBlock
        devis={devis}
        prospects={prospects}
        onChange={(patch) => setDevis((d) => ({ ...d, ...patch }))}
        disabled={isReadOnly}
      />

      {/* Lignes : tableau desktop / cards mobile */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}>
        {devis.lignes.length === 0 ? (
          <div
            style={{
              background: 'var(--bg-elevated)',
              border: '1px dashed var(--border-strong)',
              borderRadius: radius.md,
              padding: space.xl,
              textAlign: 'center',
              color: 'var(--text-muted)',
              ...typoStyle('body-md'),
            }}
          >
            Aucune ligne. Ajoute une ref catalogue.
          </div>
        ) : isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}>
            {devis.lignes.map((ligne) => (
              <DevisLigneCard
                key={ligne.id}
                ligne={ligne}
                onUpdateQte={updateLigneQte}
                onRequestRemove={requestRemoveLigne}
                disabled={isReadOnly}
              />
            ))}
          </div>
        ) : (
          <div
            style={{
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              borderRadius: radius.md,
              overflow: 'hidden',
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border-strong)' }}>
                  <th style={{ padding: space.sm, textAlign: 'left', ...typoStyle('body-sm'), color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>Ref</th>
                  <th style={{ padding: space.sm, textAlign: 'left', ...typoStyle('body-sm'), color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>Designation</th>
                  <th style={{ padding: space.sm, textAlign: 'left', ...typoStyle('body-sm'), color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>Qte</th>
                  <th style={{ padding: space.sm, textAlign: 'right', ...typoStyle('body-sm'), color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>PA HT</th>
                  <th style={{ padding: space.sm, textAlign: 'right', ...typoStyle('body-sm'), color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>Total</th>
                  <th style={{ width: 36 }} />
                </tr>
              </thead>
              <tbody>
                {devis.lignes.map((ligne) => (
                  <DevisLigneRow
                    key={ligne.id}
                    ligne={ligne}
                    onUpdateQte={updateLigneQte}
                    onRequestRemove={requestRemoveLigne}
                    disabled={isReadOnly}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!isReadOnly && (
          <Button variant="secondary" iconLeft={Plus} onClick={() => setAddModalOpen(true)}>
            Ajouter une ref catalogue
          </Button>
        )}
      </div>

      <CGVSeuilBanner sousTotal={totaux.sousTotal} />

      <ComparateurAmazonBlock
        total={totaux.total}
        prixAmazon={devis.prixAmazon}
        onChange={(v) => setDevis((d) => ({ ...d, prixAmazon: v }))}
        disabled={isReadOnly}
      />

      <TotauxBlock
        sousTotal={totaux.sousTotal}
        port={totaux.port}
        total={totaux.total}
        atteintFranco={totaux.atteintFranco}
      />

      <DevisActions
        devis={devis}
        canSave={canSave}
        canExport={canExport}
        isMobile={isMobile}
        isReadOnly={isReadOnly}
        isLoadingPdf={isLoadingPdf}
        onCancel={handleCancel}
        onSave={handleSave}
        onPreview={handlePreview}
        onExport={handleExport}
        onMarkSent={() => setMarkSentConfirmOpen(true)}
        onMarkAccepted={() => setMarkAcceptedConfirmOpen(true)}
        onMarkRefused={() => setMarkRefusedConfirmOpen(true)}
      />

      {/* Modales */}
      <AjouterLigneModal open={addModalOpen} onClose={() => setAddModalOpen(false)} onAdd={addLigne} />

      <Modal
        open={!!removeRowConfirmId}
        onClose={() => setRemoveRowConfirmId(null)}
        title="Retirer cette ligne ?"
        size="sm"
        variant="danger"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRemoveRowConfirmId(null)}>Annuler</Button>
            <Button variant="danger" onClick={() => performRemoveLigne(removeRowConfirmId)}>Retirer</Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>La ligne sera retiree du devis (action immediate).</p>
      </Modal>

      <Modal
        open={exitConfirmOpen}
        onClose={() => setExitConfirmOpen(false)}
        title="Quitter sans sauvegarder ?"
        size="sm"
        variant="danger"
        footer={
          <>
            <Button variant="ghost" onClick={() => setExitConfirmOpen(false)}>Annuler</Button>
            <Button variant="danger" onClick={() => { setExitConfirmOpen(false); onCancel(); }}>Quitter quand meme</Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>Le devis n'est pas sauvegarde. Modifications perdues.</p>
      </Modal>

      <Modal
        open={markSentConfirmOpen}
        onClose={() => setMarkSentConfirmOpen(false)}
        title="Marquer comme envoye ?"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setMarkSentConfirmOpen(false)}>Annuler</Button>
            <Button variant="primary" onClick={performMarkSent}>Oui, marquer comme envoye</Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          Confirmer que ce devis a bien ete transmis au client ? Cette action est definitive
          et verrouille les modifications. Pour modifier ensuite : delete + recreate.
        </p>
      </Modal>

      <Modal
        open={markAcceptedConfirmOpen}
        onClose={() => setMarkAcceptedConfirmOpen(false)}
        title="Marquer comme accepte ?"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setMarkAcceptedConfirmOpen(false)}>Annuler</Button>
            <Button variant="primary" onClick={performMarkAccepted}>Oui, accepte</Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          Le client a accepte le devis. Statut final (pas d'annulation possible).
        </p>
      </Modal>

      <Modal
        open={markRefusedConfirmOpen}
        onClose={() => setMarkRefusedConfirmOpen(false)}
        title="Marquer comme refuse ?"
        size="sm"
        variant="danger"
        footer={
          <>
            <Button variant="ghost" onClick={() => setMarkRefusedConfirmOpen(false)}>Annuler</Button>
            <Button variant="danger" onClick={performMarkRefused}>Oui, refuse</Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          Le client a refuse le devis. Statut final (pas d'annulation possible).
        </p>
      </Modal>
    </section>
  );
}

// --- Module 4 main -------------------------------------------

function Module4Chiffrage() {
  const [editorDevis, setEditorDevis] = useState(null);

  // Mount priorite :
  //   1. activeDevisId (Pipeline -> Voir devis) : ouvre devis existant
  //   2. activeAuditId (Audit -> Generer devis) : convertit audit en brouillon
  // Les 2 cles sont clear immediat (evite re-load au prochain mount).
  useEffect(() => {
    const devisId = getActiveDevisId();
    if (devisId) {
      clearActiveDevisId();
      const loaded = getDevis(devisId);
      if (loaded) {
        setEditorDevis(loaded);
        return;
      }
    }
    const auditId = getActiveAuditId();
    if (!auditId) return;
    clearActiveAuditId();
    const audit = getAudit(auditId);
    if (audit) {
      setEditorDevis(buildDevisFromAudit(audit));
    }
  }, []);

  function startNew() {
    setEditorDevis(makeEmptyDevisDraft());
  }

  function resumeDevis(devisId) {
    const loaded = getDevis(devisId);
    if (!loaded) return;
    setEditorDevis(loaded);
  }

  if (!editorDevis) {
    return <ChiffrageAccueil onNew={startNew} onResume={resumeDevis} />;
  }
  return <ChiffrageEditeur initialDevis={editorDevis} onCancel={() => setEditorDevis(null)} />;
}

// ============================================================
// MISSION 5 — Module 5 Pipeline prospects
// ============================================================
//
// Desktop : Kanban 5 colonnes (Repere / Audite / Devis envoye / Compte ouvert / Recurrent)
// Mobile : accordeon par statut (collapse/expand par section)
// Card prospect cliquable -> modal detail
// Detail expose : edit / change statut / supprimer / creer audit / voir devis / voir audit
//
// Reuse STATUT_LABELS, STATUT_STYLES, StatutChip (deja definis dans Module 1).
// Relance J+5 : si statut === 'devis_envoye' et dateDernierStatut + 5j <= now,
// badge "Relance prevue" affiche dans la card (jamais envoyee auto).

const STATUTS_ORDRE = ['repere', 'audite', 'devis_envoye', 'compte_ouvert', 'recurrent'];

function isRelanceDue(prospect) {
  if (prospect.statut !== 'devis_envoye') return false;
  if (!prospect.dateDernierStatut) return false;
  const d = new Date(prospect.dateDernierStatut);
  const due = new Date(d.getTime() + 5 * 24 * 60 * 60 * 1000);
  return due <= new Date();
}

function ProspectCard({ prospect, onClick }) {
  const relance = isRelanceDue(prospect);
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%',
        textAlign: 'left',
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: radius.md,
        padding: space.md,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: space.xs,
        transition: cssTransition('instant', ['border-color', 'background']),
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--border-strong)';
        e.currentTarget.style.background = 'var(--bg-hover)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--border)';
        e.currentTarget.style.background = 'var(--bg-elevated)';
      }}
    >
      <div style={{
        ...typoStyle('body-md'),
        color: 'var(--text)',
        fontWeight: 600,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {prospect.nom}
      </div>
      <div style={{ ...typoStyle('body-sm'), color: 'var(--text-subtle)' }}>
        {prospect.type === 'interne' ? 'GMS bureau' : 'B2B externe'}
        {prospect.caEstimeParcMensuel ? ` · ~${formatEUR(prospect.caEstimeParcMensuel, { withHT: false })}/m parc` : ''}
      </div>
      {prospect.dateDernierStatut && (
        <div style={{ ...typoStyle('body-sm'), color: 'var(--text-subtle)' }}>
          Maj {formatRelativeDate(prospect.dateDernierStatut)}
        </div>
      )}
      {relance && (
        <div style={{
          ...typoStyle('body-sm'),
          color: 'var(--warning)',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: space.xs,
        }}>
          <AlertTriangle size={14} strokeWidth={2} />
          Relance prevue (J+5 atteint)
        </div>
      )}
    </button>
  );
}

function ViewToggle({ value, onChange, options }) {
  return (
    <div
      role="tablist"
      aria-label="Mode d'affichage"
      style={{
        display: 'inline-flex',
        background: 'var(--bg-subtle)',
        border: '1px solid var(--border)',
        borderRadius: radius.md,
        padding: 2,
      }}
    >
      {options.map((opt) => {
        const Icon = opt.icon;
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(opt.value)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: space.xs,
              padding: `${space.xs} ${space.md}`,
              background: selected ? 'var(--bg-elevated)' : 'transparent',
              border: 'none',
              borderRadius: radius.sm,
              color: selected ? 'var(--text)' : 'var(--text-muted)',
              fontSize: 12,
              fontWeight: 600,
              fontFamily: 'inherit',
              cursor: 'pointer',
              boxShadow: selected ? 'var(--shadow-sm)' : 'none',
              transition: cssTransition('instant', ['background', 'color']),
            }}
          >
            {Icon && <Icon size={14} strokeWidth={1.75} />}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function PipelineListe({ prospects, onCardClick }) {
  const [sortKey, setSortKey] = useState('dateDernierStatut');
  const [sortDir, setSortDir] = useState('desc');

  function toggleSort(key) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  }

  const statutWeight = { repere: 0, audite: 1, devis_envoye: 2, compte_ouvert: 3, recurrent: 4 };

  const sorted = useMemo(() => {
    const arr = [...prospects];
    arr.sort((a, b) => {
      let av, bv;
      if (sortKey === 'statut') { av = statutWeight[a.statut] ?? 99; bv = statutWeight[b.statut] ?? 99; }
      else if (sortKey === 'nom') { av = String(a.nom).toLowerCase(); bv = String(b.nom).toLowerCase(); }
      else if (sortKey === 'caEstime') { av = Number(a.caEstimeParcMensuel || 0); bv = Number(b.caEstimeParcMensuel || 0); }
      else { av = a.dateDernierStatut || ''; bv = b.dateDernierStatut || ''; }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [prospects, sortKey, sortDir]);

  function SortHeader({ k, children, align = 'left' }) {
    const active = sortKey === k;
    return (
      <th
        onClick={() => toggleSort(k)}
        style={{
          padding: `${space.sm} ${space.md}`,
          textAlign: align,
          background: 'var(--bg-subtle)',
          borderBottom: '1px solid var(--border)',
          ...typoStyle('body-sm'),
          color: active ? 'var(--text)' : 'var(--text-muted)',
          fontWeight: 600,
          cursor: 'pointer',
          userSelect: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        {children} {active ? (sortDir === 'asc' ? '▲' : '▼') : ''}
      </th>
    );
  }

  return (
    <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: radius.lg, overflow: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <SortHeader k="nom">Nom</SortHeader>
            <SortHeader k="statut">Statut</SortHeader>
            <SortHeader k="caEstime" align="right">CA parc/mois</SortHeader>
            <SortHeader k="dateDernierStatut">MAJ</SortHeader>
            <th style={{ padding: `${space.sm} ${space.md}`, background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }} />
          </tr>
        </thead>
        <tbody>
          {sorted.map((p) => (
            <tr
              key={p.id}
              onClick={() => onCardClick(p.id)}
              style={{ cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <td style={{ padding: `${space.sm} ${space.md}`, ...typoStyle('body-md'), color: 'var(--text)', fontWeight: 600 }}>
                {p.nom}
                <div style={{ ...typoStyle('body-sm'), color: 'var(--text-subtle)', fontWeight: 400 }}>
                  {p.type === 'interne' ? 'GMS bureau' : 'B2B externe'}
                </div>
              </td>
              <td style={{ padding: `${space.sm} ${space.md}` }}>
                <StatutChip statut={p.statut} />
              </td>
              <td style={{ padding: `${space.sm} ${space.md}`, ...typoStyle('mono-md'), color: 'var(--text)', textAlign: 'right' }}>
                {p.caEstimeParcMensuel ? formatEUR(p.caEstimeParcMensuel, { withHT: false }) : '—'}
              </td>
              <td style={{ padding: `${space.sm} ${space.md}`, ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
                {p.dateDernierStatut ? formatRelativeDate(p.dateDernierStatut) : '—'}
              </td>
              <td style={{ padding: `${space.sm} ${space.md}`, textAlign: 'right', color: 'var(--text-subtle)' }}>
                <ArrowRight size={14} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PipelineKanbanColumn({ statut, prospects, onCardClick }) {
  return (
    <div style={{
      flex: '1 1 220px',
      minWidth: 220,
      display: 'flex',
      flexDirection: 'column',
      gap: space.sm,
    }}>
      <header style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `${space.sm} ${space.md}`,
        background: 'var(--bg-subtle)',
        border: '1px solid var(--border)',
        borderRadius: radius.md,
      }}>
        <StatutChip statut={statut} />
        <span style={{ ...typoStyle('mono-md'), color: 'var(--text-muted)' }}>
          {prospects.length}
        </span>
      </header>
      <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}>
        {prospects.length === 0 ? (
          <p style={{ ...typoStyle('body-sm'), color: 'var(--text-subtle)', textAlign: 'center', padding: space.md, margin: 0 }}>
            (vide)
          </p>
        ) : (
          prospects.map((p) => (
            <ProspectCard key={p.id} prospect={p} onClick={() => onCardClick(p.id)} />
          ))
        )}
      </div>
    </div>
  );
}

function PipelineKanban({ groups, onCardClick }) {
  return (
    <div style={{
      display: 'flex',
      gap: space.md,
      overflowX: 'auto',
      paddingBottom: space.md,
    }}>
      {STATUTS_ORDRE.map((s) => (
        <PipelineKanbanColumn
          key={s}
          statut={s}
          prospects={groups[s] || []}
          onCardClick={onCardClick}
        />
      ))}
    </div>
  );
}

function PipelineListMobile({ groups, onCardClick }) {
  const [expanded, setExpanded] = useState(() => new Set(['devis_envoye', 'audite']));
  function toggle(s) {
    setExpanded((set) => {
      const next = new Set(set);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}>
      {STATUTS_ORDRE.map((s) => {
        const isOpen = expanded.has(s);
        const list = groups[s] || [];
        return (
          <section key={s} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: radius.md, overflow: 'hidden' }}>
            <button
              type="button"
              onClick={() => toggle(s)}
              aria-expanded={isOpen}
              style={{
                width: '100%',
                background: 'transparent',
                border: 'none',
                padding: space.md,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: space.sm }}>
                <StatutChip statut={s} />
                <span style={{ ...typoStyle('mono-md'), color: 'var(--text-muted)' }}>
                  {list.length}
                </span>
              </div>
              <ArrowRight
                size={16}
                color="var(--text-subtle)"
                style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: cssTransition('standard', ['transform']) }}
              />
            </button>
            {isOpen && (
              <div style={{ padding: space.md, paddingTop: 0, display: 'flex', flexDirection: 'column', gap: space.sm }}>
                {list.length === 0 ? (
                  <p style={{ ...typoStyle('body-sm'), color: 'var(--text-subtle)', margin: 0 }}>(vide)</p>
                ) : (
                  list.map((p) => (
                    <ProspectCard key={p.id} prospect={p} onClick={() => onCardClick(p.id)} />
                  ))
                )}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

function ProspectFormModal({ open, onClose, initial, onSubmit }) {
  const emptyForm = {
    nom: '', type: 'externe', statut: 'repere', contact: '', notes: '', caEstimeParcMensuel: 0,
    reseau: false, tonChoice: 'auto',
  };
  // tonChoice UI : 'auto' (deriveTon reprend la main) | 'vouvoiement' | 'tutoiement' (force)
  const fromInitial = (init) => init
    ? { ...init, reseau: init.reseau === true, tonChoice: init.tonForce === true ? init.ton : 'auto' }
    : emptyForm;
  const [form, setForm] = useState(() => fromInitial(initial));

  useEffect(() => {
    if (open) {
      setForm(fromInitial(initial));
    }
  }, [open, initial]);

  const isValid = form.nom.trim().length > 0;

  function handleSubmit() {
    if (!isValid) return;
    const { tonChoice, ...rest } = form;
    onSubmit({
      ...rest,
      caEstimeParcMensuel: Number(form.caEstimeParcMensuel) || 0,
      reseau: form.reseau === true,
      // ton force seulement si choix explicite ; 'auto' rend la main a deriveTon
      ...(tonChoice === 'auto' ? { tonForce: false } : { ton: tonChoice, tonForce: true }),
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initial?.id ? 'Editer prospect' : 'Nouveau prospect'}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!isValid}>
            {initial?.id ? 'Sauvegarder' : 'Creer'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
          <label htmlFor="p-nom" style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', fontWeight: 500 }}>
            Nom *
          </label>
          <input
            id="p-nom"
            type="text"
            value={form.nom}
            onChange={(e) => setForm({ ...form, nom: e.target.value })}
            placeholder="Nom client / entreprise"
            autoFocus
            style={inputBaseStyle}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: space.md }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
            <label htmlFor="p-type" style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', fontWeight: 500 }}>
              Type
            </label>
            <select
              id="p-type"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              style={inputBaseStyle}
            >
              <option value="externe">B2B externe</option>
              <option value="interne">GMS bureau</option>
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
            <label htmlFor="p-statut" style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', fontWeight: 500 }}>
              Statut
            </label>
            <select
              id="p-statut"
              value={form.statut}
              onChange={(e) => setForm({ ...form, statut: e.target.value })}
              style={inputBaseStyle}
            >
              {STATUTS_ORDRE.map((s) => (
                <option key={s} value={s}>{STATUT_LABELS[s]}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
          <label htmlFor="p-contact" style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', fontWeight: 500 }}>
            Contact
          </label>
          <input
            id="p-contact"
            type="text"
            value={form.contact}
            onChange={(e) => setForm({ ...form, contact: e.target.value })}
            placeholder="Tel, mail, nom personne"
            style={inputBaseStyle}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
          <label htmlFor="p-ca" style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', fontWeight: 500 }}>
            CA estime parc mensuel (EUR HT)
          </label>
          <input
            id="p-ca"
            type="number"
            min={0}
            step="10"
            value={form.caEstimeParcMensuel}
            onChange={(e) => setForm({ ...form, caEstimeParcMensuel: e.target.value })}
            style={{ ...inputBaseStyle, maxWidth: 200 }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: space.md }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
            <label htmlFor="p-ton" style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', fontWeight: 500 }}>
              Ton des mails
            </label>
            <select
              id="p-ton"
              value={form.tonChoice}
              onChange={(e) => setForm({ ...form, tonChoice: e.target.value })}
              style={inputBaseStyle}
            >
              <option value="auto">Auto (selon segment/statut)</option>
              <option value="vouvoiement">Vouvoiement</option>
              <option value="tutoiement">Tutoiement</option>
            </select>
          </div>
          <label htmlFor="p-reseau" style={{
            display: 'flex',
            alignItems: 'center',
            gap: space.sm,
            cursor: 'pointer',
            alignSelf: 'end',
            paddingBottom: space.sm,
            ...typoStyle('body-md'),
            color: 'var(--text)',
          }}>
            <input
              id="p-reseau"
              type="checkbox"
              checked={form.reseau === true}
              onChange={(e) => setForm({ ...form, reseau: e.target.checked })}
              style={{ accentColor: 'var(--accent)' }}
            />
            Reseau / franchise
          </label>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
          <label htmlFor="p-notes" style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', fontWeight: 500 }}>
            Notes
          </label>
          <textarea
            id="p-notes"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={3}
            placeholder="Contexte commercial, decideurs, particularites..."
            style={{ ...inputBaseStyle, height: 'auto', padding: space.sm, resize: 'vertical' }}
          />
        </div>
      </div>
    </Modal>
  );
}

/**
 * Bloc fiche prospect "Commandes & reassort" (SPEC-04) : historique d'achat
 * reel + prochaine echeance predite + bouton "Commande confirmee" (2e point
 * d'entree de l'evenement commande, avec la carte Dashboard).
 */
function ProspectCommandesBlock({ prospect }) {
  const { showToast } = useToast();
  const [tick, setTick] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const commandes = useMemo(
    () => listCommandesByProspect(prospect.id).sort((a, b) => String(b.date).localeCompare(String(a.date))),
    [prospect.id, tick],
  );
  const prochaine = useMemo(() => {
    if (!commandes.length) return null;
    const preds = computeReassortsCommande(commandes[0], prospect);
    if (!preds.length) return null;
    preds.sort((a, b) => a.dateReassort - b.dateReassort);
    return preds[0];
  }, [commandes, prospect]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
      <h3 style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, margin: 0 }}>
        Commandes & reassort ({commandes.length})
      </h3>
      {commandes.length === 0 ? (
        <span style={{ ...typoStyle('body-sm'), color: 'var(--text-subtle)' }}>
          Aucune commande journalisee. Le suivi demarre au prochain devis accepte
          ou via "Commande confirmee".
        </span>
      ) : (
        <>
          {commandes.slice(0, 3).map((c) => (
            <div key={c.id} style={{
              border: '1px solid var(--border)',
              borderRadius: radius.sm,
              padding: space.sm,
              display: 'flex',
              justifyContent: 'space-between',
              gap: space.sm,
              ...typoStyle('body-sm'),
            }}>
              <span>{formatRelativeDate(c.date)} · {c.lignes.length} ref(s)</span>
              <span style={{ color: 'var(--text-muted)' }}>
                {c.totalHT != null ? formatEUR(c.totalHT) : ''} {c.source === 'devis_accepte' ? '(devis)' : c.source === 'reassort_confirme' ? '(reassort)' : ''}
              </span>
            </div>
          ))}
          {prochaine && (
            <div style={{
              background: 'var(--accent-subtle)',
              borderRadius: radius.sm,
              padding: space.sm,
              display: 'flex',
              alignItems: 'center',
              gap: space.sm,
              flexWrap: 'wrap',
              ...typoStyle('body-sm'),
            }}>
              <span style={{ color: 'var(--accent-strong)', fontWeight: 600 }}>
                Prochain reassort : {prochaine.dateReassort.toLocaleDateString('fr-FR')} — {prochaine.ref_interne} ×{prochaine.quantite}
              </span>
              {prochaine.estimation && <EstimationBadge />}
            </div>
          )}
        </>
      )}
      <div>
        <Button variant="secondary" size="sm" onClick={() => setConfirmOpen(true)}>
          Commande confirmee
        </Button>
      </div>
      <ConfirmCommandeModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        prospect={prospect}
        prefill={prochaine ? {
          ref_interne: prochaine.ref_interne,
          designation: prochaine.designation,
          quantite: prochaine.quantite,
          pa_ht: prochaine.pa_ht,
        } : null}
        onConfirmed={() => {
          showToast('Commande enregistree — prochain reassort recalcule', { kind: 'success' });
          setTick((t) => t + 1);
        }}
      />
    </div>
  );
}

const INTERACTION_TYPE_LABELS = { appel: 'Appel', mail: 'Mail', visite: 'Visite', note: 'Note' };
const INTERACTION_TYPE_ICONS = { appel: Phone, mail: Mail, visite: MapPin, note: FileText };

/**
 * Formulaire d'ajout d'interaction (SPEC-05, Parties 1+3).
 *
 * "Saisie vocale" = dictee clavier OS directement dans le champ texte
 * (decision Wissam 2026-08-18, remplace Web Speech API — zero code, micro
 * natif Android/iOS, pas de probleme Firefox). Le bouton "Structurer avec
 * IA" est optionnel : il reformule le texte + propose type/prochaine action
 * via /api/structure-interaction, TOUJOURS affiche dans des champs editables
 * avant le clic "Enregistrer" — aucune ecriture automatique.
 */
function AddInteractionModal({ open, onClose, prospect, onAdded }) {
  const { showToast } = useToast();
  const [texte, setTexte] = useState('');
  const [type, setType] = useState('note');
  const [prochaineDate, setProchaineDate] = useState('');
  const [prochaineLibelle, setProchaineLibelle] = useState('');
  const [source, setSource] = useState('manuel');
  const [structuring, setStructuring] = useState(false);

  const dejaPlanifie = prospect ? hasFutureProchaineAction(prospect) : false;

  useEffect(() => {
    if (!open) return;
    setTexte('');
    setType('note');
    setSource('manuel');
    if (dejaPlanifie) {
      setProchaineDate('');
      setProchaineLibelle('');
    } else {
      const j7 = new Date();
      j7.setDate(j7.getDate() + 7);
      setProchaineDate(j7.toISOString().slice(0, 10));
      setProchaineLibelle('Relancer');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prospect?.id]);

  async function handleStructurer() {
    if (!texte.trim()) return;
    setStructuring(true);
    try {
      const resp = await fetch('/api/structure-interaction', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transcript: texte, date: new Date().toISOString() }),
      });
      if (!resp.ok) {
        if (resp.status === 404) {
          showToast("Function /api/structure-interaction non deployee.", { kind: 'warning', duration: 6000 });
          return;
        }
        const err = await resp.text();
        throw new Error(`HTTP ${resp.status}: ${err.slice(0, 200)}`);
      }
      const data = await resp.json();
      setType(data.type || 'note');
      setTexte(data.texte_resume || texte);
      if (data.prochaineAction?.date) {
        setProchaineDate(data.prochaineAction.date);
        setProchaineLibelle(data.prochaineAction.libelle || '');
      }
      // Sans mention d'action dans le transcript : ne rien ecraser si le
      // prospect a deja une prochaine action planifiee ; sinon les champs
      // pre-remplis (J+7 "Relancer") restent en place, editables.
      setSource('vocal');
      showToast('Dictee structuree — relis avant d\'enregistrer', { kind: 'info' });
    } catch (err) {
      showToast(`Structuration echouee : ${err.message}`, { kind: 'danger', duration: 6000 });
    } finally {
      setStructuring(false);
    }
  }

  const prochaineActionInput = prochaineDate ? { date: prochaineDate, libelle: prochaineLibelle } : null;
  const isValid = texte.trim().length > 0 && (dejaPlanifie || !!prochaineDate);

  function handleSave() {
    if (!isValid || !prospect) return;
    const record = addInteraction({
      prospectId: prospect.id,
      type,
      texte: texte.trim(),
      source,
      prochaineAction: prochaineActionInput,
    });
    if (!record) {
      showToast('Enregistrement refuse : prochaine action requise.', { kind: 'danger' });
      return;
    }
    showToast('Interaction enregistree', { kind: 'success' });
    onAdded?.();
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Interaction — ${prospect?.nom || ''}`}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button variant="primary" onClick={handleSave} disabled={!isValid}>Enregistrer</Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
          <label htmlFor="int-texte" style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', fontWeight: 500 }}>
            Texte *
          </label>
          <textarea
            id="int-texte"
            value={texte}
            onChange={(e) => { setTexte(e.target.value); setSource('manuel'); }}
            rows={4}
            placeholder="Dicte au clavier (micro natif) ou tape directement..."
            style={{ ...inputBaseStyle, height: 'auto', padding: space.sm, resize: 'vertical' }}
          />
          <div>
            <Button variant="secondary" size="sm" iconLeft={Sparkles} onClick={handleStructurer} disabled={!texte.trim() || structuring}>
              {structuring ? 'Structuration...' : 'Structurer avec IA'}
            </Button>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
          <label htmlFor="int-type" style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', fontWeight: 500 }}>
            Type
          </label>
          <select id="int-type" value={type} onChange={(e) => setType(e.target.value)} style={inputBaseStyle}>
            {Object.entries(INTERACTION_TYPE_LABELS).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </div>

        <div style={{
          background: 'var(--bg-subtle)',
          borderRadius: radius.sm,
          padding: space.sm,
          display: 'flex',
          flexDirection: 'column',
          gap: space.sm,
        }}>
          <span style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', fontWeight: 600 }}>
            Prochaine action {dejaPlanifie ? '(deja planifiee — laisser vide pour ne pas changer)' : '* obligatoire'}
          </span>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: space.sm }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
              <label htmlFor="int-pa-date" style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>Date</label>
              <input id="int-pa-date" type="date" value={prochaineDate} onChange={(e) => setProchaineDate(e.target.value)} style={inputBaseStyle} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
              <label htmlFor="int-pa-libelle" style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>Libelle</label>
              <input id="int-pa-libelle" type="text" value={prochaineLibelle} onChange={(e) => setProchaineLibelle(e.target.value)} style={inputBaseStyle} />
            </div>
          </div>
          {!isValid && !dejaPlanifie && (
            <span style={{ ...typoStyle('body-sm'), color: 'var(--danger)' }}>
              Aucune prochaine action planifiee sur ce prospect : une date est requise pour enregistrer.
            </span>
          )}
        </div>
      </div>
    </Modal>
  );
}

/** Historique compact des interactions (SPEC-05, Partie 1). */
function InteractionsBlock({ prospect }) {
  const [tick, setTick] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const interactions = useMemo(
    () => listInteractionsByProspect(prospect.id),
    [prospect.id, tick],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, margin: 0 }}>
          Interactions ({interactions.length})
        </h3>
        <Button variant="secondary" size="sm" iconLeft={MessageSquare} onClick={() => setAddOpen(true)}>
          + Interaction
        </Button>
      </div>
      {interactions.length === 0 ? (
        <span style={{ ...typoStyle('body-sm'), color: 'var(--text-subtle)' }}>
          Aucune interaction journalisee.
        </span>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: space.xs }}>
          {interactions.slice(0, 8).map((i) => {
            const Icon = INTERACTION_TYPE_ICONS[i.type] || FileText;
            const expanded = expandedId === i.id;
            return (
              <li key={i.id}>
                <button
                  type="button"
                  onClick={() => setExpandedId(expanded ? null : i.id)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    background: 'transparent',
                    border: '1px solid var(--border)',
                    borderRadius: radius.sm,
                    padding: space.sm,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: space.sm, ...typoStyle('body-sm') }}>
                    <Icon size={14} strokeWidth={1.75} color="var(--text-muted)" />
                    <span style={{ color: 'var(--text-muted)' }}>{formatRelativeDate(i.date)}</span>
                    {i.source === 'vocal' && <span style={{ color: 'var(--text-subtle)' }}>· dicte</span>}
                  </div>
                  <span style={{ color: 'var(--text)', whiteSpace: expanded ? 'pre-wrap' : 'nowrap', overflow: expanded ? 'visible' : 'hidden', textOverflow: 'ellipsis' }}>
                    {i.texte}
                  </span>
                  {i.prochaineAction?.date && (
                    <span style={{ ...typoStyle('body-sm'), color: 'var(--accent-strong)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <CalendarClock size={12} strokeWidth={1.75} />
                      {i.prochaineAction.libelle || 'Action'} — {new Date(i.prochaineAction.date).toLocaleDateString('fr-FR')}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <AddInteractionModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        prospect={prospect}
        onAdded={() => setTick((t) => t + 1)}
      />
    </div>
  );
}

function ProspectDetailModal({ open, onClose, prospect, onEdit, onDelete, onChangeStatut, onCreateAudit, onVoirAudit, onVoirDevis, onComposeMail }) {
  if (!prospect) return null;
  const audits = useMemo(
    () => listAudits().filter((a) => a.id && prospect.id && getAudit(a.id)?.prospectId === prospect.id),
    [prospect.id],
  );
  const devisList = useMemo(() => listDevisByProspect(prospect.id), [prospect.id]);
  const hasMinimalInfo = !!(prospect.contact || prospect.notes || prospect.caEstimeParcMensuel > 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={prospect.nom}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Fermer</Button>
          <Button variant="secondary" onClick={onEdit}>Editer</Button>
          <Button variant="danger" iconLeft={Trash2} onClick={onDelete}>Supprimer</Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: space.sm, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={onChangeStatut}
            title="Changer le statut"
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
            }}
          >
            <StatutChip statut={prospect.statut} />
          </button>
          <span style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
            {prospect.type === 'interne' ? 'GMS bureau' : 'B2B externe'}
          </span>
        </div>

        <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: `${space.xs} ${space.md}`, margin: 0, ...typoStyle('body-sm') }}>
          {prospect.contact && (
            <>
              <dt style={{ color: 'var(--text-muted)' }}>Contact</dt>
              <dd style={{ margin: 0, color: 'var(--text)' }}>{prospect.contact}</dd>
            </>
          )}
          {prospect.caEstimeParcMensuel > 0 && (
            <>
              <dt style={{ color: 'var(--text-muted)' }}>CA parc /mois</dt>
              <dd style={{ margin: 0, color: 'var(--text)' }}>{formatEUR(prospect.caEstimeParcMensuel, { withHT: false })}</dd>
            </>
          )}
          {prospect.dateContact && (
            <>
              <dt style={{ color: 'var(--text-muted)' }}>Dernier contact</dt>
              <dd style={{ margin: 0, color: 'var(--text)' }}>{formatRelativeDate(prospect.dateContact)}</dd>
            </>
          )}
          {prospect.dateAjout && (
            <>
              <dt style={{ color: 'var(--text-muted)' }}>Ajoute</dt>
              <dd style={{ margin: 0, color: 'var(--text)' }}>{formatRelativeDate(prospect.dateAjout)}</dd>
            </>
          )}
          {prospect.dateDernierStatut && (
            <>
              <dt style={{ color: 'var(--text-muted)' }}>Statut maj</dt>
              <dd style={{ margin: 0, color: 'var(--text)' }}>{formatRelativeDate(prospect.dateDernierStatut)}</dd>
            </>
          )}
        </dl>

        {prospect.notes && (
          <div style={{
            background: 'var(--bg-subtle)',
            borderRadius: radius.sm,
            padding: space.sm,
            ...typoStyle('body-sm'),
            color: 'var(--text-muted)',
            whiteSpace: 'pre-wrap',
          }}>
            {prospect.notes}
          </div>
        )}

        <ProspectCommandesBlock prospect={prospect} />

        <InteractionsBlock prospect={prospect} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
          <h3 style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, margin: 0 }}>
            Audits lies ({audits.length})
          </h3>
          {audits.length === 0 ? (
            <span style={{ ...typoStyle('body-sm'), color: 'var(--text-subtle)' }}>Aucun audit.</span>
          ) : (
            audits.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => onVoirAudit(a.id)}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border)',
                  borderRadius: radius.sm,
                  padding: space.sm,
                  cursor: 'pointer',
                  textAlign: 'left',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span style={{ ...typoStyle('body-sm') }}>{formatRelativeDate(a.createdAt)}</span>
                <ArrowRight size={14} />
              </button>
            ))
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
          <h3 style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, margin: 0 }}>
            Devis lies ({devisList.length})
          </h3>
          {devisList.length === 0 ? (
            <span style={{ ...typoStyle('body-sm'), color: 'var(--text-subtle)' }}>Aucun devis.</span>
          ) : (
            devisList.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => onVoirDevis(d.id)}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border)',
                  borderRadius: radius.sm,
                  padding: space.sm,
                  cursor: 'pointer',
                  textAlign: 'left',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: space.sm,
                }}
              >
                <span style={{ ...typoStyle('body-sm'), display: 'flex', alignItems: 'center', gap: space.sm }}>
                  {d.numero || '(sans numero)'}
                  <DevisStatutChip statut={d.statut} />
                  <span style={{ color: 'var(--text-muted)' }}>{formatEUR(d.total || 0, { withHT: false })}</span>
                </span>
                <ArrowRight size={14} />
              </button>
            ))
          )}
        </div>

        {prospect.siret ? (
          <div style={{
            background: 'var(--bg-subtle)',
            borderRadius: radius.md,
            padding: space.md,
            border: '1px solid var(--border)',
          }}>
            <EnrichPitchPanel
              etablissement={{
                siret: prospect.siret,
                nom: prospect.nom,
                adresse: prospect.contact || '',
                commune: prospect.commune || '',
                codePostal: '',
                naf: prospect.nafCode || '',
                effectifLabel: prospect.effectifLabel || '',
                score: prospect.sireneScore || 0,
              }}
              autoFetch={false}
            />
          </div>
        ) : (
          <div style={{
            background: 'var(--bg-subtle)',
            borderRadius: radius.md,
            padding: space.md,
            border: '1px dashed var(--border-strong)',
            display: 'flex',
            flexDirection: 'column',
            gap: space.sm,
          }}>
            <div style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
              Prospect manuel (sans SIRET). Enrichissement annuaire et pitch auto indisponibles. Tu peux composer un mail à la main via la Rédaction.
            </div>
            {!hasMinimalInfo && (
              <div style={{ ...typoStyle('body-sm'), color: 'var(--warning)' }}>
                Aucune info contact / notes / CA. Clique « Éditer » pour compléter la fiche.
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: space.sm, flexWrap: 'wrap' }}>
          <Button variant="primary" iconLeft={Printer} onClick={onCreateAudit}>
            Créer un audit
          </Button>
          {onComposeMail && (
            <Button variant="secondary" iconLeft={PenLine} onClick={onComposeMail}>
              Composer un mail
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

function StatutChangeModal({ open, onClose, current, onConfirm }) {
  const [picked, setPicked] = useState(current);
  useEffect(() => { if (open) setPicked(current); }, [open, current]);
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Changer le statut"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button variant="primary" onClick={() => onConfirm(picked)} disabled={picked === current}>
            Valider
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}>
        {STATUTS_ORDRE.map((s) => (
          <label
            key={s}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: space.sm,
              padding: space.sm,
              borderRadius: radius.sm,
              cursor: 'pointer',
              background: picked === s ? 'var(--accent-subtle)' : 'transparent',
              border: `1px solid ${picked === s ? 'var(--accent)' : 'var(--border)'}`,
            }}
          >
            <input
              type="radio"
              name="statut"
              checked={picked === s}
              onChange={() => setPicked(s)}
              style={{ accentColor: 'var(--accent)' }}
            />
            <StatutChip statut={s} />
          </label>
        ))}
      </div>
    </Modal>
  );
}

function Module5Pipeline() {
  const isMobile = useMediaQuery(BREAKPOINT_MOBILE);
  const { showToast } = useToast();
  const { setActiveId } = useNav();
  const [refreshKey, setRefreshKey] = useState(0);

  // isMounted ref pour eviter setState apres unmount (toggle Liste/Kanban + modal close race)
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Cleanup au mount : sync SIRENE_IMPORTED avec prospects reels.
  // Garantit coherence si des SIRETs orphelins existent (ex: reset partiel).
  useEffect(() => {
    cleanupOrphanImportedSirets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prospects = useMemo(() => getProspects(), [refreshKey]);
  const [detailId, setDetailId] = useState(null);

  // Deep-link depuis la carte Dashboard "Prospects sans prochaine action"
  // (meme mecanique que __FG_REDACTION_PREFILL__ pour Module6Redaction).
  useEffect(() => {
    const openId = typeof window !== 'undefined' ? window.__FG_PIPELINE_OPEN_ID__ : null;
    if (openId) {
      window.__FG_PIPELINE_OPEN_ID__ = null;
      setDetailId(openId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [formMode, setFormMode] = useState(null); // 'add' | 'edit' | null
  const [statutChangeOpen, setStatutChangeOpen] = useState(false);
  const [statutChangeTargetId, setStatutChangeTargetId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' | 'liste' (desktop only)

  const groups = useMemo(() => {
    const out = {};
    for (const s of STATUTS_ORDRE) out[s] = [];
    for (const p of prospects) {
      if (out[p.statut]) out[p.statut].push(p);
    }
    return out;
  }, [prospects]);

  const detailProspect = detailId ? prospects.find((p) => p.id === detailId) : null;

  function bump() {
    if (!isMountedRef.current) return;
    setRefreshKey((k) => k + 1);
  }

  function handleFormSubmit(form) {
    if (formMode === 'edit' && detailId) {
      updateProspect(detailId, form);
      showToast('Prospect mis à jour', { kind: 'success' });
    } else {
      addProspect(form);
      showToast('Prospect ajouté', { kind: 'success' });
    }
    setFormMode(null);
    bump();
  }

  function handleDelete() {
    if (!detailId) return;
    removeProspect(detailId);
    setDeleteConfirm(false);
    setDetailId(null);
    showToast('Prospect supprimé', { kind: 'danger' });
    bump();
  }

  function handleStatutChange(newStatut) {
    // Cible : prospect actuel OU prospect capture pour stagger (FIX 6)
    const targetId = detailId || statutChangeTargetId;
    if (!targetId) return;
    const patch = { statut: newStatut };
    // Planifier relance J+5 sur passage en devis_envoye
    if (newStatut === 'devis_envoye') {
      const j5 = new Date();
      j5.setDate(j5.getDate() + 5);
      patch.relanceJ5 = j5.toISOString();
    }
    const updated = updateProspect(targetId, patch);
    setStatutChangeOpen(false);
    setStatutChangeTargetId(null);

    // Triggers metier
    if (newStatut === 'audite') {
      showToast('Pense à créer un devis pour ce prospect', { kind: 'info', duration: 5000 });
    } else if (newStatut === 'devis_envoye') {
      showToast(`Devis envoyé. Relance planifiée J+5 (${new Date(patch.relanceJ5).toLocaleDateString('fr-FR')}).`, { kind: 'success', duration: 5000 });
    } else if (newStatut === 'compte_ouvert') {
      showToast('Compte ouvert ! +50 EUR si commande >= 1500 EUR HT.', { kind: 'success', duration: 6000 });
    } else if (newStatut === 'recurrent') {
      showToast('Client récurrent : CA récurrent parc actualise sur Dashboard.', { kind: 'success', duration: 5000 });
    } else {
      showToast(`Statut mis à jour : ${STATUT_LABELS[newStatut]}`, { kind: 'success' });
    }

    if (updated) {
      try {
        triggerOnStatutChange(updated, newStatut);
      } catch (err) {
        console.warn('[pipeline] triggerOnStatutChange err:', err.message);
      }
    }
    bump();
  }

  function handleCreateAudit() {
    if (!detailId) return;
    setActiveProspectForAudit(detailId);
    setDetailId(null);
    setActiveId('audit');
  }

  function handleVoirAudit(auditId) {
    setActiveAuditId(auditId);
    setDetailId(null);
    setActiveId('audit');
  }

  function handleVoirDevis(devisId) {
    setActiveDevisId(devisId);
    setDetailId(null);
    setActiveId('chiffrage');
  }

  function handleComposeMail() {
    if (!detailId) return;
    const p = prospects.find((x) => x.id === detailId);
    if (!p) return;
    // Pre-fill Module 6 : type adapte au statut
    const typeByStatut = {
      repere: 'premier_contact',
      audite: 'proposition_audit',
      devis_envoye: 'relance',
      compte_ouvert: 'remerciement',
      recurrent: 'remerciement',
    };
    window.__FG_REDACTION_PREFILL__ = {
      prospectId: detailId,
      type: typeByStatut[p.statut] || 'premier_contact',
    };
    setDetailId(null);
    setActiveId('redaction');
  }

  return (
    <section
      id="panel-pipeline"
      role="tabpanel"
      aria-labelledby="tab-pipeline"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: space.lg,
        maxWidth: 1400,
        margin: '0 auto',
        width: '100%',
      }}
    >
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: space.md, flexWrap: 'wrap' }}>
        <h1 style={{ ...typoStyle('display-lg'), color: 'var(--text)', margin: 0 }}>
          Pipeline prospects
        </h1>
        <div style={{ display: 'flex', gap: space.sm, alignItems: 'center' }}>
          {!isMobile && (
            <ViewToggle value={viewMode} onChange={setViewMode} options={[
              { value: 'kanban', label: 'Kanban', icon: LayoutGrid },
              { value: 'liste',  label: 'Liste',  icon: List },
            ]} />
          )}
          <Button variant="primary" iconLeft={Plus} onClick={() => { setDetailId(null); setFormMode('add'); }}>
            Nouveau prospect
          </Button>
        </div>
      </header>

      {prospects.length === 0 ? (
        <div style={{
          background: 'var(--bg-elevated)',
          border: '1px dashed var(--border-strong)',
          borderRadius: radius.md,
          padding: space.xl,
          textAlign: 'center',
          color: 'var(--text-muted)',
        }}>
          Aucun prospect. Ajoute ton premier compte ci-dessus.
        </div>
      ) : isMobile ? (
        <PipelineListMobile groups={groups} onCardClick={setDetailId} />
      ) : viewMode === 'liste' ? (
        <PipelineListe prospects={prospects} onCardClick={setDetailId} />
      ) : (
        <PipelineKanban groups={groups} onCardClick={setDetailId} />
      )}

      <ProspectDetailModal
        open={!!detailProspect && formMode !== 'edit'}
        prospect={detailProspect}
        onClose={() => setDetailId(null)}
        onEdit={() => setFormMode('edit')}
        onDelete={() => setDeleteConfirm(true)}
        onChangeStatut={() => {
          // Stagger : ferme modal detail avant d'ouvrir la modal statut.
          // Capture l'id cible AVANT close pour conserver le contexte (StatutChangeModal lit current).
          if (!detailId) return;
          setStatutChangeTargetId(detailId);
          setDetailId(null);
          window.setTimeout(() => {
            if (!isMountedRef.current) return;
            setStatutChangeOpen(true);
          }, 200);
        }}
        onCreateAudit={handleCreateAudit}
        onVoirAudit={handleVoirAudit}
        onVoirDevis={handleVoirDevis}
        onComposeMail={handleComposeMail}
      />

      <ProspectFormModal
        open={formMode !== null}
        onClose={() => setFormMode(null)}
        initial={formMode === 'edit' ? detailProspect : null}
        onSubmit={handleFormSubmit}
      />

      <StatutChangeModal
        open={statutChangeOpen}
        onClose={() => { setStatutChangeOpen(false); setStatutChangeTargetId(null); }}
        current={
          (detailProspect?.statut)
          || prospects.find((p) => p.id === statutChangeTargetId)?.statut
          || 'repere'
        }
        onConfirm={handleStatutChange}
      />

      <Modal
        open={deleteConfirm}
        onClose={() => setDeleteConfirm(false)}
        title="Supprimer ce prospect ?"
        size="sm"
        variant="danger"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteConfirm(false)}>Annuler</Button>
            <Button variant="danger" onClick={handleDelete}>Supprimer</Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          Suppression definitive de {detailProspect?.nom || 'ce prospect'}. Les audits et devis lies ne sont pas supprimes.
        </p>
      </Modal>
    </section>
  );
}

// ============================================================
// MISSION 6 — Module 6 Redaction
// ============================================================
//
// Generation mails via Function Cloudflare /api/claude (proxy Anthropic).
// Creation brouillon Gmail via /api/gmail-draft. JAMAIS d'envoi auto.
//
// Si Functions non deployees (404/5xx au premier appel) : message
// disabled, action bloquee, pas de crash. Re-tentative au prochain clic.
//
// 3 templates :
//   - relance : prospect en statut devis_envoye, relance J+5
//   - proposition_audit : prospect en statut audite, proposition commerciale post-audit
//   - remerciement : prospect en statut compte_ouvert/recurrent, remerciement + suivi

const MAIL_TYPES = [
  { id: 'premier_contact',  label: 'Premier contact (prospect repere)', icon: Mail,           suggestedStatut: 'repere' },
  { id: 'relance',          label: 'Relance J+5 (devis sans reponse)',  icon: RefreshCw,      suggestedStatut: 'devis_envoye' },
  { id: 'proposition_audit',label: 'Proposition post-audit',            icon: FileText,       suggestedStatut: 'audite' },
  { id: 'remerciement',     label: 'Remerciement ouverture compte',     icon: CheckCircle2,   suggestedStatut: 'compte_ouvert' },
  { id: 'relance_devis_j7', label: 'Relance devis J+7',                 icon: RefreshCw,      suggestedStatut: 'devis_envoye' },
  { id: 'relance_reassort', label: 'Relance reassort (stock bientot epuise)', icon: RefreshCw, suggestedStatut: 'recurrent' },
];

function Module6Redaction() {
  const { showToast } = useToast();
  const { status: healthStatus } = useHealth();
  const [prospects, setProspects] = useState(() => getProspects());
  // Lecture pre-fill depuis Bell notification (window-level param)
  const prefill = typeof window !== 'undefined' ? window.__FG_REDACTION_PREFILL__ : null;
  if (prefill && typeof window !== 'undefined') {
    window.__FG_REDACTION_PREFILL__ = null;
  }
  const [type, setType] = useState(prefill?.type || 'premier_contact');
  const [prospectId, setProspectId] = useState(prefill?.prospectId || '');
  const [context, setContext] = useState('');
  // Ton (SPEC-03) : derive de la fiche prospect, derogation ponctuelle possible
  const [tonOverride, setTonOverride] = useState(null);        // 'vouvoiement'|'tutoiement'|null
  const [overrideThisMailOnly, setOverrideThisMailOnly] = useState(false);
  const [generatedSubject, setGeneratedSubject] = useState('');
  const [generatedBody, setGeneratedBody] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [functionStatus, setFunctionStatus] = useState({ claude: 'unknown', gmail: 'unknown' });
  const [copyDone, setCopyDone] = useState(false);
  const [history, setHistory] = useState(() => getMailsGeneres());
  const prospect = prospects.find((p) => p.id === prospectId);
  const canGenerate = !!prospect && !isGenerating;
  const gmailConfigured = integrationState(healthStatus, 'gmail') === 'ok';
  const canSend = generatedBody.trim().length > 0 && !!prospect && !isSending && gmailConfigured;
  // Le bouton n'est desactive que pour les causes "rien a envoyer" ; Gmail non
  // configure passe par un toast explicite dans handleSendDraft.
  const sendButtonDisabled = generatedBody.trim().length === 0 || !prospect || isSending;

  // Ton effectif : derogation ponctuelle > fiche prospect (deriveTon)
  const tonFiche = prospect ? deriveTon(prospect) : 'vouvoiement';
  const tonEffectif = tonOverride || tonFiche;
  const segment = prospect ? deriveSegment(prospect) : 'b2b_externe';

  // Dernier devis rattache non brouillon (le plus recent) — injecte verbatim
  const dernierDevis = useMemo(() => {
    if (!prospect) return null;
    const lies = listDevisByProspect(prospect.id)
      .filter((d) => d.statut && d.statut !== 'brouillon')
      .sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')));
    if (!lies.length) return null;
    const detail = getDevis(lies[0].id);
    return {
      numero: lies[0].numero,
      totalHT: lies[0].total,
      statut: lies[0].statut,
      date: (detail?.dateEnvoi || lies[0].updatedAt || '').slice(0, 10),
      nbLignes: detail?.lignes?.length || null,
    };
  }, [prospect?.id]);

  // Prochain reassort predit du prospect (SPEC-04) — pour le type relance_reassort
  const prochainReassort = useMemo(() => {
    if (!prospect) return null;
    const derniere = getDerniereCommande(prospect.id);
    if (!derniere) return null;
    const preds = computeReassortsCommande(derniere, prospect);
    if (!preds.length) return null;
    preds.sort((a, b) => a.dateReassort - b.dateReassort);
    const p0 = preds[0];
    return {
      ref_interne: p0.ref_interne,
      designation: p0.designation,
      quantite: p0.quantite,
      totalHT: p0.pa_ht != null ? Math.round(p0.pa_ht * p0.quantite * 100) / 100 : null,
      dateReassort: p0.dateReassort.toISOString().slice(0, 10),
      estimation: p0.estimation,
    };
  }, [prospect?.id]);

  // Dernier audit rattache — via prospectId de l'index (nouveaux audits seulement)
  const dernierAudit = useMemo(() => {
    if (!prospect) return null;
    const lies = listAudits()
      .filter((a) => a.prospectId === prospect.id)
      .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
    if (!lies.length) return null;
    const detail = getAudit(lies[0].id);
    return {
      clientName: lies[0].clientName,
      nbImprimantes: detail?.imprimantes?.length ?? null,
      date: String(lies[0].createdAt || '').slice(0, 10),
    };
  }, [prospect?.id]);

  function handleChangeTon() {
    if (!prospect) return;
    const autre = tonEffectif === 'vouvoiement' ? 'tutoiement' : 'vouvoiement';
    if (overrideThisMailOnly) {
      setTonOverride(autre);
      showToast(`Ton ${autre} pour ce mail uniquement (fiche inchangee)`, { kind: 'info' });
    } else {
      setProspectTon(prospect.id, autre);
      setProspects(getProspects());
      setTonOverride(null);
      showToast(`Ton ${autre} enregistre sur la fiche ${prospect.nom}`, { kind: 'success' });
    }
  }

  async function handleGenerate() {
    if (!canGenerate) return;
    setIsGenerating(true);
    setGeneratedSubject('');
    setGeneratedBody('');
    try {
      const resp = await fetch('/api/claude', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type,
          prospect: {
            nom: prospect.nom,
            type: prospect.type,
            contact: prospect.contact || '',
            statut: prospect.statut,
            notes: prospect.notes || '',
          },
          ton: tonEffectif,
          segment,
          devis: dernierDevis,
          audit: dernierAudit,
          reassort: type === 'relance_reassort' ? prochainReassort : null,
          context,
        }),
      });
      if (!resp.ok) {
        if (resp.status === 404) {
          setFunctionStatus((s) => ({ ...s, claude: 'missing' }));
          showToast("Function /api/claude non deployee. Verifie le deploy Cloudflare.", { kind: 'warning', duration: 6000 });
          return;
        }
        const err = await resp.text();
        throw new Error(`HTTP ${resp.status}: ${err.slice(0, 200)}`);
      }
      const data = await resp.json();
      setFunctionStatus((s) => ({ ...s, claude: 'ok' }));
      setGeneratedSubject(data.subject || '');
      setGeneratedBody(data.body || '');
      // historique
      addMailGenere({
        type,
        prospectId,
        prospectNom: prospect.nom,
        subject: data.subject || '',
        body: data.body || '',
      });
      setHistory(getMailsGeneres());
      showToast('Mail généré', { kind: 'success' });
    } catch (err) {
      console.error('[redaction] generate:', err);
      setFunctionStatus((s) => ({ ...s, claude: 'error' }));
      showToast(`Erreur generation : ${err.message}`, { kind: 'danger', duration: 6000 });
    } finally {
      setIsGenerating(false);
    }
  }

  async function handleCopyMail() {
    const txt = `Objet : ${generatedSubject}\n\n${generatedBody}`;
    try {
      await navigator.clipboard.writeText(txt);
      setCopyDone(true);
      showToast('Mail copie dans le presse-papiers', { kind: 'success' });
      window.setTimeout(() => setCopyDone(false), 1500);
    } catch {
      showToast('Echec copie. Selectionne et copie manuellement.', { kind: 'danger' });
    }
  }

  function handleReuse(mail) {
    setType(mail.type);
    setProspectId(mail.prospectId || '');
    setGeneratedSubject(mail.subject);
    setGeneratedBody(mail.body);
    showToast('Mail rechargé', { kind: 'info' });
  }

  async function handleSendDraft() {
    // Gmail non configure : le bouton reste CLIQUABLE et explique pourquoi ca
    // ne marchera pas (audit prod 18/08 — il etait `disabled`, donc le clic
    // ne produisait rien du tout et le title ne s'affiche pas au tactile).
    if (!gmailConfigured) {
      showToast('Gmail non configuré — voir Réglages > Configuration integrations', {
        kind: 'warning',
        duration: 6000,
      });
      return;
    }
    if (!canSend) return;
    setIsSending(true);
    try {
      const resp = await fetch('/api/gmail-draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: prospect.contact || '',
          subject: generatedSubject || '(sans objet)',
          body: generatedBody,
        }),
      });
      if (!resp.ok) {
        if (resp.status === 404) {
          setFunctionStatus((s) => ({ ...s, gmail: 'missing' }));
          showToast("Function /api/gmail-draft non deployee. Verifie le deploy Cloudflare + OAuth Gmail.", { kind: 'warning', duration: 6000 });
          return;
        }
        const err = await resp.text();
        throw new Error(`HTTP ${resp.status}: ${err.slice(0, 200)}`);
      }
      const data = await resp.json();
      setFunctionStatus((s) => ({ ...s, gmail: 'ok' }));
      addDraft({
        prospectId,
        type,
        subject: generatedSubject,
        body: generatedBody,
        sentToGmail: true,
        gmailDraftId: data.draftId || null,
      });
      showToast('Brouillon créé dans Gmail. Relis et envoie depuis Gmail.', { kind: 'success', duration: 6000 });
    } catch (err) {
      console.error('[redaction] gmail-draft:', err);
      setFunctionStatus((s) => ({ ...s, gmail: 'error' }));
      showToast(`Erreur brouillon Gmail : ${err.message}`, { kind: 'danger', duration: 6000 });
    } finally {
      setIsSending(false);
    }
  }

  function handleSaveLocal() {
    if (!generatedBody.trim()) return;
    addDraft({
      prospectId,
      type,
      subject: generatedSubject,
      body: generatedBody,
      sentToGmail: false,
      gmailDraftId: null,
    });
    showToast('Brouillon sauvegarde localement (pas envoye Gmail)', { kind: 'success' });
  }

  return (
    <section
      id="panel-redaction"
      role="tabpanel"
      aria-labelledby="tab-redaction"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: space.lg,
        maxWidth: 880,
        margin: '0 auto',
        width: '100%',
      }}
    >
      <header style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}>
        <h1 style={{ ...typoStyle('display-lg'), color: 'var(--text)', margin: 0 }}>
          Redaction
        </h1>
        <p style={{ ...typoStyle('body-md'), color: 'var(--text-muted)', margin: 0 }}>
          Generation de mails via Anthropic. <strong>Aucun envoi automatique</strong> :
          creation d'un brouillon dans Gmail uniquement. Tu relis et tu envoies toi-meme.
        </p>
      </header>

      {/* Type mail */}
      <div style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: radius.lg,
        padding: space.lg,
        display: 'flex',
        flexDirection: 'column',
        gap: space.md,
      }}>
        <label style={{
          ...typoStyle('body-sm'),
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          fontWeight: 600,
        }}>
          Type de mail
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
          {MAIL_TYPES.map((t) => {
            const Icon = t.icon;
            const checked = type === t.id;
            return (
              <label key={t.id} style={{
                display: 'flex',
                alignItems: 'center',
                gap: space.sm,
                padding: space.sm,
                borderRadius: radius.sm,
                cursor: 'pointer',
                background: checked ? 'var(--accent-subtle)' : 'transparent',
                border: `1px solid ${checked ? 'var(--accent)' : 'var(--border)'}`,
                transition: cssTransition('instant', ['background', 'border-color']),
              }}>
                <input
                  type="radio"
                  name="mail-type"
                  checked={checked}
                  onChange={() => setType(t.id)}
                  style={{ accentColor: 'var(--accent)' }}
                />
                <Icon size={16} strokeWidth={1.75} color={checked ? 'var(--accent)' : 'var(--text-muted)'} />
                <span style={{ ...typoStyle('body-md'), color: checked ? 'var(--accent-strong)' : 'var(--text)' }}>
                  {t.label}
                </span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Prospect */}
      <div style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: radius.lg,
        padding: space.lg,
        display: 'flex',
        flexDirection: 'column',
        gap: space.sm,
      }}>
        <label htmlFor="redaction-prospect" style={{
          ...typoStyle('body-sm'),
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          fontWeight: 600,
        }}>
          Destinataire
        </label>
        <select
          id="redaction-prospect"
          value={prospectId}
          onChange={(e) => { setProspectId(e.target.value); setTonOverride(null); }}
          style={inputBaseStyle}
        >
          <option value="">— Choisir un prospect —</option>
          {prospects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.nom} ({STATUT_LABELS[p.statut]}) · {p.contact || 'sans contact'}
            </option>
          ))}
        </select>
        {prospect && !prospect.contact && (
          <div style={{ ...typoStyle('body-sm'), color: 'var(--warning)' }}>
            ⚠ Ce prospect n'a pas de contact (mail/tel). Brouillon Gmail necessite un destinataire.
          </div>
        )}

        {/* Ton du client (SPEC-03) : persiste sur la fiche, derogation ponctuelle possible */}
        {prospect && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: space.sm, flexWrap: 'wrap' }}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: space.xs,
                  padding: `2px ${space.sm}`,
                  background: tonOverride ? 'var(--warning-subtle)' : 'var(--accent-subtle)',
                  color: tonOverride ? 'var(--warning)' : 'var(--accent-strong)',
                  borderRadius: radius.full,
                  ...typoStyle('body-sm'),
                  fontWeight: 600,
                }}
              >
                {tonEffectif === 'tutoiement' ? 'Tutoiement' : 'Vouvoiement'} — {prospect.nom}
                {tonOverride ? ' (ce mail uniquement)' : prospect.tonForce ? '' : ' (auto)'}
              </span>
              <button
                type="button"
                onClick={handleChangeTon}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  ...typoStyle('body-sm'),
                  padding: 0,
                  fontFamily: 'inherit',
                }}
              >
                Changer
              </button>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: space.xs, cursor: 'pointer', ...typoStyle('body-sm'), color: 'var(--text-subtle)' }}>
              <input
                type="checkbox"
                checked={overrideThisMailOnly}
                onChange={(e) => { setOverrideThisMailOnly(e.target.checked); if (!e.target.checked) setTonOverride(null); }}
                style={{ accentColor: 'var(--accent)' }}
              />
              Pour ce mail uniquement (ne pas modifier la fiche)
            </label>
            {(dernierDevis || dernierAudit) && (
              <div style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
                {dernierDevis && <>Devis rattache : <strong>{dernierDevis.numero}</strong> — {formatEUR(dernierDevis.totalHT)} ({dernierDevis.statut}). </>}
                {dernierAudit && <>Audit du {dernierAudit.date}{dernierAudit.nbImprimantes != null ? ` (${dernierAudit.nbImprimantes} imprimantes)` : ''}. </>}
                Injecte automatiquement dans le mail.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Context */}
      <div style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: radius.lg,
        padding: space.lg,
        display: 'flex',
        flexDirection: 'column',
        gap: space.sm,
      }}>
        <label htmlFor="redaction-context" style={{
          ...typoStyle('body-sm'),
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          fontWeight: 600,
        }}>
          Contexte additionnel (optionnel)
        </label>
        <textarea
          id="redaction-context"
          value={context}
          onChange={(e) => setContext(e.target.value)}
          rows={3}
          placeholder="Detail specifique : objection client, evenement, decideur a mentionner..."
          style={{ ...inputBaseStyle, height: 'auto', padding: space.sm, resize: 'vertical' }}
        />
      </div>

      {/* Bouton generer */}
      <div>
        <Button
          variant="primary"
          size="lg"
          iconLeft={PenLine}
          onClick={handleGenerate}
          disabled={!canGenerate}
          loading={isGenerating}
        >
          Generer le mail
        </Button>
        {functionStatus.claude === 'missing' && (
          <p style={{ ...typoStyle('body-sm'), color: 'var(--warning)', marginTop: space.sm }}>
            Function <code>/api/claude</code> non deployee. La generation sera disponible apres deploy Cloudflare + clef ANTHROPIC_API_KEY.
          </p>
        )}
      </div>

      {/* Resultat */}
      {(generatedSubject || generatedBody) && (
        <div style={{
          background: 'var(--bg-elevated)',
          border: '1px solid var(--accent)',
          borderRadius: radius.lg,
          padding: space.lg,
          display: 'flex',
          flexDirection: 'column',
          gap: space.md,
        }}>
          <header style={{
            ...typoStyle('body-sm'),
            color: 'var(--accent-strong)',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            fontWeight: 600,
          }}>
            Resultat (editable avant envoi)
          </header>
          <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
            <label htmlFor="result-subject" style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>Objet</label>
            <input
              id="result-subject"
              type="text"
              value={generatedSubject}
              onChange={(e) => setGeneratedSubject(e.target.value)}
              style={inputBaseStyle}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
            <label htmlFor="result-body" style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>Corps</label>
            <textarea
              id="result-body"
              value={generatedBody}
              onChange={(e) => setGeneratedBody(e.target.value)}
              rows={12}
              style={{ ...inputBaseStyle, height: 'auto', padding: space.sm, resize: 'vertical', fontFamily: typoStyle('body-md').fontFamily }}
            />
          </div>
          <div style={{ display: 'flex', gap: space.sm, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Button
              variant="secondary"
              iconLeft={copyDone ? Check : Copy}
              onClick={handleCopyMail}
            >
              {copyDone ? 'Copié !' : 'Copier le mail'}
            </Button>
            <Button variant="ghost" onClick={handleSaveLocal}>
              Sauvegarder brouillon local
            </Button>
            <Button
              variant="primary"
              iconLeft={Mail}
              onClick={handleSendDraft}
              disabled={sendButtonDisabled}
              loading={isSending}
              title={!gmailConfigured ? 'Configurez Gmail dans Reglages' : undefined}
            >
              Creer brouillon Gmail
            </Button>
          </div>
          {!gmailConfigured && (
            <p style={{ ...typoStyle('body-sm'), color: 'var(--warning)', margin: 0 }}>
              Gmail non configuré. <strong>Configurez Gmail dans Reglages</strong> pour creer un brouillon. En attendant : sauvegarde local ou copie manuelle.
            </p>
          )}
          {functionStatus.gmail === 'missing' && gmailConfigured && (
            <p style={{ ...typoStyle('body-sm'), color: 'var(--warning)', margin: 0 }}>
              Function <code>/api/gmail-draft</code> non deployee. Sauvegarde le brouillon localement en attendant.
            </p>
          )}
          <p style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', margin: 0 }}>
            <strong>Rappel</strong> : "Creer brouillon Gmail" cree uniquement un brouillon dans ta boite (jamais d'envoi auto). Tu dois relire et envoyer depuis Gmail manuellement.
          </p>
        </div>
      )}

      {/* Historique 5 derniers mails */}
      {history.length > 0 && (
        <div style={{
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          borderRadius: radius.lg,
          padding: space.lg,
          display: 'flex',
          flexDirection: 'column',
          gap: space.md,
        }}>
          <header style={{
            ...typoStyle('body-sm'),
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            fontWeight: 600,
          }}>
            Historique mails generes (5 derniers)
          </header>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: space.sm }}>
            {history.slice(0, 5).map((m) => (
              <li key={m.id} style={{
                padding: space.md,
                background: 'var(--bg-subtle)',
                borderRadius: radius.sm,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: space.md,
                flexWrap: 'wrap',
              }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ ...typoStyle('body-md'), color: 'var(--text)', fontWeight: 600 }}>
                    {m.subject || '(sans objet)'}
                  </div>
                  <div style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', marginTop: 2 }}>
                    {m.prospectNom || 'sans prospect'} - {MAIL_TYPES.find((t) => t.id === m.type)?.label || m.type} - {new Date(m.createdAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleReuse(m)}>
                  Réutiliser
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

// ============================================================
// MISSION 7 — Module 7 Reglages
// ============================================================

function InkyaImportBloc() {
  const { showToast } = useToast();
  const [meta, setMeta] = useState(() => getInkyaMeta());
  const [query, setQuery] = useState('');
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  // Index lookup memoise (reconstruit quand meta.count change = nouvel import)
  const index = useMemo(() => buildInkyaIndex(getInkyaCatalogue()), [meta.count]);

  function handleFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target.result);
        // Accepte array direct OU { references: [...] } OU { etablissements } non
        const refs = Array.isArray(parsed) ? parsed : parsed.references || parsed.data;
        if (!Array.isArray(refs) || refs.length === 0) {
          showToast('JSON invalide : tableau de produits attendu', { kind: 'danger' });
          return;
        }
        // Validation minimale : chaque item doit avoir ref_inkya
        const valid = refs.filter((r) => r && (r.ref_inkya || r.ref));
        const n = setInkyaCatalogue(valid.map((r) => ({
          ref_inkya: r.ref_inkya || r.ref,
          ean: r.ean || null,
          designation: r.designation || r.nom || '',
          marque: r.marque || '',
          categorie: r.categorie || '',
          compatible_ou_oem: r.compatible_ou_oem || '',
          prix_ttc: Number(r.prix_ttc) || null,
          prix_ht_estime: Number(r.prix_ht_estime) || null,
          dispo: r.dispo || '',
          url: r.url || '',
        })));
        setMeta(getInkyaMeta());
        setResult(null);
        showToast(`Catalogue Inkya importe : ${n} references`, { kind: 'success' });
      } catch (err) {
        showToast(`Erreur import : ${err.message}`, { kind: 'danger' });
      }
    };
    reader.readAsText(file);
  }

  function handleCheck() {
    const q = query.trim();
    if (!q) return;
    const hit = findInkyaMatch({ ref_interne: q, ean: /^\d{13}$/.test(q) ? q : null }, index);
    setResult(hit || { notFound: true, q });
  }

  function handleClear() {
    clearInkyaCatalogue();
    setMeta(getInkyaMeta());
    setResult(null);
    showToast('Catalogue Inkya vide', { kind: 'info' });
  }

  return (
    <ReglageBloc
      title="Comparateur Inkya (imprim-encre.com)"
      description="Catalogue concurrent (prix CLIENT FINAL TTC). Sert d'argument prix dans le Chiffrage : nos refs G&G moins cheres que le site grossiste."
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: space.md, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200, ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
          {meta.count > 0 ? (
            <>
              <strong style={{ ...typoStyle('mono-md'), color: 'var(--text)' }}>{meta.count}</strong> references importees
              {meta.importedAt && <> · {new Date(meta.importedAt).toLocaleDateString('fr-FR')}</>}
            </>
          ) : (
            'Aucun catalogue Inkya importe.'
          )}
        </div>
        <Button variant="secondary" iconLeft={Upload} onClick={() => fileRef.current?.click()}>
          Importer catalogue-inkya.json
        </Button>
        {meta.count > 0 && (
          <Button variant="ghost" size="sm" iconLeft={Trash2} onClick={handleClear}>
            Vider
          </Button>
        )}
        <input ref={fileRef} type="file" accept="application/json,.json" onChange={handleFile} style={{ display: 'none' }} />
      </div>

      {meta.count > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm, padding: space.md, background: 'var(--bg-subtle)', borderRadius: radius.md }}>
          <label htmlFor="inkya-check" style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
            Verifier une reference (ta ref interne ou un EAN)
          </label>
          <div style={{ display: 'flex', gap: space.sm, flexWrap: 'wrap' }}>
            <input
              id="inkya-check"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCheck(); }}
              placeholder="ex: GG_HL280X-S"
              style={{ ...inputBaseStyle, flex: 1, minWidth: 180 }}
            />
            <Button variant="primary" iconLeft={Search} onClick={handleCheck}>Comparer</Button>
          </div>

          {result && result.notFound && (
            <div style={{ ...typoStyle('body-sm'), color: 'var(--warning)' }}>
              Aucune correspondance Inkya pour "{result.q}". (Ref non vendue chez eux ou nommee differemment.)
            </div>
          )}
          {result && result.match && (
            <div style={{
              display: 'flex', flexDirection: 'column', gap: space.xs,
              padding: space.md, background: 'var(--accent-subtle)',
              border: '1px solid var(--accent)', borderRadius: radius.sm,
            }}>
              <div style={{ ...typoStyle('body-md'), color: 'var(--text)', fontWeight: 600 }}>
                {result.match.designation || result.match.ref_inkya}
              </div>
              <div style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
                Ref Inkya <strong>{result.match.ref_inkya}</strong> · match par {result.method}
              </div>
              <div style={{ ...typoStyle('mono-md'), color: 'var(--text)' }}>
                Prix client final Inkya : <strong>{formatEUR(result.match.prix_ttc || 0, { withHT: false })} TTC</strong>
                {result.match.prix_ht_estime ? ` (${formatEUR(result.match.prix_ht_estime, { withHT: false })} HT)` : ''}
              </div>
              {result.match.url && (
                <a href={result.match.url} target="_blank" rel="noreferrer" style={{ ...typoStyle('body-sm'), color: 'var(--info)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  Voir sur imprim-encre.com <ExternalLink size={12} />
                </a>
              )}
            </div>
          )}
        </div>
      )}
    </ReglageBloc>
  );
}

function CommandCopyBox({ command, showToast }) {
  const [done, setDone] = useState(false);
  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(command);
      setDone(true);
      showToast?.('Commande copiée', { kind: 'success' });
      window.setTimeout(() => setDone(false), 1500);
    } catch {
      showToast?.('Échec copie. Sélectionne manuellement.', { kind: 'danger' });
    }
  }
  return (
    <div style={{ display: 'flex', alignItems: 'stretch', gap: space.sm, flexWrap: 'wrap' }}>
      <code
        style={{
          flex: 1,
          minWidth: 200,
          ...typoStyle('mono-md'),
          background: 'var(--bg)',
          border: '1px solid var(--border-strong)',
          borderRadius: radius.sm,
          padding: `${space.sm} ${space.md}`,
          color: 'var(--text)',
          overflowX: 'auto',
          whiteSpace: 'nowrap',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {command}
      </code>
      <Button variant="secondary" size="sm" iconLeft={done ? Check : Copy} onClick={handleCopy}>
        {done ? 'Copié !' : 'Copier la commande'}
      </Button>
    </div>
  );
}

function ReglageBloc({ title, description, children }) {
  return (
    <section
      style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: radius.lg,
        padding: space.lg,
        display: 'flex',
        flexDirection: 'column',
        gap: space.md,
      }}
    >
      <header style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <h2 style={{ ...typoStyle('display-md'), color: 'var(--text)', margin: 0 }}>
          {title}
        </h2>
        {description && (
          <p style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', margin: 0 }}>
            {description}
          </p>
        )}
      </header>
      {children}
    </section>
  );
}

function Module7Reglages() {
  const { theme, toggleTheme } = useTheme();
  const { showToast } = useToast();
  const { status: healthStatus, refresh: refreshHealth } = useHealth();
  const stored = getCANonFG();
  const [caInput, setCaInput] = useState(stored.montant ? String(stored.montant) : '');
  const [resetOpen, setResetOpen] = useState(false);
  const [importConfirmOpen, setImportConfirmOpen] = useState(false);
  const [pendingImport, setPendingImport] = useState(null);
  const [storageSize, setStorageSize] = useState(() => approxSize());
  const [gmailTest, setGmailTest] = useState({ loading: false, result: null });
  const [syncRun, setSyncRun] = useState({ loading: false, result: null, error: null });
  const [cronSecretInput, setCronSecretInput] = useState('');
  const [showCronSecret, setShowCronSecret] = useState(false);
  const [syncState, setSyncState] = useState(null); // { last_sync, refs_count, tarif_date }
  const fileInputRef = useRef(null);

  // Etat sync KV au mount (refs_count + last_sync cote serveur)
  useEffect(() => {
    let alive = true;
    fetch('/api/sync-tarif')
      .then((r) => r.json())
      .then((d) => { if (alive) setSyncState(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const lastSync = getLastSync();
  const lastSyncFR = lastSync
    ? new Date(lastSync).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
    : null;

  const now = new Date();
  const monthLabel = now.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const monthLabelCap = monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1);

  function handleSaveCa() {
    const n = Number(caInput);
    if (isNaN(n) || n < 0) {
      showToast('Montant invalide', { kind: 'danger' });
      return;
    }
    setCANonFG(n);
    showToast(`CA non-FG enregistre : ${formatEUR(n, { withHT: false })}`, { kind: 'success' });
    setStorageSize(approxSize());
  }

  function handleReset() {
    const deleted = clearAll();
    setResetOpen(false);
    setCaInput('');
    setStorageSize(approxSize());
    showToast(`${deleted} cles supprimees. Recharge la page pour seed.`, { kind: 'danger', duration: 6000 });
  }

  function setThemeExplicit(target) {
    if (target !== theme) toggleTheme();
  }

  async function handleGmailTest() {
    setGmailTest({ loading: true, result: null });
    try {
      const resp = await fetch('/api/gmail-status');
      const data = await resp.json();
      setGmailTest({ loading: false, result: data });
      if (data.ok) {
        showToast(`Gmail OK : ${data.email}`, { kind: 'success' });
      } else {
        showToast(`Gmail KO : ${data.error || 'erreur'}`, { kind: 'warning', duration: 6000 });
      }
    } catch (err) {
      setGmailTest({ loading: false, result: { ok: false, error: err.message } });
      showToast(`Erreur test Gmail : ${err.message}`, { kind: 'danger' });
    }
  }

  async function handleTriggerSync() {
    if (!cronSecretInput.trim()) {
      showToast('Renseigne CRON_SECRET pour autoriser la sync manuelle', { kind: 'warning' });
      return;
    }
    setSyncRun({ loading: true, result: null, error: null });
    try {
      const resp = await fetch('/api/sync-tarif', {
        method: 'POST',
        headers: { 'x-cron-secret': cronSecretInput.trim() },
      });
      const data = await resp.json();
      if (!resp.ok || !data.ok) {
        const msg = data.error || `HTTP ${resp.status}`;
        setSyncRun({ loading: false, result: null, error: msg });
        showToast(`Sync echouee : ${msg}`, { kind: 'danger', duration: 7000 });
        return;
      }
      setSyncRun({ loading: false, result: data, error: null });
      const secs = data.duration_ms ? (data.duration_ms / 1000).toFixed(1) : '?';
      showToast(`Sync OK : ${data.refs_count} réfs importées en ${secs}s`, { kind: 'success', duration: 6000 });
      // Rafraichir l'etat KV affiche
      setSyncState({ last_sync: data.last_sync || new Date().toISOString(), refs_count: data.refs_count, tarif_date: data.tarif_date });
    } catch (err) {
      setSyncRun({ loading: false, result: null, error: err.message });
      showToast(`Erreur sync : ${err.message}`, { kind: 'danger' });
    }
  }

  function handleExport() {
    const backup = exportAllData();
    if (!backup) {
      showToast('Export indisponible (localStorage KO)', { kind: 'danger' });
      return;
    }
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const dateTag = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `backup-fg-os-${dateTag}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showToast('Backup téléchargé', { kind: 'success' });
  }

  function handleImportFile(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset pour reselection meme fichier
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target.result);
        if (parsed?.app !== 'fg-os' || !parsed.data) {
          showToast('Fichier invalide (app != fg-os)', { kind: 'danger' });
          return;
        }
        setPendingImport(parsed);
        setImportConfirmOpen(true);
      } catch (err) {
        showToast(`JSON invalide : ${err.message}`, { kind: 'danger' });
      }
    };
    reader.readAsText(file);
  }

  function handleConfirmImport() {
    if (!pendingImport) return;
    const result = importAllData(pendingImport);
    setImportConfirmOpen(false);
    setPendingImport(null);
    if (result.ok) {
      showToast(`${result.imported} cles importees. Rechargement...`, { kind: 'success' });
      window.setTimeout(() => window.location.reload(), 800);
    } else {
      showToast(`Import echoue : ${result.error}`, { kind: 'danger' });
    }
  }

  return (
    <section
      id="panel-reglages"
      role="tabpanel"
      aria-labelledby="tab-reglages"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: space.lg,
        maxWidth: 720,
        margin: '0 auto',
        width: '100%',
      }}
    >
      <header>
        <h1 style={{ ...typoStyle('display-lg'), color: 'var(--text)', margin: 0 }}>
          Reglages
        </h1>
      </header>

      <ReglageBloc
        title="Apparence"
        description="Bascule clair / sombre. Persistee localement (DESIGN.md : light default force, dark optionnel manuel)."
      >
        <div style={{ display: 'flex', gap: space.sm }}>
          <Button
            variant={theme === 'light' ? 'primary' : 'secondary'}
            iconLeft={Sun}
            onClick={() => setThemeExplicit('light')}
          >
            Clair
          </Button>
          <Button
            variant={theme === 'dark' ? 'primary' : 'secondary'}
            iconLeft={Moon}
            onClick={() => setThemeExplicit('dark')}
          >
            Sombre
          </Button>
        </div>
      </ReglageBloc>

      <ReglageBloc
        title="CA mensuel hors-FG"
        description="Saisie manuelle. Utilise par le Dashboard pour calculer les paliers de prime (cumule avec CA FG)."
      >
        <div style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
          Mois courant : <strong style={{ color: 'var(--text)' }}>{monthLabelCap}</strong>
          {stored.mois && stored.mois !== now.toISOString().slice(0, 7) && (
            <span style={{ color: 'var(--warning)' }}>
              {' '}· Dernier enregistrement : {stored.mois}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: space.sm, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="number"
            min={0}
            step="100"
            value={caInput}
            onChange={(e) => setCaInput(e.target.value)}
            placeholder="0"
            aria-label="CA mensuel hors-FG en euros HT"
            style={{ ...inputBaseStyle, maxWidth: 240 }}
          />
          <span style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>EUR HT</span>
          <Button variant="primary" onClick={handleSaveCa}>Enregistrer</Button>
        </div>
      </ReglageBloc>

      <ReglageBloc
        title="Configuration integrations"
        description="Statut des secrets Cloudflare Pages et tests de connexion."
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: `${space.sm} ${space.md}`, alignItems: 'center', ...typoStyle('body-sm') }}>
          {[
            { key: 'anthropic', label: 'Anthropic (mails IA)', secret: 'ANTHROPIC_API_KEY' },
            { key: 'sirene',    label: 'SIRENE (prospection)', secret: 'SIRENE_API_KEY' },
            { key: 'gmail',     label: 'Gmail (brouillons)',   secret: 'GMAIL_CLIENT_ID + SECRET + REFRESH_TOKEN' },
            { key: 'notion',    label: 'Notion (sync tarif)',  secret: 'NOTION_API_KEY + NOTION_PAGE_ID' },
            { key: 'cron',      label: 'Cron secret (sync)',   secret: 'CRON_SECRET' },
            { key: 'pappers',   label: 'Pappers (enrich+)',    secret: 'PAPPERS_API_KEY (optionnel)' },
          ].map((it) => {
            const state = integrationState(healthStatus, it.key);
            const err = healthStatus?.details?.[it.key]?.error;
            const color = integrationStateColor(state);
            return (
              <Fragment key={it.key}>
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: color,
                    display: 'inline-block',
                  }}
                />
                <div>
                  <div style={{ color: 'var(--text)', fontWeight: 600 }}>{it.label}</div>
                  <code style={{ color: 'var(--text-subtle)', fontSize: 11 }}>{it.secret}</code>
                  {state === 'invalid' && err && (
                    <div style={{ color: 'var(--danger)', fontSize: 11, marginTop: 2 }}>{err}</div>
                  )}
                </div>
                <span style={{ color, fontWeight: 600 }}>
                  {state === 'ok' ? 'OK'
                    : state === 'invalid' ? 'INVALIDE'
                    : state === 'present' ? 'configuré'
                    : state === 'unknown' ? '…'
                    : 'non configuré'}
                </span>
              </Fragment>
            );
          })}
        </div>
        <Button variant="ghost" size="sm" iconLeft={RefreshCw} onClick={refreshHealth}>
          Re-verifier
        </Button>

        {/* Gmail test */}
        <div style={{
          marginTop: space.md,
          padding: space.md,
          background: 'var(--bg-subtle)',
          borderRadius: radius.md,
          display: 'flex',
          flexDirection: 'column',
          gap: space.sm,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: space.sm }}>
            <strong style={{ ...typoStyle('body-md'), color: 'var(--text)' }}>Test Gmail</strong>
            <Button variant="secondary" size="sm" iconLeft={Mail} onClick={handleGmailTest} loading={gmailTest.loading} disabled={gmailTest.loading}>
              Tester connexion Gmail
            </Button>
          </div>
          {gmailTest.result && (
            <div style={{ ...typoStyle('body-sm'), color: gmailTest.result.ok ? 'var(--accent-strong)' : 'var(--warning)' }}>
              {gmailTest.result.ok
                ? `Connecte sur ${gmailTest.result.email} (${gmailTest.result.messagesTotal} messages dans la boite).`
                : `KO : ${gmailTest.result.error}`}
            </div>
          )}
        </div>

        {/* Setup OAuth */}
        <details style={{ marginTop: space.md, ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
          <summary style={{ cursor: 'pointer', color: 'var(--text)', fontWeight: 600 }}>
            Setup Gmail OAuth pas a pas
          </summary>
          <ol style={{ marginTop: space.sm, paddingLeft: space.lg, display: 'flex', flexDirection: 'column', gap: space.xs, lineHeight: 1.6 }}>
            <li>Console Google Cloud → cree un projet (ou reutilise).</li>
            <li>APIs & Services → active "Gmail API".</li>
            <li>Credentials → OAuth 2.0 Client ID type "Web application".</li>
            <li>Authorized redirect URI : <code>https://developers.google.com/oauthplayground</code></li>
            <li>Note <code>client_id</code> et <code>client_secret</code>.</li>
            <li>Va sur <a href="https://developers.google.com/oauthplayground" target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>OAuth Playground</a> → settings (engrenage) → coche "Use your own OAuth credentials" → renseigne.</li>
            <li>Step 1 : scope <code>https://www.googleapis.com/auth/gmail.compose</code> → Authorize APIs → connecte ton compte Espace Conso.</li>
            <li>Step 2 : "Exchange authorization code for tokens" → copie <code>refresh_token</code>.</li>
            <li>Cloudflare Pages → Settings → Environment → ajoute en production :
              <pre style={{ background: 'var(--bg)', padding: space.sm, borderRadius: radius.sm, overflow: 'auto', marginTop: 4 }}>
{`GMAIL_CLIENT_ID=<client_id>
GMAIL_CLIENT_SECRET=<client_secret>
GMAIL_REFRESH_TOKEN=<refresh_token>`}
              </pre>
            </li>
            <li>Redéploie le projet (les secrets Pages Functions ne sont pas runtime-read).</li>
            <li>Clique "Tester connexion Gmail" ci-dessus pour valider.</li>
          </ol>
        </details>

        {/* Note importante redeploy + commande a copier */}
        <div style={{
          marginTop: space.md,
          padding: space.md,
          background: 'var(--warning-subtle)',
          border: '1px solid var(--warning)',
          borderRadius: radius.md,
          display: 'flex',
          flexDirection: 'column',
          gap: space.sm,
        }}>
          <div style={{ ...typoStyle('body-sm'), color: 'var(--text)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: space.xs }}>
            <AlertTriangle size={16} strokeWidth={2} color="var(--warning)" />
            Important : après ajout des 3 secrets Gmail dans Cloudflare, redéploie l'application pour que les Functions les lisent.
          </div>
          <CommandCopyBox command="npx wrangler pages deploy dist --project-name fg-os" showToast={showToast} />
        </div>
      </ReglageBloc>

      <ReglageBloc
        title="Synchronisation tarif Notion"
        description="Lit le XLSM attache a la page Notion et ecrit dans le namespace KV FG_CATALOGUE. Mission 2 implementee."
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: space.md, flexWrap: 'wrap' }}>
          <RefreshCw size={18} color={syncState?.last_sync ? 'var(--accent)' : 'var(--warning)'} strokeWidth={1.75} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ ...typoStyle('body-md'), color: 'var(--text)' }}>
              {syncState?.last_sync
                ? `Dernière sync : ${new Date(syncState.last_sync).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}`
                : (lastSyncFR ? `Dernière sync locale : ${lastSyncFR}` : 'Aucune sync effectuée')}
            </div>
            <div style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
              {syncState?.refs_count
                ? `${syncState.refs_count} réfs en KV${syncState.tarif_date ? ` · tarif ${syncState.tarif_date}` : ''}`
                : 'Fallback catalogue bundle si KV vide (588 réfs FG + 88 laser).'}
            </div>
          </div>
        </div>
        <div style={{
          display: 'flex', flexDirection: 'column', gap: space.sm,
          padding: space.md,
          background: 'var(--bg-subtle)',
          borderRadius: radius.md,
        }}>
          <label htmlFor="cron-secret-input" style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
            CRON_SECRET (jamais stocké, juste pour declencher cette sync)
          </label>
          <div style={{ display: 'flex', gap: space.sm, flexWrap: 'wrap', alignItems: 'stretch' }}>
            <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
              <input
                id="cron-secret-input"
                type={showCronSecret ? 'text' : 'password'}
                value={cronSecretInput}
                onChange={(e) => setCronSecretInput(e.target.value)}
                placeholder="Colle CRON_SECRET ici"
                style={{ ...inputBaseStyle, paddingRight: 38 }}
              />
              <button
                type="button"
                onClick={() => setShowCronSecret((v) => !v)}
                aria-label={showCronSecret ? 'Masquer le secret' : 'Afficher le secret'}
                title={showCronSecret ? 'Masquer' : 'Afficher'}
                style={{
                  position: 'absolute',
                  top: '50%',
                  right: 6,
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 4,
                  borderRadius: radius.sm,
                  color: 'var(--text-muted)',
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                {showCronSecret ? <EyeOff size={16} strokeWidth={1.75} /> : <Eye size={16} strokeWidth={1.75} />}
              </button>
            </div>
            <Button
              variant="primary"
              size="md"
              iconLeft={RefreshCw}
              onClick={handleTriggerSync}
              loading={syncRun.loading}
              disabled={syncRun.loading || !cronSecretInput}
            >
              Déclencher sync
            </Button>
          </div>
          {syncRun.error && (
            <div style={{ ...typoStyle('body-sm'), color: 'var(--danger)' }}>
              {syncRun.error}
            </div>
          )}
          {syncRun.result && (
            <div style={{ ...typoStyle('body-sm'), color: 'var(--accent-strong)' }}>
              OK : {syncRun.result.refs_count} refs depuis <strong>{syncRun.result.filename}</strong>
              {syncRun.result.sample && (
                <details style={{ marginTop: space.xs }}>
                  <summary style={{ cursor: 'pointer', color: 'var(--text-muted)' }}>Apercu 3 premieres refs</summary>
                  <pre style={{ marginTop: space.xs, padding: space.sm, background: 'var(--bg)', borderRadius: radius.sm, overflow: 'auto', fontSize: 11 }}>
                    {JSON.stringify(syncRun.result.sample, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          )}
        </div>
      </ReglageBloc>

      <InkyaImportBloc />

      <ReglageBloc
        title="Backup donnees locales"
        description="Export et import du localStorage fg-os: (prospects, audits, devis, notifications, drafts...)."
      >
        <div style={{ display: 'flex', gap: space.sm, flexWrap: 'wrap' }}>
          <Button variant="secondary" iconLeft={Download} onClick={handleExport}>
            Exporter mes donnees
          </Button>
          <Button variant="secondary" iconLeft={Upload} onClick={() => fileInputRef.current?.click()}>
            Importer une sauvegarde
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            onChange={handleImportFile}
            style={{ display: 'none' }}
          />
        </div>
        <p style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', margin: 0 }}>
          Format JSON. L'import remplace integralement les donnees actuelles apres confirmation.
        </p>
      </ReglageBloc>

      <ReglageBloc
        title="Donnees locales"
        description="localStorage namespace fg-os: (prospects, audits, devis, drafts, theme, CA hors-FG, devis counter, notifications)."
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: space.md }}>
          <div style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
            Taille approximative : <strong style={{ ...typoStyle('mono-md'), color: 'var(--text)' }}>
              {(storageSize / 1024).toFixed(1)} Ko
            </strong>
          </div>
          <Button variant="danger" iconLeft={Trash2} onClick={() => setResetOpen(true)}>
            Reset complet
          </Button>
        </div>
      </ReglageBloc>

      <ReglageBloc title="Infos">
        <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: `${space.xs} ${space.md}`, margin: 0, ...typoStyle('body-sm') }}>
          <dt style={{ color: 'var(--text-muted)' }}>App</dt>
          <dd style={{ margin: 0, color: 'var(--text)' }}>FG-OS v0.1.0</dd>
          <dt style={{ color: 'var(--text-muted)' }}>Stack</dt>
          <dd style={{ margin: 0, color: 'var(--text)' }}>React 18 · Vite 5 · Cloudflare Pages + Functions · KV</dd>
          <dt style={{ color: 'var(--text-muted)' }}>Utilisateur</dt>
          <dd style={{ margin: 0, color: 'var(--text)' }}>Outil interne Espace Conso</dd>
          <dt style={{ color: 'var(--text-muted)' }}>Editeur</dt>
          <dd style={{ margin: 0, color: 'var(--text)' }}>TECHFLOW DISTRIBUTION (jamais expose client)</dd>
          <dt style={{ color: 'var(--text-muted)' }}>Branding exports</dt>
          <dd style={{ margin: 0, color: 'var(--text)' }}>Espace Conso uniquement (devis PDF, brouillons Gmail)</dd>
        </dl>
      </ReglageBloc>

      <Modal
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        title="Reset complet des donnees locales ?"
        size="md"
        variant="danger"
        footer={
          <>
            <Button variant="ghost" onClick={() => setResetOpen(false)}>Annuler</Button>
            <Button variant="danger" onClick={handleReset}>Supprimer tout</Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          Toutes les donnees locales (prospects, audits, devis, theme, CA non-FG, compteur devis)
          seront supprimees. Action <strong>definitive</strong>. Le seed v1 sera reapplique au
          prochain chargement du Dashboard.
        </p>
        <p style={{ marginTop: space.md, marginBottom: 0, color: 'var(--text-muted)', ...typoStyle('body-sm') }}>
          Le catalogue bundle (588 refs FG + 88 laser) n'est pas affecte (dans le code, pas le localStorage).
        </p>
      </Modal>

      <Modal
        open={importConfirmOpen}
        onClose={() => { setImportConfirmOpen(false); setPendingImport(null); }}
        title="Importer cette sauvegarde ?"
        size="md"
        variant="danger"
        footer={
          <>
            <Button variant="ghost" onClick={() => { setImportConfirmOpen(false); setPendingImport(null); }}>Annuler</Button>
            <Button variant="danger" onClick={handleConfirmImport}>Remplacer mes donnees</Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          Cela <strong>remplacera toutes vos donnees actuelles</strong> par le contenu du backup.
        </p>
        {pendingImport && (
          <p style={{ marginTop: space.md, marginBottom: 0, color: 'var(--text-muted)', ...typoStyle('body-sm') }}>
            Backup du {pendingImport.exportedAt ? new Date(pendingImport.exportedAt).toLocaleString('fr-FR') : 'date inconnue'} · {Object.keys(pendingImport.data || {}).length} cles.
          </p>
        )}
      </Modal>
    </section>
  );
}

// ============================================================
// MISSION 8 — Module 8 Prospection SIRENE
// ============================================================
//
// Recherche etablissements B2B via INSEE SIRENE API.
// Function /api/prospection-sirene gere auth OAuth + scoring + tri.
// Import direct vers Pipeline (anti-doublon par SIRET).

const DEPARTEMENTS_SECTEUR = [
  '01', '03', '06', '07', '08', '10',
  '13', '25', '26', '30', '34', '38', '39',
  '42', '43', '48', '51', '52',
  '54', '55', '57', '63', '67', '68', '69',
  '70', '71', '73', '74', '83', '84', '88', '90',
];

const NAF_OPTIONS = [
  { code: '8411Z', label: 'Mairies / collectivites territoriales' },
  { code: '6920Z', label: 'Experts-comptables' },
  { code: '6910Z', label: 'Notaires / avocats' },
  { code: '6831Z', label: 'Agences immobilieres' },
  { code: '4520A', label: 'Garages / carrosseries' },
  { code: '8531Z', label: 'Enseignement secondaire general (college)' },
  { code: '8532Z', label: 'Enseignement secondaire technique (lycee)' },
  { code: '7500Z', label: 'Activites veterinaires' },
  { code: '4778A', label: 'Opticiens' },
];

// Code zone pour le Luxembourg (option separee des departements FR)
const ZONE_LUXEMBOURG = 'LU';

/**
 * Recherche dans le dataset statique Luxembourg.
 * Filtre par NAF (souple : prefixe 2 chiffres = secteur). Scoring delegue a
 * scoreProspect (src/lib/scoring.js) : le dataset a un vrai champ `naf`
 * (pas de champ `categorie`), zone Luxembourg detectee via prefixe siret "LU-".
 * Retourne meme schema que /api/prospection-sirene.
 */
function searchLuxembourg(nafCode) {
  const all = luxembourgB2B.etablissements || [];
  const nafPrefix = String(nafCode || '').slice(0, 2);
  const filtered = all.filter((e) => {
    if (!nafCode) return true;
    // Match exact OU meme secteur (2 premiers chiffres) pour elargir un peu
    return e.naf === nafCode || String(e.naf).slice(0, 2) === nafPrefix;
  });
  const list = (filtered.length > 0 ? filtered : all).map((e) => {
    const r = scoreProspect({ nafCode: e.naf, effectifLabel: e.effectifLabel, codePostal: e.codePostal, siret: e.siret, pays: 'LU' });
    return {
      siret: e.siret,
      nom: e.nom,
      adresse: e.adresse,
      commune: e.commune,
      codePostal: e.codePostal,
      naf: e.naf,
      effectifLabel: e.effectifLabel,
      pays: 'LU',
      score: r.score,
      secteur: r.secteur,
      priorite: r.priorite,
    };
  });
  list.sort((a, b) => b.score - a.score);
  return list;
}

function scoreColor(score) {
  if (score >= 70) return { bg: 'var(--accent-subtle)', fg: 'var(--accent-strong)', border: 'var(--accent)' };
  if (score >= 40) return { bg: 'var(--warning-subtle)', fg: 'var(--warning)', border: 'transparent' };
  return { bg: 'var(--danger-subtle)', fg: 'var(--danger)', border: 'transparent' };
}

function ScoreBadge({ score }) {
  const c = scoreColor(score);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: space.xs,
        padding: `2px ${space.sm}`,
        background: c.bg,
        color: c.fg,
        border: `1px solid ${c.border}`,
        borderRadius: radius.full,
        ...typoStyle('mono-md'),
        fontWeight: 700,
        minWidth: 44,
        justifyContent: 'center',
      }}
    >
      {score}
    </span>
  );
}

function ResultRow({ et, alreadyImported, onImport, onOpenFiche, disabled }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto auto auto',
        gap: space.md,
        padding: space.md,
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: radius.md,
        alignItems: 'center',
        cursor: 'pointer',
        transition: cssTransition('instant', ['background', 'border-color']),
      }}
      onClick={() => onOpenFiche(et)}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.borderColor = 'var(--border-strong)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--bg-elevated)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
    >
      <ScoreBadge score={et.score} />
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            ...typoStyle('body-md'),
            color: 'var(--text)',
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={et.nom}
        >
          {et.nom}
        </div>
        <div
          style={{
            ...typoStyle('body-sm'),
            color: 'var(--text-muted)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={et.adresse}
        >
          {et.adresse || '(adresse inconnue)'}
        </div>
        <div style={{ ...typoStyle('body-sm'), color: 'var(--text-subtle)' }}>
          SIRET {et.siret} · NAF {et.naf} · {et.effectifLabel}
        </div>
      </div>
      <div style={{ ...typoStyle('mono-md'), color: 'var(--text-muted)' }}>
        {et.codePostal}
      </div>
      {alreadyImported ? (
        <span
          style={{
            ...typoStyle('body-sm'),
            color: 'var(--accent-strong)',
            background: 'var(--accent-subtle)',
            padding: `${space.xs} ${space.sm}`,
            borderRadius: radius.sm,
            border: '1px solid var(--accent)',
            whiteSpace: 'nowrap',
          }}
        >
          Déjà prospecté
        </span>
      ) : (
        <Button
          variant="secondary"
          size="sm"
          iconLeft={Plus}
          onClick={(e) => { e.stopPropagation(); onImport(et); }}
          disabled={disabled}
        >
          Ajouter
        </Button>
      )}
    </div>
  );
}

// --- EnrichPitchPanel reutilisable (Module 8 + Module 5) ---

async function copyToClipboard(text, label, showToast) {
  try {
    if (typeof navigator === 'undefined' || !navigator.clipboard) {
      throw new Error('clipboard indispo');
    }
    await navigator.clipboard.writeText(text);
    showToast(`${label} copie`, { kind: 'success', duration: 2000 });
  } catch {
    showToast('Copie indisponible (navigateur ou contexte non-securise)', { kind: 'danger' });
  }
}

function CopyButton({ value, label, showToast }) {
  const [done, setDone] = useState(false);
  function handleClick() {
    copyToClipboard(value, label, showToast);
    setDone(true);
    window.setTimeout(() => setDone(false), 1500);
  }
  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={`Copier ${label}`}
      title={`Copier ${label}`}
      style={{
        background: 'transparent',
        border: 'none',
        color: done ? 'var(--accent)' : 'var(--text-subtle)',
        cursor: 'pointer',
        padding: 4,
        borderRadius: radius.sm,
        display: 'inline-flex',
        alignItems: 'center',
        transition: cssTransition('instant', ['color', 'background']),
      }}
      onMouseEnter={(e) => { if (!done) { e.currentTarget.style.color = 'var(--accent)'; e.currentTarget.style.background = 'var(--bg-subtle)'; } }}
      onMouseLeave={(e) => { if (!done) { e.currentTarget.style.color = 'var(--text-subtle)'; e.currentTarget.style.background = 'transparent'; } }}
    >
      {done ? <Check size={14} /> : <Copy size={14} />}
    </button>
  );
}

function CopyPitchButton({ pitch, showToast }) {
  const [done, setDone] = useState(false);
  function handle() {
    copyToClipboard(pitch, 'Pitch', showToast);
    setDone(true);
    window.setTimeout(() => setDone(false), 1500);
  }
  return (
    <Button variant="secondary" size="sm" iconLeft={done ? Check : Copy} onClick={handle}>
      {done ? 'Copié !' : 'Copier le pitch'}
    </Button>
  );
}

function SkeletonLine({ width = '60%', height = 14 }) {
  return (
    <div
      style={{
        width,
        height,
        background: 'var(--bg-subtle)',
        borderRadius: radius.sm,
        animation: 'fg-pulse 1.4s ease-in-out infinite',
      }}
    />
  );
}

function EnrichPitchPanel({ etablissement, autoFetch = true }) {
  const { showToast } = useToast();
  const cached = getEnrichissement(etablissement.siret);
  const [enrichLoading, setEnrichLoading] = useState(false);
  const [enrichData, setEnrichData] = useState(cached);
  const [enrichError, setEnrichError] = useState(null);
  const [pitchLoading, setPitchLoading] = useState(false);
  const [pitchData, setPitchData] = useState(
    cached?.pitch ? { pitch: cached.pitch, objections: cached.objections || [] } : null,
  );
  const [pitchError, setPitchError] = useState(null);

  async function fetchEnrich() {
    setEnrichLoading(true);
    setEnrichError(null);
    try {
      const r = await fetch('/api/enrich-prospect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siret: etablissement.siret,
          nom: etablissement.nom,
          ville: etablissement.commune,
          nafCode: etablissement.naf,
          pays: etablissement.pays || null,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || `HTTP ${r.status}`);
      }
      const data = await r.json();
      if (!data.ok) throw new Error(data.error);
      const saved = saveEnrichissement(etablissement.siret, data);
      setEnrichData(saved);
    } catch (err) {
      setEnrichError(err.message);
    } finally {
      setEnrichLoading(false);
    }
  }

  async function fetchPitch(force = false) {
    if (pitchLoading) return;
    setPitchLoading(true);
    setPitchError(null);
    try {
      const r = await fetch('/api/generate-pitch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nomEntreprise: etablissement.nom,
          nafCode: etablissement.naf,
          dirigeant: enrichData?.dirigeant || null,
          ville: etablissement.commune,
          effectifLabel: etablissement.effectifLabel,
          telephone: enrichData?.telephone || null,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || `HTTP ${r.status}`);
      }
      const data = await r.json();
      if (!data.ok) throw new Error(data.error);
      setPitchData({ pitch: data.pitch, objections: data.objections });
      mergeEnrichissement(etablissement.siret, { pitch: data.pitch, objections: data.objections });
    } catch (err) {
      setPitchError(err.message);
    } finally {
      setPitchLoading(false);
    }
  }

  // Auto-fetch enrich au mount si pas cache
  useEffect(() => {
    if (!autoFetch) return;
    if (cached) return;
    fetchEnrich();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etablissement.siret]);

  // Auto-fetch pitch apres enrich, sauf si deja cache
  useEffect(() => {
    if (!autoFetch) return;
    if (pitchData) return;
    if (enrichLoading) return;
    if (!enrichData && !cached) return;
    fetchPitch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrichData]);

  // Liens de recherche telephone qui FONCTIONNENT (l'ancien /recherche/slug/slug
  // de PagesJaunes renvoyait une page vide). Query-string = format valide.
  const lookupNom = etablissement.nom || '';
  const lookupVille = etablissement.commune || '';
  const isFrSiret = /^\d{14}$/.test(String(etablissement.siret || ''));
  const phoneLinks = [
    {
      label: 'Pages Jaunes',
      url: `https://www.pagesjaunes.fr/annuaire/chercherlespros?quoiqui=${encodeURIComponent(lookupNom)}&ou=${encodeURIComponent(lookupVille)}`,
    },
    {
      label: 'Google',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${lookupNom} ${lookupVille} téléphone`)}`,
    },
    // Fiche officielle annuaire-entreprises (souvent le tel y figure), FR uniquement
    isFrSiret
      ? {
          label: 'Annuaire Entreprises',
          url: `https://annuaire-entreprises.data.gouv.fr/etablissement/${etablissement.siret}`,
        }
      : null,
  ].filter(Boolean);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.lg }}>
      {/* SECTION CONTACT */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}>
        <h3 style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, margin: 0 }}>
          Contact
          {enrichData?.sourceEnrichissement?.length > 0 && (
            <span style={{ ...typoStyle('body-sm'), color: 'var(--text-subtle)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
              {' '}· source {enrichData.sourceEnrichissement.join(' + ')}
            </span>
          )}
        </h3>

        {enrichLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}>
            <SkeletonLine width="70%" />
            <SkeletonLine width="50%" />
            <SkeletonLine width="60%" />
          </div>
        ) : enrichError ? (
          <div style={{ color: 'var(--danger)', ...typoStyle('body-sm') }}>
            Echec enrichissement : {enrichError}
            <Button variant="ghost" size="sm" onClick={fetchEnrich} style={{ marginLeft: space.sm }}>
              Reessayer
            </Button>
          </div>
        ) : enrichData ? (
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: `${space.xs} ${space.md}`, margin: 0 }}>
            <dt style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
              <MapPin size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Adresse
            </dt>
            <dd style={{ margin: 0, ...typoStyle('body-sm'), color: 'var(--text)' }}>
              {etablissement.adresse || '(inconnue)'}
            </dd>

            <dt style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
              <Phone size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Telephone
            </dt>
            <dd style={{ margin: 0, ...typoStyle('body-sm'), color: 'var(--text)', display: 'flex', alignItems: 'center', gap: space.xs, flexWrap: 'wrap' }}>
              {enrichData.telephone ? (
                <>
                  <a
                    href={`tel:${enrichData.telephone}`}
                    style={{ color: 'var(--accent-strong)', textDecoration: 'none', fontWeight: 600 }}
                  >
                    {enrichData.telephone}
                  </a>
                  <CopyButton value={enrichData.telephone} label="Telephone" showToast={showToast} />
                </>
              ) : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: space.xs, flexWrap: 'wrap' }}>
                  <span style={{ color: 'var(--text-subtle)' }}>Non trouvé, chercher sur :</span>
                  {phoneLinks.map((lk) => (
                    <a
                      key={lk.label}
                      href={lk.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: 'var(--info)',
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 3,
                        padding: `2px ${space.sm}`,
                        border: '1px solid var(--border)',
                        borderRadius: radius.full,
                        ...typoStyle('body-sm'),
                      }}
                    >
                      {lk.label} <ExternalLink size={11} />
                    </a>
                  ))}
                </span>
              )}
            </dd>

            <dt style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
              <AtSign size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Email
            </dt>
            <dd style={{ margin: 0, ...typoStyle('body-sm'), color: 'var(--text)', display: 'flex', alignItems: 'center', gap: space.xs }}>
              {enrichData.email ? (
                <>
                  <a href={`mailto:${enrichData.email}`} style={{ color: 'var(--accent-strong)', textDecoration: 'none' }}>
                    {enrichData.email}
                  </a>
                  <CopyButton value={enrichData.email} label="Email" showToast={showToast} />
                </>
              ) : (
                <span style={{ color: 'var(--text-subtle)' }}>Non disponible</span>
              )}
            </dd>

            {enrichData.dirigeant && (
              <>
                <dt style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
                  <User size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  Dirigeant
                </dt>
                <dd style={{ margin: 0, ...typoStyle('body-sm'), color: 'var(--text)' }}>
                  {[enrichData.dirigeant.prenom, enrichData.dirigeant.nom].filter(Boolean).join(' ')}
                  {enrichData.dirigeant.fonction && (
                    <span style={{ color: 'var(--text-muted)' }}> — {enrichData.dirigeant.fonction}</span>
                  )}
                </dd>
              </>
            )}

            {(enrichData.formeJuridique || enrichData.dateCreation || enrichData.capitalSocial) && (
              <>
                <dt style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
                  <Building2 size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  Legal
                </dt>
                <dd style={{ margin: 0, ...typoStyle('body-sm'), color: 'var(--text)' }}>
                  {[
                    enrichData.formeJuridique,
                    enrichData.dateCreation ? `cree ${String(enrichData.dateCreation).slice(0, 4)}` : null,
                    enrichData.capitalSocial ? `capital ${formatEUR(enrichData.capitalSocial, { withHT: false })}` : null,
                  ].filter(Boolean).join(' · ')}
                </dd>
              </>
            )}
          </dl>
        ) : (
          <Button variant="secondary" iconLeft={Sparkles} onClick={fetchEnrich}>
            Enrichir la fiche
          </Button>
        )}
      </div>

      {/* SECTION PITCH */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h3 style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, margin: 0 }}>
            Pitch d'appel
          </h3>
          {pitchData && !pitchLoading && (
            <Button variant="ghost" size="sm" iconLeft={RefreshCw} onClick={() => fetchPitch(true)}>
              Regenerer
            </Button>
          )}
        </div>
        {pitchLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
            <SkeletonLine width="100%" />
            <SkeletonLine width="95%" />
            <SkeletonLine width="85%" />
            <SkeletonLine width="70%" />
          </div>
        ) : pitchError ? (
          <div style={{ color: 'var(--danger)', ...typoStyle('body-sm') }}>
            Echec generation pitch : {pitchError}
            <Button variant="ghost" size="sm" onClick={() => fetchPitch(true)} style={{ marginLeft: space.sm }}>
              Reessayer
            </Button>
          </div>
        ) : pitchData ? (
          <div
            style={{
              background: 'var(--accent-subtle)',
              border: '1px solid var(--accent)',
              borderRadius: radius.md,
              padding: space.lg,
              display: 'flex',
              flexDirection: 'column',
              gap: space.md,
            }}
          >
            <p style={{ margin: 0, ...typoStyle('body-md'), color: 'var(--text)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
              {pitchData.pitch}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <CopyPitchButton pitch={pitchData.pitch} showToast={showToast} />
            </div>
          </div>
        ) : (
          <Button variant="secondary" iconLeft={PenLine} onClick={() => fetchPitch(true)}>
            Generer un pitch d'appel
          </Button>
        )}

        {pitchData?.objections?.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm, marginTop: space.md }}>
            <h4 style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, margin: 0 }}>
              Objections frequentes
            </h4>
            {pitchData.objections.map((o, i) => (
              <div
                key={i}
                style={{
                  background: 'var(--bg-subtle)',
                  borderLeft: '3px solid var(--warning)',
                  borderRadius: radius.sm,
                  padding: space.md,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: space.xs,
                }}
              >
                <div style={{ ...typoStyle('body-sm'), color: 'var(--warning)', fontWeight: 600 }}>
                  "{o.objection}"
                </div>
                <div style={{ ...typoStyle('body-sm'), color: 'var(--text)' }}>
                  → {o.reponse}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// --- FicheProspectModal (Module 8 click sur etablissement) ---

function FicheProspectModal({ open, onClose, etablissement, alreadyImported, onImport }) {
  if (!etablissement) return null;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={etablissement.nom}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Fermer</Button>
          {alreadyImported ? (
            <span style={{ ...typoStyle('body-sm'), color: 'var(--accent-strong)', alignSelf: 'center' }}>
              Déjà en Pipeline
            </span>
          ) : (
            <Button variant="primary" iconLeft={Plus} onClick={onImport} iconRight={ArrowRight}>
              Ajouter au Pipeline
            </Button>
          )}
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: space.lg }}>
        <header style={{ display: 'flex', alignItems: 'center', gap: space.md, flexWrap: 'wrap' }}>
          <ScoreBadge score={etablissement.score} />
          <span style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)' }}>
            NAF {etablissement.naf} · {etablissement.effectifLabel} · SIRET {etablissement.siret}
          </span>
        </header>
        <EnrichPitchPanel etablissement={etablissement} autoFetch={true} />
      </div>
    </Modal>
  );
}

function Module8Prospection() {
  const { showToast } = useToast();
  const [view, setView] = useState('search'); // 'search' | 'history'
  const [ficheEt, setFicheEt] = useState(null);
  const [selectedDept, setSelectedDept] = useState('69');
  const [selectedNaf, setSelectedNaf] = useState('8411Z');
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [searchMeta, setSearchMeta] = useState({ total: 0, lastSearch: null });
  const [error, setError] = useState(null);
  // Lazy init : cleanup orphelins puis read (1 seul read post-cleanup).
  // Evite "Déjà en Pipeline" sur prospects supprimes du Pipeline.
  const [importedSirets, setImportedSirets] = useState(() => {
    cleanupOrphanImportedSirets();
    return new Set(getImportedSirets());
  });
  const [importHistory, setImportHistory] = useState(() => getSireneImportHistory());
  const [importAllConfirmOpen, setImportAllConfirmOpen] = useState(false);
  const [isBulkImporting, setIsBulkImporting] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  const [showOnlyNonImported, setShowOnlyNonImported] = useState(false);

  function refreshImported() {
    setImportedSirets(new Set(getImportedSirets()));
  }

  function refreshHistory() {
    setImportHistory(getSireneImportHistory());
  }

  async function handleSearch() {
    setError(null);
    setIsSearching(true);
    setResults([]);

    // Zone Luxembourg : dataset statique local (pas d'API publique LU exploitable)
    if (selectedDept === ZONE_LUXEMBOURG) {
      const lux = searchLuxembourg(selectedNaf);
      setResults(lux);
      setSearchMeta({
        total: lux.length,
        lastSearch: { dept: ZONE_LUXEMBOURG, naf: selectedNaf, at: new Date().toISOString() },
      });
      setIsSearching(false);
      showToast(`${lux.length} établissements Luxembourg (dataset local)`, { kind: 'success' });
      return;
    }

    try {
      const resp = await fetch('/api/prospection-sirene', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          departement: selectedDept,
          nafCode: selectedNaf,
          page: 0,
          perPage: 50,
        }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }));
        throw new Error(data.error || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      if (!data.ok) throw new Error(data.error || 'Erreur SIRENE');
      setResults(data.etablissements || []);
      setSearchMeta({
        total: data.total,
        lastSearch: { dept: selectedDept, naf: selectedNaf, at: new Date().toISOString() },
      });
      showToast(`${data.etablissements?.length || 0} etablissements trouves (sur ${data.total})`, { kind: 'success' });
    } catch (err) {
      console.error('[prospection] search:', err);
      setError(err.message);
      showToast(`Erreur recherche : ${err.message}`, { kind: 'danger', duration: 6000 });
    } finally {
      setIsSearching(false);
    }
  }

  function handleImportOne(et) {
    if (isAlreadyImported(et.siret)) {
      showToast('Déjà dans le Pipeline', { kind: 'info' });
      return;
    }
    addProspect({
      nom: et.nom,
      type: 'externe',
      statut: 'repere',
      contact: et.adresse || '',
      notes: `Importe SIRENE - SIRET ${et.siret} - NAF ${et.naf} - ${et.effectifLabel} - score ${et.score}`,
      caEstimeParcMensuel: 0,
      dateContact: new Date().toISOString(),
      siret: et.siret,
      sireneScore: et.score,
      nafCode: et.naf,
      effectifCode: et.effectifCode || '',
      effectifLabel: et.effectifLabel,
      commune: et.commune,
      codePostal: et.codePostal || '',
    });
    addImportedSiret(et.siret);
    refreshImported();
    showToast(`${et.nom} ajoute au Pipeline`, { kind: 'success' });
  }

  async function handleImportAll() {
    setImportAllConfirmOpen(false);
    setIsBulkImporting(true);
    const top50 = results.slice(0, 50);
    setBulkProgress({ current: 0, total: top50.length });
    let ajoutes = 0;
    let skipped = 0;
    for (let i = 0; i < top50.length; i++) {
      const et = top50[i];
      if (isAlreadyImported(et.siret)) {
        skipped++;
      } else {
        addProspect({
          nom: et.nom,
          type: 'externe',
          statut: 'repere',
          contact: et.adresse || '',
          notes: `Importe SIRENE - SIRET ${et.siret} - NAF ${et.naf} - ${et.effectifLabel} - score ${et.score}`,
          caEstimeParcMensuel: 0,
          dateContact: new Date().toISOString(),
          siret: et.siret,
          sireneScore: et.score,
          nafCode: et.naf,
          effectifCode: et.effectifCode || '',
          effectifLabel: et.effectifLabel,
          commune: et.commune,
          codePostal: et.codePostal || '',
        });
        addImportedSiret(et.siret);
        ajoutes++;
      }
      setBulkProgress({ current: i + 1, total: top50.length });
      // Yield au browser tous les 5 pour fluidifier le repaint
      if (i % 5 === 4) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 0));
      }
    }
    addSireneImportEntry({
      departement: selectedDept,
      nafCode: selectedNaf,
      nbAjoutes: ajoutes,
      nbSkipped: skipped,
    });
    refreshImported();
    refreshHistory();
    setIsBulkImporting(false);
    setBulkProgress({ current: 0, total: 0 });
    showToast(`${ajoutes} ajoutes, ${skipped} déjà prospectés`, { kind: 'success', duration: 5000 });
  }

  const top50Count = Math.min(50, results.length);
  const nbDejaImportes = results.filter((et) => importedSirets.has(et.siret)).length;
  const displayedResults = showOnlyNonImported
    ? results.filter((et) => !importedSirets.has(et.siret))
    : results;

  return (
    <section
      id="panel-prospection"
      role="tabpanel"
      aria-labelledby="tab-prospection"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: space.lg,
        maxWidth: 1100,
        margin: '0 auto',
        width: '100%',
      }}
    >
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: space.md, flexWrap: 'wrap' }}>
        <h1 style={{ ...typoStyle('display-lg'), color: 'var(--text)', margin: 0 }}>
          Prospection SIRENE
        </h1>
        <div style={{ display: 'flex', gap: space.xs }}>
          <Button variant={view === 'search' ? 'primary' : 'ghost'} size="sm" iconLeft={Search} onClick={() => setView('search')}>
            Recherche
          </Button>
          <Button variant={view === 'history' ? 'primary' : 'ghost'} size="sm" iconLeft={FileText} onClick={() => setView('history')}>
            Historique
          </Button>
        </div>
      </header>

      {view === 'search' && (
        <>
          <div
            style={{
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              borderRadius: radius.lg,
              padding: space.lg,
              display: 'grid',
              gridTemplateColumns: '1fr 2fr auto',
              gap: space.md,
              alignItems: 'flex-end',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
              <label htmlFor="sirene-dept" style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                <MapPin size={12} strokeWidth={1.75} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                Departement
              </label>
              <select
                id="sirene-dept"
                value={selectedDept}
                onChange={(e) => setSelectedDept(e.target.value)}
                style={inputBaseStyle}
              >
                <optgroup label="France (SIRENE INSEE)">
                  {DEPARTEMENTS_SECTEUR.map((d) => (
                    <option key={d} value={d}>
                      {d}{['69','01','38','73','74','42'].includes(d) ? ' (zone active)' : ''}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="International">
                  <option value={ZONE_LUXEMBOURG}>Luxembourg (zone active)</option>
                </optgroup>
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
              <label htmlFor="sirene-naf" style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Code NAF cible
              </label>
              <select
                id="sirene-naf"
                value={selectedNaf}
                onChange={(e) => setSelectedNaf(e.target.value)}
                style={inputBaseStyle}
              >
                {NAF_OPTIONS.map((n) => (
                  <option key={n.code} value={n.code}>
                    {n.code} — {n.label}
                  </option>
                ))}
              </select>
            </div>

            <Button
              variant="primary"
              size="lg"
              iconLeft={Search}
              onClick={handleSearch}
              loading={isSearching}
              disabled={isSearching}
            >
              Lancer la recherche
            </Button>
          </div>

          {error && (
            <div
              role="alert"
              style={{
                background: 'var(--danger-subtle)',
                border: '1px solid var(--danger)',
                borderRadius: radius.md,
                padding: space.md,
                color: 'var(--danger)',
                ...typoStyle('body-sm'),
              }}
            >
              {error}
              <div style={{ color: 'var(--text-muted)', marginTop: space.xs }}>
                Verifie SIRENE_CLIENT_ID + SIRENE_CLIENT_SECRET dans les secrets Cloudflare Pages.
              </div>
            </div>
          )}

          {results.length > 0 && (
            <>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: space.md,
                  flexWrap: 'wrap',
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border)',
                  borderRadius: radius.md,
                  padding: space.md,
                }}
              >
                <div style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: space.md, flexWrap: 'wrap' }}>
                  <span>
                    <strong style={{ color: 'var(--text)' }}>{displayedResults.length}</strong>
                    {showOnlyNonImported && displayedResults.length !== results.length && (
                      <> / {results.length}</>
                    )}
                    {' '}resultats
                    {searchMeta.total > results.length && (
                      <> sur <strong style={{ color: 'var(--text)' }}>{searchMeta.total}</strong> total</>
                    )}
                    {' '} · <strong style={{ color: 'var(--accent-strong)' }}>{nbDejaImportes}</strong> déjà en Pipeline
                  </span>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: space.xs, cursor: 'pointer', color: 'var(--text)', fontWeight: 500 }}>
                    <input
                      type="checkbox"
                      checked={showOnlyNonImported}
                      onChange={(e) => setShowOnlyNonImported(e.target.checked)}
                      style={{ accentColor: 'var(--accent)' }}
                    />
                    <Filter size={12} strokeWidth={1.75} /> Non importes seulement
                  </label>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  iconLeft={Plus}
                  onClick={() => setImportAllConfirmOpen(true)}
                  disabled={isBulkImporting || top50Count === 0}
                  loading={false}
                >
                  Tout importer (top {top50Count})
                </Button>
              </div>

              {isBulkImporting && bulkProgress.total > 0 && (
                <div
                  role="status"
                  aria-live="polite"
                  style={{
                    background: 'var(--bg-elevated)',
                    border: '1px solid var(--accent)',
                    borderRadius: radius.md,
                    padding: space.md,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: space.sm,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', ...typoStyle('body-sm'), color: 'var(--text)' }}>
                    <span>Import en cours</span>
                    <span style={{ ...typoStyle('mono-md'), color: 'var(--accent-strong)' }}>
                      {bulkProgress.current} / {bulkProgress.total}
                    </span>
                  </div>
                  <div
                    style={{
                      height: 6,
                      background: 'var(--bg-subtle)',
                      borderRadius: radius.full,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.round((bulkProgress.current / bulkProgress.total) * 100)}%`,
                        height: '100%',
                        background: 'var(--accent)',
                        transition: 'width 200ms ease-out',
                      }}
                    />
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
                {displayedResults.length === 0 ? (
                  <div style={{
                    background: 'var(--bg-elevated)',
                    border: '1px dashed var(--border-strong)',
                    borderRadius: radius.md,
                    padding: space.xl,
                    textAlign: 'center',
                    color: 'var(--text-muted)',
                  }}>
                    Tous les resultats sont déjà en Pipeline. Decoche le filtre pour les revoir.
                  </div>
                ) : (
                  displayedResults.map((et) => (
                    <ResultRow
                      key={et.siret}
                      et={et}
                      alreadyImported={importedSirets.has(et.siret)}
                      onImport={handleImportOne}
                      onOpenFiche={setFicheEt}
                      disabled={isBulkImporting}
                    />
                  ))
                )}
              </div>
            </>
          )}

          {!isSearching && !error && results.length === 0 && searchMeta.lastSearch && (
            <div
              style={{
                background: 'var(--bg-elevated)',
                border: '1px dashed var(--border-strong)',
                borderRadius: radius.md,
                padding: space.xl,
                textAlign: 'center',
                color: 'var(--text-muted)',
              }}
            >
              Aucun etablissement trouve avec ces criteres.
            </div>
          )}
        </>
      )}

      {view === 'history' && (
        <>
          <h2 style={{ ...typoStyle('body-sm'), color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, margin: 0 }}>
            Imports recents ({importHistory.length})
          </h2>
          {importHistory.length === 0 ? (
            <div
              style={{
                background: 'var(--bg-elevated)',
                border: '1px dashed var(--border-strong)',
                borderRadius: radius.md,
                padding: space.xl,
                textAlign: 'center',
                color: 'var(--text-muted)',
              }}
            >
              Aucun import effectue. Lance une recherche puis "Tout importer".
            </div>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: space.xs }}>
              {importHistory.map((h, i) => {
                const nafLabel = NAF_OPTIONS.find((n) => n.code === h.nafCode)?.label || h.nafCode;
                return (
                  <li
                    key={i}
                    style={{
                      background: 'var(--bg-elevated)',
                      border: '1px solid var(--border)',
                      borderRadius: radius.md,
                      padding: space.md,
                      display: 'grid',
                      gridTemplateColumns: 'auto 1fr auto',
                      gap: space.md,
                      alignItems: 'center',
                    }}
                  >
                    <FileText size={18} color="var(--text-muted)" strokeWidth={1.75} />
                    <div>
                      <div style={{ ...typoStyle('body-md'), color: 'var(--text)' }}>
                        Dept {h.departement} · NAF {h.nafCode} ({nafLabel})
                      </div>
                      <div style={{ ...typoStyle('body-sm'), color: 'var(--text-subtle)' }}>
                        {formatRelativeDate(h.date)} · {h.nbAjoutes} ajoutes, {h.nbSkipped || 0} déjà prospectés
                      </div>
                    </div>
                    <span style={{ ...typoStyle('mono-md'), color: 'var(--accent)', fontWeight: 700 }}>
                      +{h.nbAjoutes}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      <Modal
        open={importAllConfirmOpen}
        onClose={() => setImportAllConfirmOpen(false)}
        title={`Importer ${top50Count - nbDejaImportes} etablissements dans le Pipeline ?`}
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setImportAllConfirmOpen(false)}>Annuler</Button>
            <Button variant="primary" onClick={handleImportAll}>Importer</Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          Top {top50Count} etablissements (tri score desc) seront ajoutes en statut "Repere".
          Les {nbDejaImportes} déjà en Pipeline seront skippes.
        </p>
        <p style={{ marginTop: space.md, marginBottom: 0, color: 'var(--text-muted)', ...typoStyle('body-sm') }}>
          Notes SIRET + NAF + effectif + score pre-rempli sur chaque prospect.
        </p>
      </Modal>

      <FicheProspectModal
        open={!!ficheEt}
        onClose={() => setFicheEt(null)}
        etablissement={ficheEt}
        alreadyImported={ficheEt ? importedSirets.has(ficheEt.siret) : false}
        onImport={() => {
          if (ficheEt) {
            handleImportOne(ficheEt);
            setFicheEt(null);
          }
        }}
      />
    </section>
  );
}

const MODULE_COMPONENTS = {
  dashboard: Module1Dashboard,
  sync: Module2Sync,
  audit: Module3Audit,
  chiffrage: Module4Chiffrage,
  pipeline: Module5Pipeline,
  redaction: Module6Redaction,
  reglages: Module7Reglages,
  prospection: Module8Prospection,
};

// ============================================================
// AppShell + App root
// ============================================================

function AppShell() {
  return (
    <NavProvider>
      <AppShellInner />
    </NavProvider>
  );
}

function AppShellInner() {
  const { activeId } = useNav();
  const isMobile = useMediaQuery(BREAKPOINT_MOBILE);
  const ActiveModule = MODULE_COMPONENTS[activeId] || Module1Dashboard;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        background: 'var(--bg)',
        color: 'var(--text)',
        ...typoStyle('body-md'),
      }}
    >
      <Header isMobile={isMobile} />

      {!isMobile && <TopNav />}

      <main
        style={{
          flex: 1,
          padding: space.lg,
          paddingBottom: isMobile
            ? `calc(56px + env(safe-area-inset-bottom, 0) + ${space.lg})`
            : space.lg,
        }}
      >
        <ActiveModule />
      </main>

      {isMobile && <BottomNav />}

      {/* Keyframes globaux (spinner Button loading + pulse skeleton) */}
      <style>{`
        @keyframes fg-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes fg-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}

export default function App({ initialTheme = 'light' }) {
  return (
    <ThemeProvider initial={initialTheme}>
      <ToastProvider>
        <HealthProvider>
          <NotificationProvider>
            <AppShell />
          </NotificationProvider>
        </HealthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}
